import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getToken, clearToken } from '../utils/tokenStorage';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  Send, 
  User, 
  X,
  Volume2,
  VolumeX,
  Users,
  Crown,
  Shield,
  Tv,
  Shirt,
  Maximize,
  Sparkles,
  Glasses,
  Music,
  DoorOpen,
  Lock,
  Star,
  Award,
  Gift,
  ThumbsUp,
  Mic,
  MicOff,
  Trophy
} from 'lucide-react';

// Import extracted modules
import { soundFX } from '../components/nightclub/soundFX';
import { 
  drawStarrySky,
  drawCityView,
  drawStringLights,
  drawRooftopTrees,
  drawLoungeSeating,
  drawChandelier
} from '../components/nightclub/canvasUtils';
import { drawRooftopFurniture, getBartenderHitbox } from '../components/nightclub/rooftopBar';
import {
  drawVelvetRopes,
  drawChandeliers,
  drawEquipmentCases,
  drawCables,
  drawRoomGraffiti,
  drawNeonSign,
  drawFirePit,
  drawHotTub,
  drawTubSoakOverlay,
  drawBarShelf,
  drawChampagneTower
} from '../components/nightclub/roomDecorations';
import { ensureSpriteLoaded, drawPlayerSprite, directionFromDelta } from '../components/nightclub/playerSprite';
import { drawPlayerAvatar } from '../components/nightclub/drawPlayerAvatar';
import { drawChatBubble } from '../components/nightclub/chatOverlay';
import useKeyboardMovement from '../hooks/useKeyboardMovement';
import AvatarEditor from '../components/nightclub/AvatarEditor';
import AdminPanel from '../components/nightclub/AdminPanel';
import QuestPanel from '../components/nightclub/QuestPanel';
import GiftModal, { GIFT_ITEMS } from '../components/nightclub/GiftModal';
import RoomsModal from '../components/nightclub/RoomsModal';
import EmotesPanel from '../components/nightclub/EmotesPanel';
import PlayerProfileCard from '../components/nightclub/PlayerProfileCard';
import PrivateChatModal from '../components/nightclub/PrivateChatModal';
import ElevatorPrompts from '../components/nightclub/ElevatorPrompts';
import ReactionPicker from '../components/nightclub/ReactionPicker';
import AvatarEditorModal from '../components/nightclub/AvatarEditorModal';
import LeaderboardModal from '../components/nightclub/LeaderboardModal';
import { YouTubePlayer, getYouTubeVideoId } from '../components/nightclub/YouTubePlayer';
import ChatPanel from '../components/nightclub/ChatPanel';
import HamburgerMenu from '../components/nightclub/HamburgerMenu';
import ApartmentComplex from '../components/nightclub/ApartmentComplex';
import CityBlock from '../components/nightclub/CityBlock';
import TipDJModal from '../components/nightclub/TipDJModal';
import QuickReactionBar from '../components/nightclub/QuickReactionBar';
import RoomCreator from '../components/nightclub/RoomCreator';
import PhotoMode from '../components/nightclub/PhotoMode';
import DJQueue from '../components/nightclub/DJQueue';
import DanceBattle from '../components/nightclub/DanceBattle';
import UsernamePromptModal from '../components/nightclub/UsernamePromptModal';
import VirtualBar, { DRINKS } from '../components/nightclub/VirtualBar';
import { AnnouncementBanner, AnnouncementModal } from '../components/nightclub/Announcements';
import PrivateRoomsModal from '../components/nightclub/PrivateRoomsModal';
import DailyRewardsModal from '../components/nightclub/DailyRewardsModal';
import BartenderModal, { BARTENDER_DRINKS } from '../components/nightclub/BartenderModal';
import FriendsPanel from '../components/nightclub/FriendsPanel';
import NotificationBell from '../components/nightclub/NotificationBell';
import NowPlayingBar from '../components/nightclub/NowPlayingBar';
import RoleChangeBanner from '../components/nightclub/RoleChangeBanner';
import CustomWidgets from '../components/nightclub/CustomWidgets';
import CustomCSSLoader from '../components/nightclub/CustomCSSLoader';
import TripOverlay from '../components/nightclub/TripOverlay';
import { drawTurtle } from '../components/nightclub/AvatarPreview';
import { drawMainRoomLighting, drawAdvancedVisualizerRoom, drawDropEffect, LIGHTING_PRESETS } from '../components/nightclub/lightingSystem';

import {
  NPC_DJS, QUICK_REACTIONS, VIP_PERKS, NIGHTCLUB_GIFT_COSTS, NIGHTCLUB_GIFT_EMOJIS,
  SPOTLIGHT_ZONE, CLUB_ROOMS, SKIN_TONES, FACE_MOODS, HAIR_STYLES, HAIR_COLORS,
  EYE_COLORS, FACE_ACCESSORIES, DANCE_EMOTES, OUTFIT_TOPS, OUTFIT_PANTS,
  OUTFIT_SHOES, OUTFIT_HATS, OUTFIT_ACCESSORIES
} from '../components/nightclub/constants';


const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth-aware fetch helper — handles missing/expired tokens gracefully
const authFetch = async (url, options = {}) => {
  const token = getToken();
  if (!token) {
    toast.error('Please log in to continue');
    return null;
  }
  const res = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    clearToken();
    toast.error('Session expired — please log in again');
    return null;
  }
  return res;
};

// Apartments entrance door — Chill Zone, next to the LED screen
const getApartmentsDoorRect = (W, H, floorY, isMobile) => {
  const w = isMobile ? 54 : 76;
  const h = isMobile ? 76 : 104;
  const screenHalf = (isMobile ? W * 0.55 : 240) / 2;
  const x = Math.min(W / 2 + screenHalf + (isMobile ? 10 : 30), W - w - 10);
  return { x, y: floorY - h + 34, w, h };
};

const drawApartmentsDoor = (ctx, W, H, floorY, phase, isMobile) => {
  const { x, y, w, h } = getApartmentsDoorRect(W, H, floorY, isMobile);
  const glow = 0.55 + Math.abs(Math.sin(phase * 0.03)) * 0.45;
  ctx.save();
  ctx.fillStyle = '#100a04';
  ctx.fillRect(x - 5, y - 8, w + 10, h + 8);
  ctx.strokeStyle = `rgba(251, 191, 36, ${glow})`;
  ctx.lineWidth = 2.5;
  ctx.strokeRect(x - 5, y - 8, w + 10, h + 8);
  ctx.fillStyle = '#3a2a16';
  ctx.fillRect(x, y, w / 2 - 1, h);
  ctx.fillRect(x + w / 2 + 1, y, w / 2 - 1, h);
  ctx.fillStyle = `rgba(253, 230, 138, ${0.35 + glow * 0.3})`;
  ctx.fillRect(x + 5, y + 8, w / 2 - 11, h * 0.32);
  ctx.fillRect(x + w / 2 + 6, y + 8, w / 2 - 11, h * 0.32);
  ctx.fillStyle = '#fbbf24';
  ctx.beginPath();
  ctx.arc(x + w / 2 - 6, y + h * 0.55, 2.5, 0, Math.PI * 2);
  ctx.arc(x + w / 2 + 6, y + h * 0.55, 2.5, 0, Math.PI * 2);
  ctx.fill();
  const signX = Math.min(x + w / 2, W - (isMobile ? 42 : 60));
  ctx.font = `bold ${isMobile ? 9 : 12}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fbbf24';
  ctx.shadowColor = '#fbbf24';
  ctx.shadowBlur = 10 * glow;
  ctx.fillText('🏢 APARTMENTS', signX, y - 14);
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(120, 80, 40, 0.8)';
  ctx.fillRect(x + 4, y + h + 2, w - 8, 7);
  ctx.restore();
};

export default function Nightclub() {
  const navigate = useNavigate();
  const { user, isAuthenticated, setUser, updateUser } = useAuthStore();
  // Press Enter anywhere to jump into chat (classic MMO behavior)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Enter') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const el = document.querySelector('[data-testid="chat-input"]');
      if (el) { e.preventDefault(); el.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const isGuest = !isAuthenticated || !user;
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const socketRef = useRef(null);
  const youtubeRef = useRef(null);
  const containerRef = useRef(null);
  const footstepCounter = useRef(0);
  
  // ==================== MULTIPLAYER SYSTEM ====================
  // Client-side prediction: Your character moves instantly, server confirms
  // Remote players: Smooth interpolation with configurable speed
  
  // Interpolation settings for remote players
  const INTERPOLATION_SPEED = 0.25; // 25% lerp per frame (smooth but responsive)
  const POSITION_SEND_INTERVAL = 50; // Send position every 50ms (~20fps)
  const SNAP_DISTANCE = 300; // Snap instead of lerp if distance > this
  
  // Client-side interpolation for smooth remote player movement
  const interpolatedPositionsRef = useRef({});
  
  // Store our socket ID for reliable self-filtering
  const mySocketIdRef = useRef(null);
  
  // Throttle position updates
  const lastPositionSendRef = useRef(0);
  
  // Server position (for optional ghost/lag indicator)
  const serverPositionRef = useRef({ x: 400, y: 450 });
  const [showServerGhost, setShowServerGhost] = useState(false); // Toggle in settings
  
  // Track pending position updates for reconciliation
  const pendingMovesRef = useRef([]);
  const moveSequenceRef = useRef(0);
  
  // ==================== END MULTIPLAYER SYSTEM ====================
  
  // Username prompt state
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [displayName, setDisplayName] = useState('');
  
  // Game state
  const [players, setPlayers] = useState({});
  const playersRef = useRef({});  // Keep players in ref for real-time canvas access
  const [myPosition, setMyPosition] = useState({ x: 400, y: 450 });
  const [targetPosition, setTargetPosition] = useState(null);
  const [isWalking, setIsWalking] = useState(false);
  const walkFrameRef = useRef(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [typingPlayers, setTypingPlayers] = useState({});  // {playerId: {name, timestamp}}
  const [entranceEffects, setEntranceEffects] = useState({});  // {playerId: {startTime, playerName}}
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showEmoteMenu, setShowEmoteMenu] = useState(false);
  const [showRoomMenu, setShowRoomMenu] = useState(false);
  const [showElevatorPrompt, setShowElevatorPrompt] = useState(false);  // Elevator to rooftop
  const [showReturnElevatorPrompt, setShowReturnElevatorPrompt] = useState(false);  // Return elevator to VIP
  const [streamUrl, setStreamUrl] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [muted, setMuted] = useState(false);  // Start unmuted - user controls volume
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Volume controls - persisted to localStorage
  // Voice default is lower than music so players never overpower the video/DJ
  const [voiceChatVolume, setVoiceChatVolume] = useState(() => {
    try { return parseFloat(localStorage.getItem('nightclub_voice_volume') || '0.4'); } catch { return 0.4; }
  });
  const [musicVolume, setMusicVolume] = useState(() => {
    try { return parseFloat(localStorage.getItem('nightclub_music_volume') || '0.7'); } catch { return 0.7; }
  });
  const voiceChatVolumeRef = useRef(0.4);
  const musicVolumeRef = useRef(0.7);
  const [lightingMode, setLightingMode] = useState('rave');
  const [lightingPreset, setLightingPreset] = useState('rave');
  const [autoCycleLights, setAutoCycleLights] = useState(false);
  const [slowMode, setSlowMode] = useState(false);
  const [roomLocked, setRoomLocked] = useState(false);
  const [dropEffect, setDropEffect] = useState(0); // 0 = off, 1 = starting, counts down to 0
  const dropColorRef = useRef('#9333EA');
  const autoCycleRef = useRef(null);
  const [lightingFx, setLightingFx] = useState({
    fx_mode: 'chill',
    primary_color: '#9333EA',
    secondary_color: '#3B82F6',
    speed: 0.5,
    intensity: 0.4,
    bass_reactivity: 0.5,
    treble_reactivity: 0.3
  });
  const [audioData, setAudioData] = useState({ bass: 0, mids: 0, treble: 0, overall: 0, peak: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentEmote, setCurrentEmote] = useState(null);
  const [emoteEndTime, setEmoteEndTime] = useState(0);
  const [showChatLog, setShowChatLog] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [streamStartedAt, setStreamStartedAt] = useState(null);  // For video sync
  const [purchasedItems, setPurchasedItems] = useState(() => {
    // Load purchased items from localStorage
    try {
      const saved = localStorage.getItem('nightclub_purchased_items');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  
  // New feature states
  const [particles, setParticles] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [citySpawn, setCitySpawn] = useState(null);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showTipDJ, setShowTipDJ] = useState(false);
  const [showRoomCreator, setShowRoomCreator] = useState(false);
  const [showPhotoMode, setShowPhotoMode] = useState(false);
  const [showDJQueue, setShowDJQueue] = useState(false);
  const [showDanceBattle, setShowDanceBattle] = useState(false);
  const [showFriendsPanel, setShowFriendsPanel] = useState(false);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [showPrivateChat, setShowPrivateChat] = useState(false);
  const [privateChatTarget, setPrivateChatTarget] = useState(null);
  const [friends, setFriends] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('nightclub_friends') || '[]');
    } catch { return []; }
  });
  const [activeTheme, setActiveTheme] = useState(null);
  const [hasVipAccess, setHasVipAccess] = useState(false);
  
  // Voice Chat States - Proximity based
  const [voiceChatEnabled, setVoiceChatEnabled] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(true);
  const [speakingPlayers, setSpeakingPlayers] = useState({});  // {playerId: {speaking, x, y, name}}
  const localStreamRef = useRef(null);
  const VOICE_PROXIMITY_RANGE = 200;  // Pixels - max distance to hear someone
  
  // WebRTC Peer Connection Management
  const peerConnectionsRef = useRef({});  // {peerId: RTCPeerConnection}
  const remoteAudioElementsRef = useRef({});  // {peerId: HTMLAudioElement}
  const pendingIceCandidatesRef = useRef({});  // {peerId: [candidates]} queued until remoteDescription is set
  const [voicePeers, setVoicePeers] = useState({});  // {peerId: {name, x, y}}
  const audioContextRef = useRef(null);
  const gainNodesRef = useRef({});  // {peerId: GainNode} for proximity volume control
  
  // Batch 2 & 3 Feature States
  const [vibecoins, setVibecoins] = useState(100);
  const [knockRequest, setKnockRequest] = useState(null); // apartment visitor knocking
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftTarget, setGiftTarget] = useState(null);
  const [showQuestPanel, setShowQuestPanel] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [quests, setQuests] = useState([]);
  const [giftAnimations, setGiftAnimations] = useState([]);
  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);
  const [hideFullscreenControls, setHideFullscreenControls] = useState(false);
  
  // Hourly reward tracking
  const [hourlyRewardReady, setHourlyRewardReady] = useState(false);
  const [hourlyCountdown, setHourlyCountdown] = useState(0);
  
  // New feature states
  const [showVirtualBar, setShowVirtualBar] = useState(false);
  const [activeDrink, setActiveDrink] = useState(null);
  const [drinkEffect, setDrinkEffect] = useState(null);
  const [tripActive, setTripActive] = useState(false);
  const [isTurtle, setIsTurtle] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [showPrivateRooms, setShowPrivateRooms] = useState(false);
  const [privateRooms, setPrivateRooms] = useState([]);
  const [currentPrivateRoom, setCurrentPrivateRoom] = useState(null);
  const [showDailyRewards, setShowDailyRewards] = useState(false);
  const [showBartender, setShowBartender] = useState(false);
  const [loginStreak, setLoginStreak] = useState(() => {
    const saved = localStorage.getItem('nightclub_login_streak');
    return saved ? JSON.parse(saved) : { streak: 0, lastClaim: null };
  });
  const [weatherEffect, setWeatherEffect] = useState('clear'); // clear, rain, sunset, night
  const [timeOfDay, setTimeOfDay] = useState('night');
  
  // Role change banner state
  const [roleChangeBanner, setRoleChangeBanner] = useState({ visible: false, newRole: '', changedBy: '' });
  const notificationBellRef = useRef(null);
  const [roomCounts, setRoomCounts] = useState({});
  const [currentPlayingSong, setCurrentPlayingSong] = useState(null);
  
  // Custom rooms from backend (AI-created)
  const [customRooms, setCustomRooms] = useState([]);
  
  // Visualizer settings state
  const [visualizerSettings, setVisualizerSettings] = useState(() => {
    const saved = localStorage.getItem('nightclub_visualizer');
    return saved ? JSON.parse(saved) : {
      wallVisual: 'bars', // bars, wave, circular, kaleidoscope, tunnel, custom
      floorVisual: 'grid', // grid, ripple, pulse, custom
      ceilingVisual: 'stars', // stars, lasers, plasma, custom
      wallCustomUrl: '',
      floorCustomUrl: '',
      ceilingCustomUrl: '',
      intensity: 0.7,
      colorScheme: 'neon', // neon, fire, ice, rainbow, custom
      syncToAudio: true,
      mirrorMode: false
    };
  });
  
  // Visualizer preset URLs
  const VISUALIZER_PRESETS = {
    walls: [
      { id: 'bars', name: 'Audio Bars', icon: '📊', type: 'builtin' },
      { id: 'wave', name: 'Waveform', icon: '〰️', type: 'builtin' },
      { id: 'circular', name: 'Circular Spectrum', icon: '🔵', type: 'builtin' },
      { id: 'kaleidoscope', name: 'Kaleidoscope', icon: '🔮', type: 'builtin' },
      { id: 'tunnel', name: 'Tunnel', icon: '🌀', type: 'builtin' },
      { id: 'particles', name: 'Particles', icon: '✨', type: 'builtin' },
      { id: 'matrix', name: 'Matrix Rain', icon: '🟩', type: 'builtin' },
    ],
    floor: [
      { id: 'grid', name: 'Reactive Grid', icon: '🔲', type: 'builtin' },
      { id: 'ripple', name: 'Ripple Effect', icon: '💧', type: 'builtin' },
      { id: 'pulse', name: 'Pulse Rings', icon: '🎯', type: 'builtin' },
      { id: 'tiles', name: 'Dance Tiles', icon: '🟪', type: 'builtin' },
      { id: 'fire', name: 'Fire Floor', icon: '🔥', type: 'builtin' },
    ],
    ceiling: [
      { id: 'stars', name: 'Starfield', icon: '⭐', type: 'builtin' },
      { id: 'lasers', name: 'Laser Show', icon: '📡', type: 'builtin' },
      { id: 'plasma', name: 'Plasma', icon: '🌈', type: 'builtin' },
      { id: 'nebula', name: 'Nebula', icon: '🌌', type: 'builtin' },
    ],
    colorSchemes: [
      { id: 'neon', name: 'Neon', colors: ['#FF00FF', '#00FFFF', '#FF0080'] },
      { id: 'fire', name: 'Fire', colors: ['#FF4500', '#FF8C00', '#FFD700'] },
      { id: 'ice', name: 'Ice', colors: ['#00CED1', '#87CEEB', '#E0FFFF'] },
      { id: 'rainbow', name: 'Rainbow', colors: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF'] },
      { id: 'vaporwave', name: 'Vaporwave', colors: ['#FF6AD5', '#8B5CF6', '#06B6D4'] },
      { id: 'synthwave', name: 'Synthwave', colors: ['#F72585', '#7209B7', '#3A0CA3'] },
    ]
  };
  
  // Room and DJ state
  const [currentRoom, setCurrentRoom] = useState('main');
  const [currentDJ, setCurrentDJ] = useState(NPC_DJS[0]);
  const [djPlaying, setDjPlaying] = useState(true);
  
  // Animation phase refs (update every frame, don't trigger re-renders)
  const bouncePhaseRef = useRef(0);
  const lightingPhaseRef = useRef(0);
  const canvasTouchTimeRef = useRef(0); // suppress synthetic click after touch
  const djBounceRef = useRef(0);
  
  // Wall ads state (admin configurable)
  // Load saved avatar from localStorage
  const getSavedAvatar = () => {
    try {
      const saved = localStorage.getItem('nightclub_avatar');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.log('Failed to load saved avatar');
    }
    return {
      gender: 'male',  // male or female
      skinTone: 'medium',
      faceMood: 'neutral',
      eyeColor: 'purple',
      eyeGlow: 50,
      top: 'tank_black',
      pants: 'cargo_dark',
      shoes: 'rave_green',
      hat: 'none',
      accessory: 'none',
      spriteHue: 0,  // 0-359, hue rotation applied to nightclub sprite
    };
  };
  
  // Load saved outfits from localStorage
  const getSavedOutfits = () => {
    try {
      const saved = localStorage.getItem('nightclub_saved_outfits');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.log('Failed to load saved outfits');
    }
    return [];
  };
  
  // Avatar customization state
  const [avatar, setAvatar] = useState(getSavedAvatar);
  const [savedOutfits, setSavedOutfits] = useState(getSavedOutfits);
  const [ownedOutfitItems, setOwnedOutfitItems] = useState([]);
  
  // Save avatar to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('nightclub_avatar', JSON.stringify(avatar));
    } catch (e) {
      console.log('Failed to save avatar');
    }
  }, [avatar]);
  
  // Save outfits list to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('nightclub_saved_outfits', JSON.stringify(savedOutfits));
    } catch (e) {
      console.log('Failed to save outfits');
    }
  }, [savedOutfits]);
  
  // Save current outfit
  const saveCurrentOutfit = () => {
    const outfitName = `Outfit ${savedOutfits.length + 1}`;
    const newOutfit = {
      id: Date.now(),
      name: outfitName,
      avatar: { ...avatar }
    };
    setSavedOutfits(prev => [...prev.slice(-9), newOutfit]); // Keep max 10 outfits
    toast.success(`${outfitName} saved!`);
    soundFX.success();
  };
  
  // Load a saved outfit
  const loadOutfit = (outfit) => {
    setAvatar(outfit.avatar);
    toast.success(`Loaded ${outfit.name}`);
    soundFX.click();
  };
  
  // Delete a saved outfit
  const deleteOutfit = (outfitId) => {
    setSavedOutfits(prev => prev.filter(o => o.id !== outfitId));
    toast.success('Outfit deleted');
  };
  
  // Animation state
  // Refs for game loop to access current values without stale closures
  const myPositionRef = useRef({ x: 400, y: 450 });
  const targetPositionRef = useRef(null);
  const isWalkingRef = useRef(false);
  const rooftopSoakRef = useRef(false);
  const rooftopSoakEnterRef = useRef(0);
  // Direction tracking for sprite rendering
  const myDirectionRef = useRef('down');
  const playerDirectionsRef = useRef({});
  // playersRef is already declared above
  const avatarRef = useRef(avatar);
  const currentRoomRef = useRef('main');
  const serverRoomRef = useRef('main'); // TRUE room on the server (incl. city interiors/sewer)
  const chatMessagesRef = useRef([]);
  const particlesRef = useRef([]);
  const reactionsRef = useRef([]);
  const audioLevelRef = useRef(0);
  const entranceEffectsRef = useRef({});
  
  // Animation state - refs used by game loop, no state needed for canvas-only values
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Sync refs with state for game loop access (avoids stale closures)
  useEffect(() => { myPositionRef.current = myPosition; }, [myPosition]);
  useEffect(() => { targetPositionRef.current = targetPosition; }, [targetPosition]);
  useEffect(() => { isWalkingRef.current = isWalking; }, [isWalking]);
  // playersRef is updated directly in socket handlers, no sync needed
  useEffect(() => { avatarRef.current = avatar; }, [avatar]);
  useEffect(() => { currentRoomRef.current = currentRoom; }, [currentRoom]);
  useEffect(() => { chatMessagesRef.current = chatMessages; }, [chatMessages]);
  useEffect(() => { particlesRef.current = particles; }, [particles]);
  useEffect(() => { reactionsRef.current = reactions; }, [reactions]);
  useEffect(() => { audioLevelRef.current = audioLevel; }, [audioLevel]);
  useEffect(() => { entranceEffectsRef.current = entranceEffects; }, [entranceEffects]);

  // Preload all player sprite sheets
  useEffect(() => {
    ['male', 'female', 'cyberpunk', 'mage', 'athlete', 'punk', 'royalty', 'formal']
      .forEach(g => ensureSpriteLoaded(g));
  }, []);

  // Keyboard movement (WASD + Arrow keys) — desktop only, via reusable hook
  const { keysPressedRef } = useKeyboardMovement(isMobile || currentRoom === 'apartments' || currentRoom === 'city', () => {
    // Cancel any click-to-move target since user took manual control
    targetPositionRef.current = null;
  });

  useEffect(() => {
    voiceChatVolumeRef.current = voiceChatVolume;
    localStorage.setItem('nightclub_voice_volume', String(voiceChatVolume));
  }, [voiceChatVolume]);
  useEffect(() => {
    musicVolumeRef.current = musicVolume;
    localStorage.setItem('nightclub_music_volume', String(musicVolume));
  }, [musicVolume]);

  // Auto-cycle lighting presets every 30 seconds
  useEffect(() => {
    if (autoCycleLights) {
      let cycleIdx = LIGHTING_PRESETS.findIndex(p => p.id === lightingPreset);
      autoCycleRef.current = setInterval(() => {
        cycleIdx = (cycleIdx + 1) % LIGHTING_PRESETS.length;
        const next = LIGHTING_PRESETS[cycleIdx];
        setLightingPreset(next.id);
        setLightingFx(prev => ({ ...prev, fx_mode: next.id, intensity: next.intensity, speed: next.speed }));
      }, 30000);
      return () => clearInterval(autoCycleRef.current);
    } else {
      if (autoCycleRef.current) clearInterval(autoCycleRef.current);
    }
  }, [autoCycleLights, lightingPreset]);

  // Drop effect animation (1.5s duration, decays from 1 to 0)
  useEffect(() => {
    if (dropEffect > 0) {
      const start = performance.now();
      const duration = 1500;
      const animate = (now) => {
        const elapsed = now - start;
        const progress = Math.max(0, 1 - elapsed / duration);
        setDropEffect(progress);
        if (progress > 0) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [dropEffect === 1]); // only trigger on initial activation

  const triggerDrop = useCallback(() => {
    const currentPreset = LIGHTING_PRESETS.find(p => p.id === lightingPreset);
    dropColorRef.current = currentPreset?.colors?.[0] || '#9333EA';
    setDropEffect(1);
  }, [lightingPreset]);

  // Debug: Log user role
  useEffect(() => {
    console.log('[Nightclub] User role check:', { 
      role: user?.role, 
      isAdmin: user?.role === 'admin' || user?.role === 'owner' || user?.role === 'overseer',
      fullUser: user 
    });
  }, [user]);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'overseer';
  const isDJ = user?.role === 'dj';
  const isOwner = user?.role === 'owner';
  // Rooftop access: admins, owners, overseers, VIPs, users with explicit rooftop_access flag
  // (broader than before — VIPs were previously blocked despite paying for tier)
  const canAccessRooftop = isAdmin || user?.rooftop_access === true || user?.is_vip === true || hasVipAccess;
  
  // Check if current room should show LED screen/video
  // Fetch custom rooms from backend
  useEffect(() => {
    const fetchCustomRooms = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/nightclub/custom-rooms`);
        if (res.ok) {
          const rooms = await res.json();
          setCustomRooms(rooms.map(r => ({
            id: r.room_id,
            name: r.name,
            description: r.description || '',
            isVIP: false,
            maxPlayers: 25,
            isCustom: true,
            theme: {
              floorColor1: r.theme?.floorColor || r.theme?.bgColor || '#1a1a2e',
              floorColor2: r.theme?.bgColor || '#0f0f1a',
              gridColor: `${r.theme?.accentColor || '#9333EA'}26`,
              accentColor: r.theme?.accentColor || '#9333EA',
              ambientColor: `${r.theme?.accentColor || '#9333EA'}1a`,
              wallGraffiti: [],
              showDJ: r.theme?.showDJ || false,
              showLEDScreen: false,
              showSpeakers: false,
            }
          })));
        }
      } catch (e) {
        console.log('Failed to fetch custom rooms');
      }
    };
    fetchCustomRooms();
  }, []);
  
  // Fetch room player counts periodically
  useEffect(() => {
    const fetchRoomCounts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/nightclub/room-counts`);
        if (res.ok) setRoomCounts(await res.json());
      } catch (e) { /* silent */ }
    };
    fetchRoomCounts();
    const interval = setInterval(fetchRoomCounts, 10000);
    return () => clearInterval(interval);
  }, []);
  
  // Merge hardcoded rooms with custom rooms from backend
  const allRooms = useMemo(() => [...CLUB_ROOMS, ...customRooms], [customRooms]);
  const allRoomsRef = useRef(allRooms);
  useEffect(() => { allRoomsRef.current = allRooms; }, [allRooms]);
  
  const currentRoomInfo = allRooms.find(r => r.id === currentRoom) || CLUB_ROOMS[0];
  const shouldShowVideo = isStreaming && !!streamUrl;
  
  // Track quest progress - defined early so it can be used by other functions
  // Sound toggle
  useEffect(() => {
    soundFX.enabled = soundEnabled;
  }, [soundEnabled]);
  
  // Fullscreen handlers
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    
    try {
      if (!isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if (containerRef.current.webkitRequestFullscreen) {
          await containerRef.current.webkitRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (e) {
      console.log('Fullscreen error:', e);
    }
  };
  
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Play emote
  const playEmote = (emote) => {
    setCurrentEmote(emote.id);  // Store just the ID
    setEmoteEndTime(Date.now() + emote.duration);
    setShowEmoteMenu(false);
    
    // Play emote sound
    soundFX.emote();
    
    // Spawn particles when emoting in spotlight
    const inSpotlight = Math.sqrt(
      Math.pow(myPosition.x - SPOTLIGHT_ZONE.x, 2) + 
      Math.pow(myPosition.y - SPOTLIGHT_ZONE.y, 2)
    ) < SPOTLIGHT_ZONE.radius;
    
    if (inSpotlight) {
      spawnParticles(myPosition.x, myPosition.y, 20, VIP_PERKS[user?.role]?.particleColor || '#9333EA');
    }
    
    // Broadcast emote via Socket.IO with user_id for quest tracking
    if (socketRef.current?.connected) {
      socketRef.current.emit('player_emote', { 
        emote: emote.id,
        user_id: user?.user_id 
      });
    }
  };
  
  // Spawn particle effects
  const spawnParticles = (x, y, count = 10, color = '#9333EA') => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Date.now() + i,
        x,
        y,
        vx: (Math.random() - 0.5) * 8,
        vy: -Math.random() * 6 - 2,
        life: 1,
        color,
        size: Math.random() * 4 + 2,
        type: Math.random() > 0.5 ? 'circle' : 'star',
      });
    }
    setParticles(prev => [...prev, ...newParticles]);
  };
  
  // Update particles
  useEffect(() => {
    if (particles.length === 0) return;
    
    const interval = setInterval(() => {
      setParticles(prev => prev
        .map(p => ({
          ...p,
          x: p.x + p.vx,
          y: p.y + p.vy,
          vy: p.vy + 0.2, // Gravity
          life: p.life - 0.02,
        }))
        .filter(p => p.life > 0)
      );
    }, 30);
    
    return () => clearInterval(interval);
  }, [particles.length]);
  
  // Send reaction
  const sendReaction = (reaction, targetPlayerId = null) => {
    const newReaction = {
      id: Date.now(),
      emoji: reaction.emoji,
      color: reaction.color,
      x: myPosition.x,
      y: myPosition.y - 50,
      targetId: targetPlayerId,
      life: 1,
    };
    
    setReactions(prev => [...prev, newReaction]);
    soundFX.click();
    setShowReactionPicker(false);
    
    // Broadcast to other players
    if (socketRef.current?.connected) {
      socketRef.current.emit('player_reaction', { 
        reaction: reaction.id, 
        x: myPosition.x, 
        y: myPosition.y,
        targetId: targetPlayerId 
      });
    }
  };
  
  // Update reactions (float up and fade)
  useEffect(() => {
    if (reactions.length === 0) return;
    
    const interval = setInterval(() => {
      setReactions(prev => prev
        .map(r => ({
          ...r,
          y: r.y - 2,
          life: r.life - 0.02,
        }))
        .filter(r => r.life > 0)
      );
    }, 30);
    
    return () => clearInterval(interval);
  }, [reactions.length]);
  
  // Purchase premium item with VibeCoins
  const purchaseItem = async (item) => {
    if (!item.premium || !item.price) return;
    
    // Check if already purchased
    if (purchasedItems.includes(item.id)) {
      toast.info('You already own this item!');
      return;
    }
    
    // Check if user has enough coins
    if (vibecoins < item.price) {
      toast.error(`Not enough VibeCoins! Need ${item.price}, have ${vibecoins}`);
      return;
    }
    
    try {
      const res = await authFetch(`${API_URL}/api/vibecoins/spend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: item.price, reason: `Purchase: ${item.name}` })
      });
      if (!res) return;
      
      if (res.ok) {
        const data = await res.json();
        setVibecoins(data.new_balance);
        
        // Add to purchased items
        const newPurchased = [...purchasedItems, item.id];
        setPurchasedItems(newPurchased);
        localStorage.setItem('nightclub_purchased_items', JSON.stringify(newPurchased));
        
        toast.success(`🎉 Purchased ${item.name}!`);
        soundFX.success();
      } else {
        toast.error('Purchase failed');
      }
    } catch (e) {
      console.error('Purchase error:', e);
      toast.error('Purchase failed');
    }
  };
  
  // Click on player to show profile
  const goToPlayer = (sid) => {
    const p = playersRef.current[sid];
    if (!p) { toast.error('Player not found'); return; }
    const room = p.room || 'main';
    if (room.startsWith('house_') || room.startsWith('apt') || room === 'apartments') {
      toast.info("They're inside a private interior — can't jump there");
      return;
    }
    const live = interpolatedPositionsRef.current[sid];
    const x = (live?.x ?? p.x ?? 400) + 34;
    const y = live?.y ?? p.y ?? 450;
    if (room === 'city' || room === 'sewer') {
      setCurrentRoom('city');
      currentRoomRef.current = 'city';
      setCitySpawn({ room, x, y, ts: Date.now() });
    } else {
      setCurrentRoom(room);
      currentRoomRef.current = room;
      serverRoomRef.current = room;
      setMyPosition({ x, y });
      myPositionRef.current = { x, y };
      setTargetPosition(null);
      socketRef.current?.emit('change_room', { room, user_id: user?.user_id });
      socketRef.current?.emit('player_move', { x: Math.round(x), y: Math.round(y), is_walking: false });
    }
    setShowAdminPanel(false);
    toast.success(`🚀 Jumped to ${p.name || 'player'}!`);
  };

  const handlePlayerClick = async (playerId, playerData) => {
    console.log('[Nightclub] Player clicked - playerId:', playerId, 'playerData:', playerData);
    // Set basic info immediately - ensure userId is mapped correctly
    const userId = playerData.userId || playerData.user_id;
    console.log('[Nightclub] Extracted userId:', userId);
    
    setSelectedPlayer({ 
      id: playerId, 
      ...playerData, 
      userId: userId,  // Handle both formats
      isVip: false, 
      backstageAccess: false, 
      rooftopAccess: false,
      adminCommandsAccess: false,
      lightingAccess: false,
      visualizerAccess: false
    });
    setShowProfileCard(true);
    
    // If admin/owner, fetch user details including all access permissions
    if (isAdmin && userId) {
      console.log('[Nightclub] Admin fetching user details for:', userId);
      try {
        const res = await authFetch(`${API_URL}/api/nightclub/users/${userId}`);
        if (!res) return;
        if (res.ok) {
          const userData = await res.json();
          console.log('[Nightclub] User details fetched:', userData);
          setSelectedPlayer(prev => ({
            ...prev,
            isVip: userData.is_vip || false,
            backstageAccess: userData.backstage_access || false,
            rooftopAccess: userData.rooftop_access || false,
            adminCommandsAccess: userData.admin_commands_access || false,
            lightingAccess: userData.lighting_access || false,
            visualizerAccess: userData.visualizer_access || false
          }));
        } else {
          console.log('[Nightclub] Failed to fetch user details:', res.status);
        }
      } catch (e) {
        console.log('Failed to fetch user details:', e);
      }
    } else {
      console.log('[Nightclub] Not fetching user details - isAdmin:', isAdmin, 'userId:', userId);
    }
  };
  
  // WebRTC Configuration - STUN + free TURN for NAT traversal
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'turn:openrelay.metered.ca:80', username: 'openrelayproject', credential: 'openrelayproject' },
      { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' }
    ]
  };
  
  // Create peer connection for a specific peer
  const createPeerConnection = useCallback((peerId, peerName) => {
    if (peerConnectionsRef.current[peerId]) {
      console.log(`Peer connection already exists for ${peerId}`);
      return peerConnectionsRef.current[peerId];
    }
    
    console.log(`🎙️ Creating peer connection for ${peerName} (${peerId})`);
    const pc = new RTCPeerConnection(rtcConfig);
    
    // Add local stream tracks to the connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    // Handle incoming audio stream from peer
    pc.ontrack = (event) => {
      console.log(`[Voice] Received audio track from ${peerName}`);
      const remoteStream = event.streams[0];
      
      // Create audio element for this peer if not exists
      if (!remoteAudioElementsRef.current[peerId]) {
        const audio = new Audio();
        audio.autoplay = true;
        audio.srcObject = remoteStream;
        audio.volume = 0.4;  // Voice stays below music/video level
        
        // Force play (some browsers need explicit play call)
        audio.play().catch(err => console.warn('[Voice] Audio play failed:', err));
        
        remoteAudioElementsRef.current[peerId] = audio;
        console.log(`[Voice] Audio element created for ${peerName}, playing:`, !audio.paused);
      }
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current?.connected) {
        socketRef.current.emit('webrtc_ice_candidate', {
          target: peerId,
          candidate: event.candidate
        });
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log(`ICE state for ${peerName}: ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        closePeerConnection(peerId);
      }
    };
    
    peerConnectionsRef.current[peerId] = pc;
    return pc;
  }, []);
  
  // Close peer connection
  const closePeerConnection = useCallback((peerId) => {
    if (peerConnectionsRef.current[peerId]) {
      peerConnectionsRef.current[peerId].close();
      delete peerConnectionsRef.current[peerId];
    }
    if (remoteAudioElementsRef.current[peerId]) {
      const audio = remoteAudioElementsRef.current[peerId];
      if (audio instanceof Audio) {
        audio.pause();
        audio.srcObject = null;
      }
      delete remoteAudioElementsRef.current[peerId];
    }
    if (gainNodesRef.current[peerId]) {
      delete gainNodesRef.current[peerId];
    }
    setVoicePeers(prev => {
      const { [peerId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);
  
  // Close all peer connections
  const closeAllPeerConnections = useCallback(() => {
    Object.keys(peerConnectionsRef.current).forEach(peerId => {
      closePeerConnection(peerId);
    });
  }, [closePeerConnection]);
  
  // Initiate WebRTC connection to peer (caller)
  const initiateConnection = useCallback(async (peerId, peerName) => {
    const pc = createPeerConnection(peerId, peerName);
    // NOTE: local tracks are already added inside createPeerConnection — adding them
    // twice throws InvalidAccessError and silently breaks the offer.
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      socketRef.current?.emit('webrtc_offer', {
        target: peerId,
        offer: pc.localDescription
      });
      
      setVoicePeers(prev => ({
        ...prev,
        [peerId]: { name: peerName, x: 400, y: 300 }
      }));
      
      console.log(`📤 Sent offer to ${peerName}`);
    } catch (err) {
      console.error('Error creating offer:', err);
    }
  }, [createPeerConnection]);
  
  // Voice Chat Functions
  const toggleVoiceChat = async () => {
    if (voiceChatEnabled) {
      // Disable voice chat
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      closeAllPeerConnections();
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      setVoiceChatEnabled(false);
      setIsMicMuted(true);
      socketRef.current?.emit('voice_enabled', { enabled: false });
      socketRef.current?.emit('voice_status', { speaking: false });
      toast.info('🎙️ Voice chat disabled');
    } else {
      // Enable voice chat - request microphone permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        setVoiceChatEnabled(true);
        setIsMicMuted(true); // Start muted
        // Actually mute the audio tracks
        stream.getAudioTracks().forEach(track => { track.enabled = false; });
        
        // Set up audio analyzer to detect when user is speaking
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }
        const audioContext = audioContextRef.current;
        // Ensure AudioContext is running (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);
        analyser.fftSize = 256;
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        
        const checkSpeaking = () => {
          if (!localStreamRef.current) return;
          
          // Only emit speaking status if not muted
          const tracks = localStreamRef.current.getAudioTracks();
          const isActuallyMuted = tracks.length === 0 || !tracks[0].enabled;
          
          if (!isActuallyMuted) {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            
            if (average > 20) {
              socketRef.current?.emit('voice_status', { speaking: true });
            } else {
              socketRef.current?.emit('voice_status', { speaking: false });
            }
          }
          
          if (localStreamRef.current) {
            requestAnimationFrame(checkSpeaking);
          }
        };
        
        checkSpeaking();
        
        // Notify server that voice is enabled to get list of peers
        socketRef.current?.emit('voice_enabled', { enabled: true });
        socketRef.current?.emit('voice_peers_request', {});
        
        toast.success('🎙️ Voice chat enabled! Unmute to speak');
      } catch (err) {
        console.error('Microphone error:', err);
        toast.error('Could not access microphone');
      }
    }
  };
  
  const toggleMic = () => {
    if (!voiceChatEnabled) {
      toggleVoiceChat();
      return;
    }
    
    setIsMicMuted(prev => {
      const newMuted = !prev;
      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach(track => {
          track.enabled = !newMuted;
        });
      }
      if (newMuted) {
        socketRef.current?.emit('voice_status', { speaking: false });
      }
      toast.info(newMuted ? '🔇 Mic muted' : '🎤 Mic on - nearby players can hear you!');
      return newMuted;
    });
  };
  
  // Calculate distance between two points (for proximity voice)
  const getDistance = (x1, y1, x2, y2) => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };
  
  // Check if a player is within voice proximity range
  const isInVoiceProximity = (speakerX, speakerY) => {
    // Positions are already in canvas coordinates
    const distance = getDistance(myPosition.x, myPosition.y, speakerX, speakerY);
    return distance <= VOICE_PROXIMITY_RANGE;
  };
  
  // Get voice volume based on distance (capped below music level)
  const getVoiceVolume = (speakerX, speakerY) => {
    const distance = getDistance(myPosition.x, myPosition.y, speakerX, speakerY);
    if (distance >= VOICE_PROXIMITY_RANGE) return 0;
    const proximity = 1 - (distance / VOICE_PROXIMITY_RANGE);
    // Cap at 0.5 so voice never exceeds music/video volume
    return Math.min(0.5, proximity * voiceChatVolumeRef.current);
  };
  
  // Update proximity-based volume for all voice peers — LIVE positions on a 250ms tick
  // (fixes: volume never updated when the OTHER player walked closer/away)
  useEffect(() => {
    const tick = setInterval(() => {
      const me = myPositionRef.current;
      if (!me) return;
      Object.entries(remoteAudioElementsRef.current).forEach(([peerId, audioEl]) => {
        if (!(audioEl instanceof Audio)) return;
        const live = playersRef.current[peerId];
        const px = live?.x;
        const py = live?.y;
        if (px === undefined || py === undefined) return;
        const distance = Math.sqrt((px - me.x) ** 2 + (py - me.y) ** 2);
        const sameRoom = (live.room || 'main') === currentRoomRef.current;
        let volume = 0;
        if (sameRoom && distance < VOICE_PROXIMITY_RANGE) {
          volume = Math.min(0.5, (1 - distance / VOICE_PROXIMITY_RANGE) * voiceChatVolumeRef.current);
        }
        audioEl.volume = Math.max(0, Math.min(1, volume));
      });
    }, 250);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, []);
  
  // Add/remove friend — real friend system, synced with the Friends menu
  const toggleFriend = async (targetUserId, playerName) => {
    if (!isAuthenticated || !user?.user_id) { toast.error('Sign in to add friends'); return; }
    if (!targetUserId || String(targetUserId).startsWith('guest')) {
      toast.error(`${playerName} is playing as a guest — they need an account to be friends`);
      return;
    }
    try {
      const listRes = await authFetch(`${API_URL}/api/friends`);
      const list = listRes?.ok ? await listRes.json() : [];
      const existing = list.find(f => f.user_id === targetUserId);
      if (existing) {
        const res = await authFetch(`${API_URL}/api/friends/${existing.friendship_id}`, { method: 'DELETE' });
        if (res?.ok) {
          setFriends(prev => prev.filter(f => f.id !== targetUserId));
          toast.success(`Removed ${playerName} from friends`);
        }
      } else {
        const res = await authFetch(`${API_URL}/api/friends/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: targetUserId })
        });
        const d = res ? await res.json().catch(() => ({})) : {};
        if (res?.ok) toast.success(`💌 Friend request sent to ${playerName}!`);
        else toast.error(d.detail || 'Could not send friend request');
      }
    } catch {
      toast.error('Friend action failed — try again');
    }
  };

  // Keep the friends list in sync with the server
  useEffect(() => {
    if (!isAuthenticated || !user?.user_id) return;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/api/friends`);
        if (res?.ok) {
          const list = await res.json();
          setFriends(list.map(f => ({ id: f.user_id, name: f.name, friendshipId: f.friendship_id })));
        }
      } catch {}
    })();
  }, [user?.user_id, isAuthenticated]);
  
  // Start private chat
  const startPrivateChat = (playerId, playerName) => {
    setPrivateChatTarget({ id: playerId, name: playerName });
    setShowPrivateChat(true);
    setShowProfileCard(false);
  };
  
  // Room change handler
  const changeRoom = (room) => {
    rooftopSoakRef.current = false;
    // Check room-specific access
    let hasAccess = false;
    
    if (room.id === 'rooftop') {
      hasAccess = canAccessRooftop;
    } else if (room.id === 'vip_lounge') {
      hasAccess = isAdmin || user?.is_vip === true || hasVipAccess;
    } else if (room.id === 'backstage') {
      hasAccess = isAdmin || user?.role === 'artist' || user?.backstage_access === true || hasVipAccess;
    } else if (!room.isVIP) {
      hasAccess = true;
    } else {
      hasAccess = room.requiredRole && room.requiredRole.includes(user?.role);
    }
    
    if (!hasAccess) {
      soundFX.doorLocked();
      // Clearer denial messaging — tell the user what they need
      let reason = '';
      if (room.id === 'rooftop') {
        reason = 'Requires admin role, VIP tier, or rooftop pass.';
      } else if (room.id === 'vip_lounge') {
        reason = 'Requires admin role or VIP tier.';
      } else if (room.id === 'backstage') {
        reason = 'Requires admin role, artist account, or backstage pass.';
      } else if (room.requiredRole) {
        reason = `Requires ${room.requiredRole.join(' or ')} role.`;
      }
      toast.error(`Access denied to ${room.name}${reason ? ': ' + reason : '!'}`);
      return;
    }
    
    if (room.isVIP) {
      soundFX.vipEntry();
    } else {
      soundFX.doorOpen();
    }
    
    setCurrentRoom(room.id);
    setShowRoomMenu(false);
    toast.success(`Entered ${room.name}`);
    
    // Clear chat messages when changing rooms (chat is room-specific)
    setChatMessages([]);
    
    // Disconnect all voice peers when changing rooms (room-specific voice)
    if (voiceChatEnabled) {
      Object.keys(peerConnectionsRef.current).forEach(peerId => {
        if (peerConnectionsRef.current[peerId]) {
          peerConnectionsRef.current[peerId].close();
          delete peerConnectionsRef.current[peerId];
        }
        if (gainNodesRef.current[peerId]) {
          delete gainNodesRef.current[peerId];
        }
        if (remoteAudioElementsRef.current[peerId]) {
          if (remoteAudioElementsRef.current[peerId].srcObject) {
            remoteAudioElementsRef.current[peerId].srcObject = null;
          }
          delete remoteAudioElementsRef.current[peerId];
        }
      });
      setVoicePeers({});
      setSpeakingPlayers({});
      // Re-request voice peers for the new room after a short delay
      setTimeout(() => {
        socketRef.current?.emit('voice_peers_request', {});
      }, 500);
    }
    
    // Reset position for new room
    setMyPosition({ x: 400, y: 450 });
    setTargetPosition(null);
    
    // Emit room change to server with user_id for quest tracking
    serverRoomRef.current = room.id;
    if (socketRef.current?.connected) {
      socketRef.current.emit('change_room', { 
        room: room.id,
        user_id: user?.user_id 
      });
    }
  };
  
  // Apartment knock: owner accepts/declines the visitor
  const respondToKnock = (accepted) => {
    if (!knockRequest) return;
    socketRef.current?.emit('apartment_knock_response', {
      to_user_id: knockRequest.from_user_id,
      accepted,
      room_number: knockRequest.room_number,
      property: knockRequest.property || 'apartment',
    });
    setKnockRequest(null);
    if (accepted) toast.success('You let them in!');
    else toast.info('Knock declined');
  };
  
  // Auto-dismiss a knock that goes unanswered
  useEffect(() => {
    if (!knockRequest) return;
    const t = setTimeout(() => setKnockRequest(null), 20000);
    return () => clearTimeout(t);
  }, [knockRequest]);
  
  // ==================== NEW FEATURE HANDLERS ====================
  
  // Virtual Bar - Purchase drink effect (persists to backend)
  const handlePurchaseDrink = async (drink) => {
    if (vibecoins < drink.price) {
      toast.error(`Not enough VibeCoins! Need ${drink.price}`);
      return;
    }
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/bartender/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drink_id: drink.id })
      });
      if (res?.ok) {
        const data = await res.json();
        setVibecoins(data.new_balance);
      } else {
        setVibecoins(prev => prev - drink.price);
      }
    } catch (e) {
      setVibecoins(prev => prev - drink.price);
    }
    setActiveDrink({ ...drink, remainingTime: drink.duration });
    setDrinkEffect(drink.effect);
    socketRef.current?.emit('player_effect', { effect: drink.effect, color: drink.effectColor });
    soundFX?.purchase?.();
    toast.success(`${drink.name} activated!`);

    // Activate full-screen trip for RTG soda
    if (drink.effect === 'acid_trip') {
      setTripActive(true);
    }

    // Activate turtle transform
    if (drink.effect === 'turtle_transform') {
      setIsTurtle(true);
      // Broadcast turtle state to other players via socket
      socketRef.current?.emit('player_transform', { transform: 'turtle', duration: drink.duration });
    }

    // Clear effect after duration
    setTimeout(() => {
      setActiveDrink(null);
      setDrinkEffect(null);
      setTripActive(false);
      setIsTurtle(false);
      socketRef.current?.emit('player_transform', { transform: null });
      socketRef.current?.emit('player_effect', { effect: null });
      toast.info('Drink effect wore off');
    }, drink.duration * 1000);

    // Update remaining time every second
    const interval = setInterval(() => {
      setActiveDrink(prev => {
        if (!prev || prev.remainingTime <= 1) {
          clearInterval(interval);
          return null;
        }
        return { ...prev, remainingTime: prev.remainingTime - 1 };
      });
    }, 1000);
  };
  
  // Bartender - Purchase drink with backend integration
  const handleBartenderPurchase = async (drink) => {
    if (vibecoins < drink.price) {
      toast.error(`Not enough VibeCoins! Need ${drink.price}`);
      return;
    }
    
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/bartender/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drink_id: drink.id })
      });
      if (!res) return;
      
      if (res.ok) {
        const data = await res.json();
        setVibecoins(data.new_balance);
        setActiveDrink({ ...drink, remainingTime: drink.duration });
        setDrinkEffect(drink.effect);
        socketRef.current?.emit('player_effect', { effect: drink.effect, color: drink.effectColor });
        soundFX?.purchase?.();
        toast.success(`🍾 ${drink.name} - Cheers!`);

        // Activate full-screen trip for RTG soda
        if (drink.effect === 'acid_trip') {
          setTripActive(true);
        }

        // Activate turtle transform
        if (drink.effect === 'turtle_transform') {
          setIsTurtle(true);
          socketRef.current?.emit('player_transform', { transform: 'turtle', duration: drink.duration });
        }

        // Clear effect after duration
        setTimeout(() => {
          setActiveDrink(null);
          setDrinkEffect(null);
          setTripActive(false);
          setIsTurtle(false);
          socketRef.current?.emit('player_transform', { transform: null });
          socketRef.current?.emit('player_effect', { effect: null });
          toast.info('Drink effect wore off');
        }, drink.duration * 1000);
        
        // Update remaining time every second
        const interval = setInterval(() => {
          setActiveDrink(prev => {
            if (!prev || prev.remainingTime <= 1) {
              clearInterval(interval);
              return null;
            }
            return { ...prev, remainingTime: prev.remainingTime - 1 };
          });
        }, 1000);
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Purchase failed');
      }
    } catch (e) {
      console.error('Bartender purchase error:', e);
      toast.error('Purchase failed');
    }
  };
  
  // Announcements - Send broadcast
  const handleSendAnnouncement = (message) => {
    if (socketRef.current?.connected && (isAdmin || isDJ)) {
      socketRef.current.emit('announcement', {
        message,
        senderName: user?.name || 'Admin',
        role: isAdmin ? 'admin' : 'dj'
      });
      toast.success('Announcement sent!');
    }
  };
  
  // Private Rooms - Create room (with backend integration)
  const handleCreatePrivateRoom = async (roomName) => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/private-rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roomName })
      });
      if (!res) return;
      
      if (res.ok) {
        const data = await res.json();
        const newRoom = data.room;
        setPrivateRooms(prev => [...prev, newRoom]);
        setCurrentPrivateRoom(newRoom);
        toast.success(`Room created! Code: ${newRoom.code}`);
        
        if (socketRef.current?.connected) {
          socketRef.current.emit('create_private_room', newRoom);
        }
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Failed to create room');
      }
    } catch (e) {
      console.error('Create room error:', e);
      // Fallback to local creation
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const newRoom = {
        code,
        name: roomName,
        creatorName: user?.name || 'Anonymous',
        playerCount: 1
      };
      setPrivateRooms(prev => [...prev, newRoom]);
      setCurrentPrivateRoom(newRoom);
      toast.success(`Room created! Code: ${code}`);
      
      if (socketRef.current?.connected) {
        socketRef.current.emit('create_private_room', newRoom);
      }
    }
  };
  
  // Private Rooms - Join room (with backend integration)
  const handleJoinPrivateRoom = async (code) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await authFetch(`${API_URL}/api/nightclub/private-rooms/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      if (!res) return;
      
      if (res.ok) {
        const data = await res.json();
        const room = data.room;
        setCurrentPrivateRoom(room);
        toast.success(`Joined ${room.name}`);
        if (socketRef.current?.connected) {
          socketRef.current.emit('join_private_room', { code });
        }
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Room not found');
      }
    } catch (e) {
      console.error('Join room error:', e);
      // Fallback to local check
      const room = privateRooms.find(r => r.code === code);
      if (room) {
        setCurrentPrivateRoom(room);
        toast.success(`Joined ${room.name}`);
        if (socketRef.current?.connected) {
          socketRef.current.emit('join_private_room', { code });
        }
      } else {
        toast.error('Room not found');
      }
    }
  };
  
  // Private Rooms - Leave room (with backend integration)
  const handleLeavePrivateRoom = async () => {
    if (!currentPrivateRoom) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await authFetch(`${API_URL}/api/nightclub/private-rooms/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: currentPrivateRoom.code })
      });
    } catch (e) {
      console.error('Leave room error:', e);
    }
    
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_private_room', { code: currentPrivateRoom.code });
    }
    setCurrentPrivateRoom(null);
    toast.info('Left private room');
  };
  
  // Daily Rewards - Claim (with backend integration)
  const handleClaimDailyReward = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await authFetch(`${API_URL}/api/nightclub/daily-rewards/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res) return;
      
      if (res.ok) {
        const data = await res.json();
        setVibecoins(data.new_balance);
        setLoginStreak({ streak: data.new_streak, lastClaim: new Date().toISOString() });
        localStorage.setItem('nightclub_login_streak', JSON.stringify({ 
          streak: data.new_streak, 
          lastClaim: new Date().toISOString() 
        }));
        setDailyBonusClaimed(true);
        
        soundFX?.coinCollect?.();
        toast.success(`🎁 +${data.coins_reward} VibeCoins! Day ${data.new_streak} streak!`);
        
        if (data.bonus_message) {
          setTimeout(() => toast.success(data.bonus_message), 1500);
        }
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Could not claim reward');
      }
    } catch (e) {
      console.error('Daily reward claim error:', e);
      // Fallback to local calculation
      const now = new Date();
      const lastClaim = loginStreak.lastClaim ? new Date(loginStreak.lastClaim) : null;
      const streakBroken = lastClaim && (now - lastClaim) > 48 * 60 * 60 * 1000;
      
      let newStreak = streakBroken ? 1 : (loginStreak.streak + 1);
      if (newStreak > 7) newStreak = 7;
      
      const rewards = [25, 30, 40, 50, 75, 100, 200];
      const reward = rewards[Math.min(newStreak - 1, 6)];
      
      setVibecoins(prev => prev + reward);
      setLoginStreak({ streak: newStreak, lastClaim: now.toISOString() });
      localStorage.setItem('nightclub_login_streak', JSON.stringify({ streak: newStreak, lastClaim: now.toISOString() }));
      setDailyBonusClaimed(true);
      
      soundFX?.coinCollect?.();
      toast.success(`🎁 +${reward} VibeCoins! Day ${newStreak} streak!`);
    }
  };
  
  // Check if daily reward can be claimed
  const canClaimDailyReward = () => {
    if (!loginStreak.lastClaim) return true;
    const lastClaim = new Date(loginStreak.lastClaim);
    const now = new Date();
    return (now - lastClaim) > 24 * 60 * 60 * 1000;
  };
  
  // ==================== END NEW FEATURE HANDLERS ====================
  
  // Responsive canvas dimensions
  const getCanvasDimensions = useCallback(() => {
    if (isFullscreen) {
      return { width: window.innerWidth, height: window.innerHeight };
    }
    if (isMobile) {
      // 9:16 aspect ratio for mobile
      const width = Math.min(window.innerWidth - 32, 400);
      return { width, height: Math.floor(width * (16/9)) };
    }
    return { width: 800, height: 600 };
  }, [isMobile, isFullscreen]);
  
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 800, height: 600 });
  
  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    setCanvasDimensions(getCanvasDimensions());
    
    // Re-calculate on resize for fullscreen
    const handleResize = () => {
      if (isFullscreen) {
        setCanvasDimensions({ width: window.innerWidth, height: window.innerHeight });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, isFullscreen, getCanvasDimensions]);
  
  const CANVAS_WIDTH = canvasDimensions.width;
  const CANVAS_HEIGHT = canvasDimensions.height;
  
  // Force refresh user data from server when entering nightclub
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const forceRefreshUser = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/auth/me`);
        if (!res) return;
        
        if (res.ok) {
          const userData = await res.json();
          console.log('[Nightclub] Force refreshed user data:', userData);
          setUser(userData);
        }
      } catch (err) {
        console.log('Failed to refresh user data:', err);
      }
    };
    
    forceRefreshUser();
  }, [isAuthenticated, setUser]);

  // Check if user needs to set username on first join
  // IMPORTANT: Once username_set is true, NEVER show the prompt again
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    console.log('[Nightclub] User data received:', {
      username: user.username,
      username_set: user.username_set,
      name: user.name,
      role: user.role,
      email: user.email
    });
    
    // Always set displayName from user data first
    const existingName = user.username || user.name || 'Guest';
    console.log('[Nightclub] Setting displayName to:', existingName);
    setDisplayName(existingName);
    
    // If username has EVER been set, never show prompt again
    // User can only change username via Settings in hamburger menu
    if (user.username_set === true || user.username) {
      console.log('[Nightclub] Username already set, hiding prompt');
      setShowUsernamePrompt(false);
      return; // Exit early - never show prompt
    }
    
    // Check if user previously dismissed the prompt (stored in localStorage)
    const dismissedPrompt = localStorage.getItem(`username_prompt_dismissed_${user.user_id}`);
    if (dismissedPrompt) {
      console.log('[Nightclub] Prompt previously dismissed, hiding');
      setShowUsernamePrompt(false);
      return;
    }
    
    // Only show prompt for brand new users who have never set a username
    // and haven't dismissed the prompt before
    if (!user.username && !user.username_set) {
      console.log('[Nightclub] New user, showing username prompt');
      setShowUsernamePrompt(true);
    }
  }, [isAuthenticated, user]);
  
  // Handle closing the username prompt without setting
  const handleUsernamePromptClose = () => {
    setShowUsernamePrompt(false);
    // Remember that user dismissed the prompt so we don't show it again
    if (user?.user_id) {
      localStorage.setItem(`username_prompt_dismissed_${user.user_id}`, 'true');
    }
  };
  
  // Handle username completion
  const handleUsernameComplete = (newUsername) => {
    setDisplayName(newUsername);
    setShowUsernamePrompt(false);
    // Clear the dismissed flag since they actually set a username
    if (user?.user_id) {
      localStorage.removeItem(`username_prompt_dismissed_${user.user_id}`);
    }
    
    // Update user in auth store with new username
    if (user) {
      updateUser({ username: newUsername, username_set: true });
    }
  };
  
  // Socket.IO connection
  useEffect(() => {
    // For guests, set a default display name
    const guestMode = !isAuthenticated || !user;
    const guestId = `guest_${Math.random().toString(36).substr(2, 9)}`;
    
    // Don't connect while username prompt is open (user is still deciding)
    if (!guestMode && showUsernamePrompt) return;
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const SOCKET_PATH = '/api/socket.io';
    
    console.log('Connecting to Socket.IO:', BACKEND_URL, SOCKET_PATH);
    
    const socket = io(BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
      timeout: 10000,
      forceNew: false,
      autoConnect: true,
    });
    
    socketRef.current = socket;
    
    // Track last time we heard ANYTHING from the server (players_sync arrives every 5s)
    let lastServerContact = Date.now();
    const joinClub = () => {
      // Use refs so rejoins carry CURRENT avatar/position/room/user
      const u = userRef.current;
      socket.emit('join_nightclub', {
        name: guestMode ? 'Guest' : (displayName || u?.username || u?.name || 'Guest'),
        user_id: guestMode ? guestId : u?.user_id,
        role: guestMode ? 'guest' : (u?.role || 'user'),
        avatar: avatarRef.current,
        x: myPositionRef.current.x,
        y: myPositionRef.current.y,
        room: serverRoomRef.current || currentRoomRef.current
      });
    };

    socket.on('connect', () => {
      console.log('Socket.IO connected!', socket.id);
      setWsConnected(true);
      lastServerContact = Date.now();
      joinClub();
    });

    // Server says our sid is unregistered (restart/timeout) — rejoin instantly
    socket.on('rejoin_required', () => {
      console.log('[MULTIPLAYER] Server requested rejoin — rejoining now');
      joinClub();
    });
    socket.on('presence_pong', () => { lastServerContact = Date.now(); });

    // Admin summoned you (teleport) — snap to their room + position
    socket.on('force_teleport', (data) => {
      const room = data.room || 'main';
      toast.info(`✨ ${data.by || 'An admin'} summoned you!`);
      if (room === 'city' || room === 'sewer') {
        setCurrentRoom('city');
        currentRoomRef.current = 'city';
        setCitySpawn({ room, x: data.x, y: data.y, ts: Date.now() });
      } else {
        setCurrentRoom(room);
        currentRoomRef.current = room;
        serverRoomRef.current = room;
        setMyPosition({ x: data.x, y: data.y });
        myPositionRef.current = { x: data.x, y: data.y };
        setTargetPosition(null);
      }
    });
    socket.on('teleport_done', (d) => toast.success(`✨ Summoned ${d.count} player${d.count === 1 ? '' : 's'} to you!`));
    socket.on('teleport_error', (d) => toast.error(d.detail || 'Teleport failed'));
    socket.on('world_full', () => {
      toast.error('🌍 The world is full (100 players max) — try again soon!');
    });

    // Hamburger admin commands (server-enforced, broadcast to everyone)
    socket.on('chat_cleared', (d) => {
      setChatMessages([]);
      toast.info(`🧹 Chat cleared by ${d?.by || 'an admin'}`);
    });
    socket.on('slow_mode_changed', (d) => {
      setSlowMode(!!d?.enabled);
      toast.info(d?.enabled ? `🐢 Slow mode ON (5s between messages) — ${d?.by || 'admin'}` : '🚀 Slow mode OFF');
    });
    socket.on('room_lock_changed', (d) => {
      setRoomLocked(!!d?.locked);
      toast.info(d?.locked ? `🔒 Club locked by ${d?.by || 'an admin'} — no new entries` : '🔓 Club unlocked');
    });
    socket.on('chat_rejected', (d) => toast.error(d?.reason || '🐢 Slow mode — wait a few seconds'));
    socket.on('club_locked', () => toast.error('🔒 The club is locked right now — try again soon!'));
    
    socket.on('disconnect', (reason) => {
      console.log('Socket.IO disconnected:', reason);
      setWsConnected(false);
      
      // Auto-reconnect if disconnected unexpectedly
      if (reason === 'io server disconnect') {
        // Server disconnected us, try to reconnect
        socket.connect();
      }
    });
    
    socket.on('reconnect', (attemptNumber) => {
      console.log('Socket.IO reconnected after', attemptNumber, 'attempts');
      setWsConnected(true);
      lastServerContact = Date.now();
      joinClub();
    });
    
    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Socket.IO reconnection attempt:', attemptNumber);
    });
    
    socket.on('connect_error', (error) => {
      console.log('Socket.IO connection error:', error);
      setWsConnected(false);
    });
    
    // INITIALIZATION: Receive full state when joining
    socket.on('nightclub_init', (data) => {
      console.log('[MULTIPLAYER] Init received, your_sid:', data.your_sid, 'players:', Object.keys(data.players || {}).length);
      lastServerContact = Date.now();
      const mySocketId = data.your_sid;
      const myUserId = user?.user_id;
      
      // Store our socket ID for reference
      mySocketIdRef.current = mySocketId;
      
      // Get our own player data to restore saved position
      const myPlayerData = data.players?.[mySocketId];
      if (myPlayerData && myPlayerData.x !== undefined && myPlayerData.y !== undefined) {
        // Restore saved position from server
        const savedX = myPlayerData.x;
        const savedY = myPlayerData.y;
        console.log('[MULTIPLAYER] Restoring saved position:', savedX, savedY);
        setMyPosition({ x: savedX, y: savedY });
        myPositionRef.current = { x: savedX, y: savedY };
        serverPositionRef.current = { x: savedX, y: savedY };
      }
      
      // Build player list excluding self
      const newPlayers = {};
      Object.entries(data.players || {}).forEach(([sid, p]) => {
        if (sid === mySocketId) return;
        if (myUserId && p.userId === myUserId) return;
        newPlayers[sid] = { ...p, lastUpdate: Date.now() };
      });
      
      console.log('[MULTIPLAYER] Initialized with', Object.keys(newPlayers).length, 'other players');
      // Merge interpolation data - preserve existing positions for smooth transition
      Object.entries(newPlayers).forEach(([sid, p]) => {
        if (!interpolatedPositionsRef.current[sid]) {
          interpolatedPositionsRef.current[sid] = {
            x: p.x || 400, y: p.y || 450,
            targetX: p.x || 400, targetY: p.y || 450
          };
        } else {
          // Update target but keep current interpolated position
          interpolatedPositionsRef.current[sid].targetX = p.x || 400;
          interpolatedPositionsRef.current[sid].targetY = p.y || 450;
        }
      });
      // Clean up interpolation for players no longer present
      Object.keys(interpolatedPositionsRef.current).forEach(sid => {
        if (!newPlayers[sid]) delete interpolatedPositionsRef.current[sid];
      });
      playersRef.current = newPlayers;
      setPlayers({...newPlayers});
      
      // Set stream state
      setStreamUrl(data.stream_url || '');
      setIsStreaming(data.is_streaming || false);
      setLightingMode(data.lighting_mode || 'rave');
      if (data.lighting_fx) {
        setLightingFx(prev => ({ ...prev, ...data.lighting_fx }));
      }
    });
    
    // DELTA: New player joined
    socket.on('player_joined', (data) => {
      const myUserId = user?.user_id;
      const mySocketId = mySocketIdRef.current || socket.id;
      
      // Skip self
      if (data.sid === mySocketId) return;
      if (myUserId && data.player?.userId === myUserId) return;
      
      console.log('[MULTIPLAYER] Player joined:', data.player?.name);
      
      const playerX = data.player?.x || 400;
      const playerY = data.player?.y || 450;
      
      // Silent handover: same user under an old sid → migrate without any flicker
      let migrated = false;
      const joinUid = data.player?.userId;
      if (joinUid) {
        Object.keys(playersRef.current).forEach((oldSid) => {
          if (oldSid !== data.sid && playersRef.current[oldSid]?.userId === joinUid) {
            const oldInterp = interpolatedPositionsRef.current[oldSid];
            if (oldInterp) interpolatedPositionsRef.current[data.sid] = { ...oldInterp };
            delete playersRef.current[oldSid];
            delete interpolatedPositionsRef.current[oldSid];
            migrated = true;
          }
        });
      }
      
      playersRef.current = {
        ...playersRef.current,
        [data.sid]: { ...data.player, lastUpdate: Date.now() }
      };
      setPlayers({...playersRef.current});
      
      // Initialize interpolation - start at their position (unless migrated)
      if (!interpolatedPositionsRef.current[data.sid]) {
        interpolatedPositionsRef.current[data.sid] = {
          x: playerX,
          y: playerY,
          targetX: playerX,
          targetY: playerY
        };
      }
      
      // Entrance effect only for genuinely new arrivals — not reconnects
      if (!migrated && !data.rejoin) {
        setEntranceEffects(prev => ({
          ...prev,
          [data.sid]: { startTime: Date.now(), playerName: data.player?.name || 'Guest' }
        }));
      }
    });
    
    // DELTA: Player left - fade out with grace period instead of instant removal
    socket.on('player_left', (data) => {
      console.log('[MULTIPLAYER] Player left:', data.sid);
      
      // Mark player as leaving (for fade-out) instead of instant removal
      if (playersRef.current[data.sid]) {
        playersRef.current[data.sid] = {
          ...playersRef.current[data.sid],
          status: 'leaving',
          leaveTime: Date.now()
        };
      }
      
      // Remove after fade-out period (1 second)
      setTimeout(() => {
        const { [data.sid]: removed, ...remaining } = playersRef.current;
        playersRef.current = remaining;
        delete interpolatedPositionsRef.current[data.sid];
        setPlayers({...remaining});
      }, 1000);
    });
    
    // DELTA: Single player moved (real-time position update)
    socket.on('player_moved', (data) => {
      lastServerContact = Date.now();
      const mySocketId = mySocketIdRef.current || socket.id;
      
      // Skip self (shouldn't happen with skip_sid on server, but safety check)
      if (data.sid === mySocketId) return;
      const myUserId = user?.user_id;
      if (myUserId && data.userId === myUserId) return;
      
      // Update remote player's target position for interpolation
      const existingPlayer = playersRef.current[data.sid];
      playersRef.current[data.sid] = {
        ...(existingPlayer || {}),
        name: data.name || existingPlayer?.name || 'Player',
        avatar: data.avatar || existingPlayer?.avatar || {},
        role: data.role || existingPlayer?.role || 'user',
        room: data.room || existingPlayer?.room || 'main',
        x: data.x,
        y: data.y,
        is_walking: data.is_walking,
        direction: data.direction || existingPlayer?.direction,
        transform: data.transform !== undefined ? data.transform : existingPlayer?.transform,
        lastUpdate: Date.now()
      };
      
      // Update interpolation target (smooth movement towards this position)
      if (interpolatedPositionsRef.current[data.sid]) {
        interpolatedPositionsRef.current[data.sid].targetX = data.x;
        interpolatedPositionsRef.current[data.sid].targetY = data.y;
      } else {
        // New player we haven't seen - snap to position
        interpolatedPositionsRef.current[data.sid] = {
          x: data.x,
          y: data.y,
          targetX: data.x,
          targetY: data.y
        };
      }
    });
    
    // AUTHORITATIVE ROSTER SYNC (every ~10s): recovers missed joins/leaves and
    // room changes so players never stay invisible or linger as ghosts
    socket.on('players_sync', (data) => {
      lastServerContact = Date.now();
      const roster = data?.players || {};
      const mySocketId = mySocketIdRef.current || socket.id;
      const myUserId = user?.user_id;
      const next = {};
      Object.entries(roster).forEach(([sid, p]) => {
        if (sid === mySocketId) return;
        if (myUserId && p.userId === myUserId) return;
        const existing = playersRef.current[sid];
        const fresh = existing && (Date.now() - (existing.lastUpdate || 0)) < 4000;
        // keep live delta data for players we heard from recently
        next[sid] = fresh ? { ...p, ...existing } : { ...(existing || {}), ...p, lastUpdate: Date.now() };
        if (!interpolatedPositionsRef.current[sid]) {
          interpolatedPositionsRef.current[sid] = { x: p.x, y: p.y, targetX: p.x, targetY: p.y };
        } else if (!fresh) {
          interpolatedPositionsRef.current[sid].targetX = p.x;
          interpolatedPositionsRef.current[sid].targetY = p.y;
        }
      });
      Object.keys(interpolatedPositionsRef.current).forEach((sid) => {
        if (!next[sid]) delete interpolatedPositionsRef.current[sid];
      });
      playersRef.current = next;
      setPlayers({ ...next });
    });
    
    // Legacy handlers for compatibility
    socket.on('nightclub_state', (data) => {
      if (data.type === 'state_update') {
        setStreamUrl(data.stream_url || '');
        setIsStreaming(data.is_streaming || false);
        setLightingMode(data.lighting_mode || 'rave');
        if (data.lighting_fx) {
          setLightingFx(prev => ({ ...prev, ...data.lighting_fx }));
        }
      }
    });
    
    socket.on('player_positions', () => {
      // Deprecated
    });
    
    // Handle new song playing - for sync
    socket.on('song_now_playing', (data) => {
      toast.success(`Now Playing: ${data.song_title}${data.artist_name ? ' — ' + data.artist_name : ''} (requested by ${data.requested_by})`);
      setStreamUrl(data.youtube_url);
      setIsStreaming(true);
      setStreamStartedAt(data.started_at);
      setCurrentPlayingSong({
        song_title: data.song_title,
        artist_name: data.artist_name,
        user_name: data.requested_by,
        thumbnail: data.thumbnail,
        youtube_url: data.youtube_url
      });
    });
    
    // Handle stream updates from DJ Queue play
    socket.on('stream_update', (data) => {
      if (data.url) {
        setStreamUrl(data.url);
        setIsStreaming(true);
      } else if (data.is_streaming === false) {
        setIsStreaming(false);
      }
    });
    
    // Handle chat messages
    socket.on('nightclub_chat', (data) => {
      // Skip our own messages (already added locally when sent)
      if (data.player_id === socket.id) {
        return;
      }
      soundFX.chatReceive();
      setChatMessages(prev => [
        ...prev.slice(-30),
        {
          playerId: data.player_id,
          playerName: data.player_name,
          text: data.message,
          role: data.role,
          timestamp: Date.now()
        }
      ]);
    });
    
    // Handle emotes from other players
    socket.on('nightclub_emote', (data) => {
      if (data.player_id !== socket.id) {
        // Use the emote's real duration so long dances aren't cut off at 3s
        const emoteDuration = DANCE_EMOTES.find(e => e.id === data.emote)?.duration || 3000;
        const emoteStartedAt = Date.now();
        // Update the player's emote in both ref and state
        if (playersRef.current[data.player_id]) {
          playersRef.current[data.player_id] = {
            ...playersRef.current[data.player_id],
            currentEmote: data.emote,
            emoteStartedAt,
            emoteEndTime: emoteStartedAt + emoteDuration
          };
        }
        setPlayers(prev => {
          if (prev[data.player_id]) {
            return {
              ...prev,
              [data.player_id]: {
                ...prev[data.player_id],
                currentEmote: data.emote,
                emoteStartedAt,
                emoteEndTime: emoteStartedAt + emoteDuration
              }
            };
          }
          return prev;
        });
      }
    });
    
    // Remote avatar (character/tint) changed while standing still
    socket.on('avatar_updated', (data) => {
      if (data.sid === (mySocketIdRef.current || socket.id)) return;
      if (playersRef.current[data.sid]) {
        playersRef.current[data.sid] = {
          ...playersRef.current[data.sid],
          avatar: data.avatar || {}
        };
        setPlayers({ ...playersRef.current });
      }
    });
    
    // Remote player transform (turtle drink) started/ended
    socket.on('player_transform_update', (data) => {
      if (data.sid === (mySocketIdRef.current || socket.id)) return;
      if (playersRef.current[data.sid]) {
        playersRef.current[data.sid] = {
          ...playersRef.current[data.sid],
          transform: data.transform || null
        };
        setPlayers({ ...playersRef.current });
      }
    });
    
    // Remote drink effect (glow/flames/etc.) started/ended
    socket.on('player_effect_update', (data) => {
      if (data.sid === (mySocketIdRef.current || socket.id)) return;
      if (playersRef.current[data.sid]) {
        playersRef.current[data.sid] = {
          ...playersRef.current[data.sid],
          drink_effect: data.effect ? { effect: data.effect, color: data.color } : null
        };
        setPlayers({ ...playersRef.current });
      }
    });
    
    // Handle voice status updates from other players - includes position for proximity
    socket.on('voice_status_update', (data) => {
      if (data.player_id !== socket.id) {
        setSpeakingPlayers(prev => {
          if (data.speaking) {
            // Store speaker with their position
            return {
              ...prev,
              [data.player_id]: {
                speaking: true,
                x: data.x,
                y: data.y,
                name: data.player_name,
                room: data.room
              }
            };
          } else {
            // Remove speaker
            const { [data.player_id]: removed, ...rest } = prev;
            return rest;
          }
        });
        
        // Also update voice peer position for WebRTC volume control
        if (data.speaking) {
          setVoicePeers(prev => {
            if (prev[data.player_id]) {
              return {
                ...prev,
                [data.player_id]: {
                  ...prev[data.player_id],
                  x: data.x,
                  y: data.y
                }
              };
            }
            return prev;
          });
        }
      }
    });
    
    // Handle reactions from other players
    socket.on('nightclub_reaction', (data) => {
      if (data.player_id !== socket.id) {
        const reactionData = QUICK_REACTIONS.find(r => r.id === data.reaction);
        if (reactionData) {
          setReactions(prev => [...prev, {
            id: Date.now(),
            emoji: reactionData.emoji,
            color: reactionData.color,
            x: data.x * (CANVAS_WIDTH / 800),
            y: data.y * (CANVAS_HEIGHT / 600) - 50,
            life: 1,
          }]);
        }
      }
    });
    
    // Handle private messages
    socket.on('private_message', (data) => {
      if (data.to === socket.id) {
        soundFX.chatReceive();
        setPrivateMessages(prev => [...prev.slice(-50), {
          from: data.from,
          fromName: data.fromName,
          text: data.message,
          timestamp: Date.now(),
          isIncoming: true,
        }]);
        toast.info(`💬 ${data.fromName}: ${data.message.substring(0, 30)}...`);
      }
    });
    
    // Handle gift animations
    socket.on('nightclub_gift', (data) => {
      setGiftAnimations(prev => [...prev, {
        ...data,
        id: Date.now(),
        life: 1
      }]);
      soundFX.success();
      toast.success(`🎁 ${data.from_name} sent ${data.gift_emoji} to ${data.to_name}!`);
    });
    
    // Handle quest completion notifications from server
    socket.on('quest_completed', (data) => {
      toast.success(`🎉 Quest Complete: ${data.title}! Claim your ${data.reward} VibeCoins!`, {
        duration: 5000
      });
      soundFX.success();
      // Refresh quests to show updated progress - use inline fetch to avoid dependency issues
      authFetch(`${API_URL}/api/nightclub/quests`)
        .then(res => res?.json())
        .then(data => data && setQuests(data))
        .catch(err => console.log('Failed to refresh quests:', err));
    });

    // Live quest progress updates (pushed on every tracked action)
    socket.on('quest_progress', (data) => {
      setQuests(prev => prev.map(q => q.quest_id === data.quest_id
        ? { ...q, progress: data.progress, completed: data.completed }
        : q));
    });

    // Apartment: a visitor is knocking on my door
    socket.on('apartment_knock', (data) => {
      soundFX.click();
      setKnockRequest(data);
    });
    
    // Handle blocked chat messages (profanity filter)
    socket.on('chat_blocked', (data) => {
      toast.error('⛔ ' + (data.reason || 'Message blocked'), {
        duration: 3000
      });
    });
    
    // Handle access notifications (VIP, Rooftop, Backstage granted)
    socket.on('access_notification', (data) => {
      const accessType = data.access_type || 'Special';
      const grantedBy = data.granted_by || 'Admin';
      toast.success(`🎉 ${grantedBy} granted you ${accessType} access!`, {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }
      });
      // Play celebration sound
      soundFX?.celebration?.();
      // Refresh user permissions
      authFetch(`${API_URL}/api/auth/me`)
        .then(res => res?.json())
        .then(data => {
          if (data?.user_id) {
            console.log('Refreshed user permissions:', data);
          }
        })
        .catch(err => console.log('Failed to refresh permissions:', err));
    });
    
    // Handle DJ tips (show floating animation)
    socket.on('dj_tip', (data) => {
      toast.success(`${data.from_name} tipped the DJ ${data.amount} VibeCoins!`, {
        duration: 4000,
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          fontWeight: 'bold'
        }
      });
      soundFX?.celebration?.();
    });
    
    // Handle player entrance announcements
    socket.on('player_entrance', (data) => {
      if (data.player_id !== socket.id) {
        // Add entrance effect for the new player
        setEntranceEffects(prev => ({
          ...prev,
          [data.player_id]: {
            startTime: Date.now(),
            playerName: data.player_name
          }
        }));
        // Show subtle toast notification
        toast(`✨ ${data.player_name} entered the ${data.room === 'main' ? 'club' : data.room}`, {
          duration: 2000,
          style: { background: '#1a1a2e', color: '#eee' }
        });
        // Clear entrance effect after animation completes
        setTimeout(() => {
          setEntranceEffects(prev => {
            const updated = { ...prev };
            delete updated[data.player_id];
            return updated;
          });
        }, 2000);
      }
    });
    
    // Handle typing indicators
    socket.on('typing_indicator', (data) => {
      setTypingPlayers(prev => ({
        ...prev,
        [data.player_id]: { name: data.player_name, timestamp: Date.now() }
      }));
    });
    
    socket.on('typing_stopped', (data) => {
      setTypingPlayers(prev => {
        const updated = { ...prev };
        delete updated[data.player_id];
        return updated;
      });
    });
    
    // Handle announcements from DJ/Admin
    socket.on('announcement', (data) => {
      setCurrentAnnouncement(data);
      soundFX?.notification?.();
    });
    
    // Handle themed nights - real-time theme changes
    socket.on('theme_changed', (data) => {
      setActiveTheme(data);
      toast.success(`Theme changed to ${data.name || 'new theme'}!`);
    });
    
    // Friend notifications (filtered to the intended recipient)
    socket.on('friend_request_received', (data) => {
      if (data.to_user_id && user?.user_id && data.to_user_id !== user.user_id) return;
      toast.info(`💌 ${data.from_name} sent you a friend request! Check the Friends menu`, { duration: 6000 });
    });
    socket.on('friend_request_accepted', (data) => {
      if (data.to_user_id && user?.user_id && data.to_user_id !== user.user_id) return;
      toast.success(`💚 ${data.by_name} accepted your friend request!`, { duration: 6000 });
      authFetch(`${API_URL}/api/friends`).then(async (res) => {
        if (res?.ok) {
          const list = await res.json();
          setFriends(list.map(f => ({ id: f.user_id, name: f.name, friendshipId: f.friendship_id })));
        }
      }).catch(() => {});
    });
    
    // Handle role changes - real-time notification when your role is changed
    socket.on('role_changed', (data) => {
      const newRole = data.new_role;
      const changedBy = data.changed_by || 'Admin';
      // Update auth store with new role
      updateUser({ role: newRole });
      // Show banner
      setRoleChangeBanner({ visible: true, newRole, changedBy });
      // Auto-dismiss after 5 seconds
      setTimeout(() => setRoleChangeBanner(prev => ({ ...prev, visible: false })), 5000);
      // Play sound
      soundFX?.celebration?.();
    });
    
    // Handle player role updates broadcast (update other players' displayed role)
    socket.on('player_role_updated', (data) => {
      const { user_id: changedUserId, new_role, user_name } = data;
      // Update the players map so their role badge reflects in chat
      setPlayers(prev => {
        const updated = { ...prev };
        for (const sid in updated) {
          if (updated[sid].userId === changedUserId) {
            updated[sid] = { ...updated[sid], role: new_role };
          }
        }
        return updated;
      });
      // Also update in the ref
      for (const sid in playersRef.current) {
        if (playersRef.current[sid]?.userId === changedUserId) {
          playersRef.current[sid].role = new_role;
        }
      }
    });
    
    // Handle rooms_updated from AI room creation
    socket.on('rooms_updated', (data) => {
      // Re-fetch custom rooms
      const fetchRooms = async () => {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${API_URL}/api/nightclub/custom-rooms`);
          if (res.ok) {
            const rooms = await res.json();
            setCustomRooms(rooms.map(r => ({
              id: r.room_id,
              name: r.name,
              description: r.description || '',
              isVIP: false,
              maxPlayers: 25,
              isCustom: true,
              theme: {
                floorColor1: r.theme?.floorColor || r.theme?.bgColor || '#1a1a2e',
                floorColor2: r.theme?.bgColor || '#0f0f1a',
                gridColor: `${r.theme?.accentColor || '#9333EA'}26`,
                accentColor: r.theme?.accentColor || '#9333EA',
                ambientColor: `${r.theme?.accentColor || '#9333EA'}1a`,
                wallGraffiti: [],
                showDJ: r.theme?.showDJ || false,
                showLEDScreen: false,
                showSpeakers: false,
              }
            })));
          }
        } catch (e) {
          console.log('Failed to refresh custom rooms');
        }
      };
      fetchRooms();
    });
    
    // Handle private room updates
    socket.on('private_room_update', (data) => {
      setPrivateRooms(data.rooms || []);
    });
    
    // WebRTC Signaling Handlers
    
    // Flush ICE candidates queued before the remote description was ready
    const flushPendingIce = async (peerId) => {
      const pc = peerConnectionsRef.current[peerId];
      const pending = pendingIceCandidatesRef.current[peerId] || [];
      pendingIceCandidatesRef.current[peerId] = [];
      if (!pc) return;
      for (const cand of pending) {
        try { await pc.addIceCandidate(new RTCIceCandidate(cand)); } catch (e) { console.warn('flush ICE failed', e); }
      }
    };
    
    // Handle list of voice-enabled peers in room
    socket.on('voice_peers_list', async (data) => {
      console.log('🎙️ Voice peers in room:', data.peers);
      for (const peer of data.peers) {
        if (!peerConnectionsRef.current[peer.sid]) {
          // Create connection to each existing peer (we're the initiator)
          const pc = new RTCPeerConnection(rtcConfig);
          
          // Add local stream
          if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => {
              pc.addTrack(track, localStreamRef.current);
            });
          }
          
          // Handle remote stream
          pc.ontrack = (event) => {
            console.log(`[Voice] Received audio from ${peer.name}`);
            const remoteStream = event.streams[0];
            
            const audio = new Audio();
            audio.autoplay = true;
            audio.srcObject = remoteStream;
            audio.volume = 0.4;  // Voice stays below music/video level
            audio.play().catch(err => console.warn('[Voice] Audio play failed:', err));
            
            remoteAudioElementsRef.current[peer.sid] = audio;
            console.log(`[Voice] Audio element created for ${peer.name}, playing:`, !audio.paused);
          };
          
          pc.onicecandidate = (event) => {
            if (event.candidate) {
              socket.emit('webrtc_ice_candidate', {
                target: peer.sid,
                candidate: event.candidate
              });
            }
          };
          
          pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
              pc.close();
              delete peerConnectionsRef.current[peer.sid];
              delete gainNodesRef.current[peer.sid];
            }
          };
          
          peerConnectionsRef.current[peer.sid] = pc;
          
          // Create and send offer
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('webrtc_offer', {
              target: peer.sid,
              offer: pc.localDescription
            });
            
            setVoicePeers(prev => ({
              ...prev,
              [peer.sid]: { name: peer.name, x: peer.x, y: peer.y }
            }));
            
            console.log(`📤 Sent offer to ${peer.name}`);
          } catch (err) {
            console.error('Error creating offer:', err);
          }
        }
      }
    });
    
    // Handle new peer joining with voice enabled
    socket.on('peer_voice_status', async (data) => {
      console.log('🎙️ Peer voice status:', data);
      if (data.enabled) {
        // Update peer position for proximity
        setVoicePeers(prev => ({
          ...prev,
          [data.sid]: { name: data.name, x: data.x, y: data.y }
        }));
      } else {
        // Peer disabled voice - close connection
        if (peerConnectionsRef.current[data.sid]) {
          peerConnectionsRef.current[data.sid].close();
          delete peerConnectionsRef.current[data.sid];
        }
        if (gainNodesRef.current[data.sid]) {
          delete gainNodesRef.current[data.sid];
        }
        setVoicePeers(prev => {
          const { [data.sid]: removed, ...rest } = prev;
          return rest;
        });
      }
    });
    
    // Handle incoming WebRTC offer
    socket.on('webrtc_offer', async (data) => {
      console.log(`📥 Received offer from ${data.from}`);
      
      if (!localStreamRef.current) {
        console.log('No local stream, ignoring offer');
        return;
      }
      
      // Replace any stale connection from this peer (re-negotiation)
      if (peerConnectionsRef.current[data.from]) {
        try { peerConnectionsRef.current[data.from].close(); } catch (e) { /* noop */ }
        delete peerConnectionsRef.current[data.from];
      }
      
      // Create peer connection for the caller
      const pc = new RTCPeerConnection(rtcConfig);
      
      // Add local stream
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
      
      // Handle remote stream
      pc.ontrack = (event) => {
        console.log(`[Voice] Received audio from peer ${data.from}`);
        const remoteStream = event.streams[0];
        
        const audio = new Audio();
        audio.autoplay = true;
        audio.srcObject = remoteStream;
        audio.volume = 0.4;  // Voice stays below music/video level
        audio.play().catch(err => console.warn('[Voice] Audio play failed:', err));
        
        remoteAudioElementsRef.current[data.from] = audio;
        console.log(`[Voice] Audio element created for peer ${data.from}, playing:`, !audio.paused);
      };
      
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit('webrtc_ice_candidate', {
            target: data.from,
            candidate: event.candidate
          });
        }
      };
      
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          pc.close();
          delete peerConnectionsRef.current[data.from];
          delete gainNodesRef.current[data.from];
        }
      };
      
      peerConnectionsRef.current[data.from] = pc;
      
      // Ensure this peer is tracked in voicePeers for proximity volume
      setVoicePeers(prev => {
        if (!prev[data.from]) {
          // Get peer position from players list if available
          const peerPlayer = playersRef.current[data.from];
          return {
            ...prev,
            [data.from]: { 
              name: peerPlayer?.name || 'Peer', 
              x: peerPlayer?.x || 400, 
              y: peerPlayer?.y || 300 
            }
          };
        }
        return prev;
      });
      
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        await flushPendingIce(data.from);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        
        socket.emit('webrtc_answer', {
          target: data.from,
          answer: pc.localDescription
        });
        
        console.log(`📤 Sent answer to ${data.from}`);
      } catch (err) {
        console.error('Error handling offer:', err);
      }
    });
    
    // Handle WebRTC answer
    socket.on('webrtc_answer', async (data) => {
      console.log(`📥 Received answer from ${data.from}`);
      const pc = peerConnectionsRef.current[data.from];
      if (pc) {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          await flushPendingIce(data.from);
          console.log(`✅ Connection established with ${data.from}`);
        } catch (err) {
          console.error('Error handling answer:', err);
        }
      }
    });
    
    // Handle ICE candidates — queue any that arrive before the remote description
    // is set (they used to be dropped, causing silent voice connection failures)
    socket.on('webrtc_ice_candidate', async (data) => {
      const pc = peerConnectionsRef.current[data.from];
      if (pc && pc.remoteDescription) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch (err) {
          console.error('Error adding ICE candidate:', err);
        }
      } else {
        if (!pendingIceCandidatesRef.current[data.from]) pendingIceCandidatesRef.current[data.from] = [];
        pendingIceCandidatesRef.current[data.from].push(data.candidate);
      }
    });
    
    // Watchdog: server broadcasts players_sync every 5s. If we look "connected"
    // but heard nothing for too long, the socket is half-dead — ping, then force
    // a clean reconnect cycle if still silent.
    const watchdog = setInterval(() => {
      if (document.hidden || !socket.connected) return;
      const silence = Date.now() - lastServerContact;
      if (silence > 30000) {
        console.log('[MULTIPLAYER] Half-dead socket detected — forcing reconnect');
        lastServerContact = Date.now();
        socket.disconnect();
        socket.connect();
      } else if (silence > 12000) {
        socket.emit('presence_ping', {});
      }
    }, 8000);

    // Returning to the tab/app: verify presence immediately instead of waiting
    const onVisible = () => {
      if (document.hidden) return;
      lastServerContact = Date.now();
      if (socket.connected) {
        socket.emit('presence_ping', {});
      } else {
        socket.connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', onVisible);
      // Close all peer connections on unmount
      Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
      peerConnectionsRef.current = {};
      socket.disconnect();
    };
    // eslint-disable-next-line
  }, [isAuthenticated, user?.user_id, showUsernamePrompt, displayName]);
  
  // No longer need periodic cleanup - using player_left events instead
  // Keeping empty effect for future use if needed
  
  // Periodic sync: Update React state from refs every 500ms for UI updates
  useEffect(() => {
    const syncInterval = setInterval(() => {
      // Only update if there are changes (compare keys)
      const refKeys = Object.keys(playersRef.current).sort().join(',');
      setPlayers(prev => {
        const stateKeys = Object.keys(prev).sort().join(',');
        if (refKeys !== stateKeys) {
          return { ...playersRef.current };
        }
        return prev;
      });
    }, 500);
    
    return () => clearInterval(syncInterval);
  }, []);
  
  // Function to fetch quests (called on mount and when quest completes)
  const fetchQuests = useCallback(() => {
    if (!isAuthenticated) return;
    
    authFetch(`${API_URL}/api/nightclub/quests`)
      .then(res => res?.json())
      .then(data => data && setQuests(data))
      .catch(err => console.log('Failed to load quests:', err));
  }, [isAuthenticated]);
  
  // Load VibeCoins and Quests on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Load VibeCoins
    authFetch(`${API_URL}/api/nightclub/vibecoins`)
      .then(res => res?.json())
      .then(data => {
        if (data) {
          setVibecoins(data.vibecoins || 100);
          setDailyBonusClaimed(data.last_daily_bonus === new Date().toISOString().split('T')[0]);
        }
      })
      .catch(err => console.log('Failed to load vibecoins:', err));
    
    // Check hourly reward status
    authFetch(`${API_URL}/api/nightclub/vibecoins/hourly-status`)
      .then(res => res?.json())
      .then(data => {
        if (data) {
          setHourlyRewardReady(data.can_claim);
          setHourlyCountdown(data.seconds_remaining || 0);
        }
      })
      .catch(err => console.log('Failed to check hourly status:', err));
    
    // Load owned outfit items (permanent unlocks)
    authFetch(`${API_URL}/api/nightclub/outfits/owned`)
      .then(res => (res && res.ok ? res.json() : null))
      .then(data => data && setOwnedOutfitItems(data.owned || []))
      .catch(err => console.log('Failed to load owned outfits:', err));

    // Load Quests
    fetchQuests();
      
  }, [isAuthenticated, fetchQuests]);
  
  // Hourly reward countdown timer
  useEffect(() => {
    if (!isAuthenticated || hourlyRewardReady) return;
    
    const timer = setInterval(() => {
      setHourlyCountdown(prev => {
        if (prev <= 1) {
          setHourlyRewardReady(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isAuthenticated, hourlyRewardReady]);
  
  // Claim hourly reward
  const claimHourlyReward = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/vibecoins/hourly`, {
        method: 'POST'
      });
      if (!res) return;
      
      const data = await res.json();
      
      if (res.ok) {
        setVibecoins(data.new_balance);
        setHourlyRewardReady(false);
        setHourlyCountdown(3600);
        toast.success(`🎉 ${data.message}`);
      } else {
        toast.error(data.detail || 'Failed to claim reward');
      }
    } catch (err) {
      toast.error('Failed to claim hourly reward');
    }
  };
  
  // Buy an outfit item (permanent VibeCoin unlock) then auto-equip it
  const handlePurchaseOutfitItem = async (item, field) => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/outfits/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.id })
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        setVibecoins(data.new_balance);
        setOwnedOutfitItems(data.owned || []);
        if (field) updateAvatar(field, item.id);
        soundFX?.purchase?.();
        toast.success(`${item.name} unlocked!`);
      } else {
        toast.error(data.detail || 'Purchase failed');
      }
    } catch (err) {
      toast.error('Purchase failed');
    }
  };

  // Refresh user permissions from server (for role, rooftop_access, vip, etc.)
  const refreshUserPermissions = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const res = await authFetch(`${API_URL}/api/auth/me`);
      if (!res) return;
      
      if (res.ok) {
        const userData = await res.json();
        console.log('[Nightclub] Refreshed user data from server:', userData);
        // Update user in auth store with ALL important fields including role
        updateUser({
          role: userData.role,
          rooftop_access: userData.rooftop_access,
          is_vip: userData.is_vip,
          backstage_access: userData.backstage_access,
          username: userData.username,
          username_set: userData.username_set,
          username_changed_at: userData.username_changed_at,
          has_admin_commands: userData.has_admin_commands,
          has_lighting_control: userData.has_lighting_control,
          has_visualizer_access: userData.has_visualizer_access,
        });
      }
    } catch (err) {
      console.log('Failed to refresh permissions:', err);
    }
  }, [isAuthenticated, updateUser]);
  useEffect(() => {
    if (!isAuthenticated) return;
    
    refreshUserPermissions();
    const interval = setInterval(refreshUserPermissions, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUserPermissions]);
  
  // Fetch current theme and VIP access on mount
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    // Fetch current nightclub theme
    fetch(`${API_URL}/api/nightclub/current-theme`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setActiveTheme(data); })
      .catch(() => {});
    // Check VIP access for current user
    if (isAuthenticated) {
      authFetch(`${API_URL}/api/nightclub/vip/check`)
        .then(r => r?.ok ? r.json() : null)
        .then(data => { if (data) setHasVipAccess(data.has_access); })
        .catch(() => {});
    }
  }, [isAuthenticated]);
  
  // Update avatar when it changes
  useEffect(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('avatar_update', { avatar });
    }
  }, [avatar]);
  
  // Position emit is now handled directly in the game loop for better performance
  
  // Music-reactive audio analysis (simulated based on streaming state)
  useEffect(() => {
    if (isStreaming) {
      const audioInterval = setInterval(() => {
        // Simulate beat detection
        setAudioLevel(Math.random() * 0.5 + 0.5);
      }, 100);
      return () => clearInterval(audioInterval);
    } else {
      setAudioLevel(0);
    }
  }, [isStreaming]);
  
  // Use imported getYouTubeVideoId from YouTubePlayer component
  const currentVideoId = useMemo(() => getYouTubeVideoId(streamUrl), [streamUrl]);
  
  // YouTube iframe API message listener for video end detection
  useEffect(() => {
    if (!isStreaming || !currentVideoId) return;
    
    const handleYouTubeMessage = (event) => {
      // Only handle messages from YouTube
      if (!event.origin.includes('youtube.com')) return;
      
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // YouTube iframe API sends player state changes
        // State 0 = ended
        if ((data.event === 'onStateChange' && data.info === 0) ||
            (data.event === 'infoDelivery' && data.info?.playerState === 0)) {
          console.log('Video ended');
        }
      } catch (e) {
        // Not a JSON message, ignore
      }
    };
    
    window.addEventListener('message', handleYouTubeMessage);
    
    // Enable JS API on the iframe
    const enableJsApi = () => {
      if (youtubeRef.current?.contentWindow) {
        youtubeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: 'listening' }),
          '*'
        );
      }
    };
    
    const timer = setTimeout(enableJsApi, 2000);
    
    return () => {
      window.removeEventListener('message', handleYouTubeMessage);
      clearTimeout(timer);
    };
  }, [isStreaming, currentVideoId]);
  
  // Draw the nightclub environment
  const drawEnvironment = useCallback((ctx) => {
    const W = CANVAS_WIDTH;
    const H = CANVAS_HEIGHT;
    const scale = isMobile ? 0.5 : 1;
    const floorY = isMobile ? H * 0.35 : 200;
    
    // Get current room theme
    const roomInfo = allRoomsRef.current.find(r => r.id === currentRoom) || CLUB_ROOMS[0];
    const baseTheme = roomInfo.theme;
    // Apply themed nights override for main floor
    const theme = (currentRoom === 'main' && activeTheme) ? {
      ...baseTheme,
      floorColor1: activeTheme.floorColor1 || baseTheme.floorColor1,
      floorColor2: activeTheme.floorColor2 || baseTheme.floorColor2,
      accentColor: activeTheme.accentColor || baseTheme.accentColor,
      gridColor: activeTheme.gridColor || baseTheme.gridColor,
      ambientColor: activeTheme.ambientColor || baseTheme.ambientColor,
    } : baseTheme;
    
    // Clear canvas
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, W, H);
    
    // Calculate lighting intensity based on mode and audio
    const currentAudioLevel = audioLevelRef.current;
    let lightIntensity = 0.3;
    if (lightingMode === 'ambient') lightIntensity = 0.15;
    else if (lightingMode === 'drop') lightIntensity = 0.5 + currentAudioLevel * 0.3;
    else lightIntensity = 0.3 + currentAudioLevel * 0.2;
    
    // Draw room-specific ambient overlay
    ctx.fillStyle = theme.ambientColor;
    ctx.fillRect(0, 0, W, H);
    
    // Draw floor with room-specific colors
    const floorGradient = ctx.createLinearGradient(0, floorY, 0, H);
    floorGradient.addColorStop(0, theme.floorColor1);
    floorGradient.addColorStop(1, theme.floorColor2);
    ctx.fillStyle = floorGradient;
    ctx.fillRect(0, floorY, W, H - floorY);
    
    // Floor grid lines with room-specific color
    ctx.strokeStyle = theme.gridColor;
    ctx.lineWidth = 1;
    const gridSize = isMobile ? 30 : 40;
    for (let i = floorY; i < H; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(W, i);
      ctx.stroke();
    }
    for (let i = 0; i < W; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(i, floorY);
      ctx.lineTo(i, H);
      ctx.stroke();
    }
    
    // Draw room-specific decorations
    if (theme.hasVelvetRopes) {
      drawVelvetRopes(ctx, W, H, floorY, theme.accentColor);
    }
    if (theme.hasChandeliers) {
      drawChandeliers(ctx, W, lightingPhaseRef.current, currentAudioLevel);
    }
    if (theme.hasEquipmentCases) {
      drawEquipmentCases(ctx, W, H, floorY);
    }
    if (theme.hasCables) {
      drawCables(ctx, W, H, floorY);
    }
    if (theme.hasLoungeSeating) {
      drawLoungeSeating(ctx, W, H, floorY, theme.accentColor);
    }
    
    // DJ Booth platform - only in main room
    if (theme.showDJ !== false) {
      const boothW = isMobile ? W * 0.5 : 300;
      const boothH = isMobile ? 80 : 120;
      const boothX = (W - boothW) / 2;
      const boothY = isMobile ? 60 : 80;
      
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(boothX, boothY, boothW, boothH);
      ctx.strokeStyle = theme.accentColor;
      ctx.globalAlpha = 0.5 + currentAudioLevel * 0.5;
      ctx.lineWidth = 2;
      ctx.strokeRect(boothX, boothY, boothW, boothH);
      ctx.globalAlpha = 1;
      
      // Draw NPC DJ at booth
      if (djPlaying && currentDJ) {
        drawNPCDJ(ctx, W / 2, boothY + boothH - 20, currentDJ, djBounceRef.current, currentAudioLevel);
      }
    }
    
    // Speaker stacks - only in main room
    if (theme.showSpeakers !== false) {
      const speakerW = isMobile ? 50 : 80;
      const speakerH = isMobile ? 100 : 150;
      drawSpeakerStack(ctx, isMobile ? 20 : 100, isMobile ? 40 : 60, speakerW, speakerH, currentAudioLevel, lightingPhaseRef.current, theme.accentColor);
      drawSpeakerStack(ctx, W - speakerW - (isMobile ? 20 : 100), isMobile ? 40 : 60, speakerW, speakerH, currentAudioLevel, lightingPhaseRef.current, theme.accentColor);
    }
    
    // Main LED Screen - only in main room
    if (theme.showLEDScreen !== false) {
      const screenW = isMobile ? W * 0.55 : 240;
      const screenH = isMobile ? 100 : 140;
      const screenX = (W - screenW) / 2;
      const screenY = isMobile ? 10 : 20;
      drawLEDScreen(ctx, screenX, screenY, screenW, screenH, isStreaming, lightingPhaseRef.current, currentAudioLevel, theme.accentColor);
    }
    
    // === NEW: Advanced Lighting System (PAR cans, lasers, moving heads) ===
    if (currentRoom === 'main' || !allRoomsRef.current.find(r => r.id === currentRoom)?.theme?.showDJ === false) {
      drawMainRoomLighting(ctx, W, H, lightingPhaseRef.current, currentAudioLevel, lightingPreset, lightingFx);
    }
    
    // Safety reset - ensure lighting didn't leak any canvas state
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    
    // Floor fog (handled by new lighting system for main room, keep for other rooms)
    if (currentRoom !== 'main') {
      drawFog(ctx, W, H, lightingPhaseRef.current, currentAudioLevel, theme.accentColor);
    }
    
    // Weather effects (rain, sunset on rooftop)
    drawWeatherEffects(ctx, W, H, lightingPhaseRef.current);
    
    // Room-specific graffiti on walls
    if (!isMobile && theme.wallGraffiti) {
      drawRoomGraffiti(ctx, W, H, theme.wallGraffiti, theme.accentColor);
    }
    
    // Room name banner (for all rooms)
    ctx.font = `bold ${isMobile ? 14 : 18}px sans-serif`;
    ctx.fillStyle = theme.accentColor;
    ctx.textAlign = 'center';
    ctx.shadowColor = theme.accentColor;
    ctx.shadowBlur = 10;
    ctx.fillText(roomInfo.name, W / 2, isMobile ? 25 : 35);
    ctx.shadowBlur = 0;
    
    if (roomInfo.isVIP) {
      ctx.font = `${isMobile ? 10 : 12}px sans-serif`;
      ctx.fillStyle = '#FCD34D';
      ctx.shadowColor = '#FCD34D';
      ctx.shadowBlur = 5;
      ctx.fillText('★ VIP ★', W / 2, isMobile ? 38 : 50);
      ctx.shadowBlur = 0;
    }
    
    // Rooftop-specific decorations
    if (theme.hasStars) {
      drawStarrySky(ctx, W, H, lightingPhaseRef.current);
    }
    if (theme.hasCityView) {
      drawCityView(ctx, W, H, lightingPhaseRef.current);
    }
    if (theme.hasStringLights) {
      drawStringLights(ctx, W, H, lightingPhaseRef.current);
    }
    if (theme.hasTrees) {
      drawRooftopTrees(ctx, W, H, floorY, lightingPhaseRef.current);
    }
    
    // Draw rooftop furniture (loungers, bar) - NEW P1 FEATURE
    if (currentRoom === 'rooftop') {
      drawRooftopFurniture(ctx, W, H, floorY, lightingPhaseRef.current);
      // Rooftop overhaul: fire pit + bubbling hot tub (SKY BAR sign now lives on the bar itself)
      drawFirePit(ctx, W * 0.24, floorY + 160, lightingPhaseRef.current);
      drawHotTub(ctx, W * 0.78, floorY + 175, lightingPhaseRef.current);
    }
    
    // VIP lounge overhaul: backlit bar shelf + champagne tower
    if (currentRoom === 'vip_lounge') {
      drawBarShelf(ctx, W - (isMobile ? 150 : 190), isMobile ? 55 : 75, lightingPhaseRef.current);
      const bsx = W - (isMobile ? 150 : 190);
      const bsy = isMobile ? 55 : 75;
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = `rgba(252,211,77,${0.5 + Math.abs(Math.sin(lightingPhaseRef.current * 0.05)) * 0.5})`;
      ctx.fillText('tap to order 🍸', bsx + 65, bsy + 88);
      drawChampagneTower(ctx, isMobile ? 80 : 130, floorY + 140, lightingPhaseRef.current);
    }
    
    // Backstage overhaul: ON AIR neon
    if (currentRoom === 'backstage') {
      drawNeonSign(ctx, W / 2, isMobile ? 62 : 78, 'ON AIR', '#f87171', lightingPhaseRef.current);
    }
    
    // Apartments entrance door — Chill Zone only, next to the LED screen
    if (currentRoom === 'chill_zone') {
      drawApartmentsDoor(ctx, W, H, floorY, lightingPhaseRef.current, isMobile);
    }
    
    // Draw elevator in VIP Lounge (for users with rooftop access) - now visible on mobile too
    if (currentRoom === 'vip_lounge') {
      drawElevator(ctx, W, H, floorY, canAccessRooftop);
    }
    
    // Draw return elevator in Rooftop (goes back to VIP) - visible on mobile too
    if (currentRoom === 'rooftop') {
      drawReturnElevator(ctx, W, H, floorY);
    }
    
    // Visualizer Room - Immersive audio-reactive visuals (OVERHAULED)
    if (currentRoom === 'visualizer') {
      drawAdvancedVisualizerRoom(ctx, W, H, floorY, lightingPhaseRef.current, currentAudioLevel, visualizerSettings);
    }
    
    // Safety reset after visualizer
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    
    // Drop effect overlay (dramatic transition triggered by DJ/admin)
    if (dropEffect > 0) {
      drawDropEffect(ctx, W, H, dropEffect, dropColorRef.current);
    }
    
    // eslint-disable-next-line
  }, [CANVAS_WIDTH, CANVAS_HEIGHT, isMobile, isStreaming, lightingMode, lightingPreset, lightingFx, audioData, djPlaying, currentDJ, currentRoom, isAdmin, canAccessRooftop, dropEffect, activeTheme]);
  
  // Room-specific decoration drawing helpers come from canvasUtils.js — no
  // local wrappers needed (they were a leftover refactor artifact that broke
  // the rooftop room when the wrappers weren't declared before the useCallback
  // referenced them).
  
  // Draw rooftop trees - use imported version from canvasUtils
  
  // Draw wall ads - admin configurable flyers with cool frames
  // 5 evenly spaced frames in a row below the DJ booth area
  // Draw elevator (visible in VIP room for users with rooftop access)
  // Positioned in center on the wall, below the video screen - responsive for mobile
  const drawElevator = (ctx, W, H, floorY, canAccess) => {
    // Bigger, easier-to-tap elevator
    const elevatorW = isMobile ? 58 : 80;
    const elevatorH = isMobile ? 66 : 94;
    const elevatorX = Math.round(W * 0.22) - elevatorW / 2;  // Left wall — clear of the video screen
    const elevatorY = floorY - elevatorH - 5;  // Just above the floor line
    
    // Draw elevator background/shaft - dark metallic
    const shaftGradient = ctx.createLinearGradient(elevatorX, elevatorY, elevatorX + elevatorW, elevatorY);
    shaftGradient.addColorStop(0, '#1a1a2a');
    shaftGradient.addColorStop(0.5, '#2a2a3a');
    shaftGradient.addColorStop(1, '#1a1a2a');
    ctx.fillStyle = shaftGradient;
    ctx.fillRect(elevatorX, elevatorY, elevatorW, elevatorH);
    
    // Metallic frame
    ctx.strokeStyle = canAccess ? '#FFD700' : '#666';
    ctx.lineWidth = 4;
    ctx.strokeRect(elevatorX, elevatorY, elevatorW, elevatorH);
    
    // Inner frame highlight
    ctx.strokeStyle = canAccess ? '#FFF8DC' : '#888';
    ctx.lineWidth = 1;
    ctx.strokeRect(elevatorX + 4, elevatorY + 4, elevatorW - 8, elevatorH - 8);
    
    // Door panels with metallic look
    const doorGradient = ctx.createLinearGradient(elevatorX + 6, elevatorY, elevatorX + elevatorW - 6, elevatorY);
    doorGradient.addColorStop(0, '#4a4a5a');
    doorGradient.addColorStop(0.3, '#6a6a7a');
    doorGradient.addColorStop(0.5, '#5a5a6a');
    doorGradient.addColorStop(0.7, '#6a6a7a');
    doorGradient.addColorStop(1, '#4a4a5a');
    ctx.fillStyle = doorGradient;
    
    // Left door
    ctx.fillRect(elevatorX + 6, elevatorY + 6, (elevatorW - 14) / 2, elevatorH - 12);
    // Right door
    ctx.fillRect(elevatorX + 6 + (elevatorW - 14) / 2 + 2, elevatorY + 6, (elevatorW - 14) / 2, elevatorH - 12);
    
    // Door split line
    ctx.strokeStyle = canAccess ? '#FFD700' : '#555';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(elevatorX + elevatorW / 2, elevatorY + 6);
    ctx.lineTo(elevatorX + elevatorW / 2, elevatorY + elevatorH - 6);
    ctx.stroke();
    
    // Door handles
    ctx.fillStyle = canAccess ? '#FFD700' : '#888';
    ctx.beginPath();
    ctx.arc(elevatorX + elevatorW / 2 - 8, elevatorY + elevatorH / 2, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(elevatorX + elevatorW / 2 + 8, elevatorY + elevatorH / 2, 3, 0, Math.PI * 2);
    ctx.fill();
    
    // Floor indicator panel above doors
    ctx.fillStyle = '#1a1a2a';
    ctx.fillRect(elevatorX + 10, elevatorY - 25, elevatorW - 20, 20);
    ctx.strokeStyle = canAccess ? '#FFD700' : '#555';
    ctx.lineWidth = 2;
    ctx.strokeRect(elevatorX + 10, elevatorY - 25, elevatorW - 20, 20);
    
    // Up arrow in panel (animated glow for accessible)
    if (canAccess) {
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 8;
    }
    ctx.fillStyle = canAccess ? '#00FF00' : '#333';
    ctx.beginPath();
    ctx.moveTo(elevatorX + elevatorW / 2, elevatorY - 20);
    ctx.lineTo(elevatorX + elevatorW / 2 - 8, elevatorY - 10);
    ctx.lineTo(elevatorX + elevatorW / 2 + 8, elevatorY - 10);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
    
    // "ROOFTOP" text above
    ctx.font = 'bold 12px sans-serif';
    ctx.fillStyle = canAccess ? '#FFD700' : '#666';
    ctx.textAlign = 'center';
    ctx.fillText('🏙️ ROOFTOP', elevatorX + elevatorW / 2, elevatorY - 32);
    
    // Glow effect for accessible elevator
    if (canAccess) {
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 15;
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 2;
      ctx.strokeRect(elevatorX - 2, elevatorY - 2, elevatorW + 4, elevatorH + 4);
      ctx.shadowBlur = 0;
      
      // Pulsing indicator light
      const pulseAlpha = 0.5 + Math.sin(Date.now() / 300) * 0.3;
      ctx.fillStyle = `rgba(0, 255, 0, ${pulseAlpha})`;
      ctx.beginPath();
      ctx.arc(elevatorX + elevatorW / 2, elevatorY - 15, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  };
  
  // Draw Return Elevator in Rooftop (goes back to VIP) — matches the VIP elevator style
  const drawReturnElevator = (ctx, W, H, floorY) => {
    const elevatorW = isMobile ? 52 : 72;
    const elevatorH = isMobile ? 60 : 86;
    const elevatorX = isMobile ? 30 : 50;  // Left side
    const elevatorY = floorY + 30;

    // Shaft housing
    const shaftGradient = ctx.createLinearGradient(elevatorX, elevatorY, elevatorX + elevatorW, elevatorY);
    shaftGradient.addColorStop(0, '#101826');
    shaftGradient.addColorStop(0.5, '#1e2a3e');
    shaftGradient.addColorStop(1, '#101826');
    ctx.fillStyle = shaftGradient;
    ctx.fillRect(elevatorX, elevatorY, elevatorW, elevatorH);

    // Frame
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 4;
    ctx.strokeRect(elevatorX, elevatorY, elevatorW, elevatorH);
    ctx.strokeStyle = '#bae6fd';
    ctx.lineWidth = 1;
    ctx.strokeRect(elevatorX + 4, elevatorY + 4, elevatorW - 8, elevatorH - 8);

    // Metallic door panels
    ctx.fillStyle = '#3d556f';
    ctx.fillRect(elevatorX + 6, elevatorY + 6, (elevatorW - 14) / 2, elevatorH - 12);
    ctx.fillRect(elevatorX + 6 + (elevatorW - 14) / 2 + 2, elevatorY + 6, (elevatorW - 14) / 2, elevatorH - 12);

    // Door split + handles
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(elevatorX + elevatorW / 2, elevatorY + 6);
    ctx.lineTo(elevatorX + elevatorW / 2, elevatorY + elevatorH - 6);
    ctx.stroke();
    ctx.fillStyle = '#bae6fd';
    ctx.beginPath(); ctx.arc(elevatorX + elevatorW / 2 - 8, elevatorY + elevatorH / 2, 3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(elevatorX + elevatorW / 2 + 8, elevatorY + elevatorH / 2, 3, 0, Math.PI * 2); ctx.fill();

    // Indicator panel + down arrow
    ctx.fillStyle = '#0c1420';
    ctx.fillRect(elevatorX + 10, elevatorY - 25, elevatorW - 20, 20);
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.strokeRect(elevatorX + 10, elevatorY - 25, elevatorW - 20, 20);
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 8;
    ctx.fillStyle = '#00BFFF';
    ctx.beginPath();
    ctx.moveTo(elevatorX + elevatorW / 2, elevatorY - 8);
    ctx.lineTo(elevatorX + elevatorW / 2 - 8, elevatorY - 18);
    ctx.lineTo(elevatorX + elevatorW / 2 + 8, elevatorY - 18);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    // Label
    ctx.font = 'bold 12px sans-serif';
    ctx.fillStyle = '#38bdf8';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 6;
    ctx.fillText('👑 TO VIP', elevatorX + elevatorW / 2, elevatorY - 32);
    ctx.shadowBlur = 0;

    // Pulsing glow so it's easy to find
    const pulseAlpha = 0.4 + Math.sin(Date.now() / 300) * 0.25;
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 12;
    ctx.strokeStyle = `rgba(56, 189, 248, ${pulseAlpha + 0.35})`;
    ctx.lineWidth = 2;
    ctx.strokeRect(elevatorX - 2, elevatorY - 2, elevatorW + 4, elevatorH + 4);
    ctx.shadowBlur = 0;
  };
  
  // Draw NPC DJ
  const drawNPCDJ = (ctx, x, y, dj, bounce, audio) => {
    const djScale = isMobile ? 0.4 : 0.6;
    const bobOffset = Math.sin(bounce * 0.15) * (3 + audio * 5);
    
    ctx.save();
    ctx.translate(x, y + bobOffset);
    ctx.scale(djScale, djScale);
    
    // Glow effect
    ctx.shadowColor = dj.eyeColor;
    ctx.shadowBlur = 15 + audio * 10;
    
    // DJ Equipment (turntables)
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(-50, 10, 100, 30);
    ctx.fillStyle = '#374151';
    ctx.beginPath();
    ctx.arc(-25, 25, 15, 0, Math.PI * 2);
    ctx.arc(25, 25, 15, 0, Math.PI * 2);
    ctx.fill();
    // Spinning vinyl
    ctx.strokeStyle = '#6B7280';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(-25, 25, 10, bounce * 0.1, bounce * 0.1 + Math.PI * 1.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(25, 25, 10, -bounce * 0.1, -bounce * 0.1 + Math.PI * 1.5);
    ctx.stroke();
    
    // Body
    const topData = OUTFIT_TOPS.find(t => t.id === dj.outfit.top) || OUTFIT_TOPS[0];
    ctx.fillStyle = topData.color;
    if (topData.glow) {
      ctx.shadowColor = topData.glow;
      ctx.shadowBlur = 10;
    }
    ctx.fillRect(-15, -30, 30, 35);
    
    // Arms reaching to turntables
    ctx.fillStyle = dj.skinTone;
    const armAngle = Math.sin(bounce * 0.2) * 0.2;
    ctx.save();
    ctx.translate(-15, -20);
    ctx.rotate(-0.5 + armAngle);
    ctx.fillRect(-5, 0, 10, 30);
    ctx.restore();
    ctx.save();
    ctx.translate(15, -20);
    ctx.rotate(0.5 - armAngle);
    ctx.fillRect(-5, 0, 10, 30);
    ctx.restore();
    
    // Head
    ctx.fillStyle = dj.skinTone;
    ctx.beginPath();
    ctx.ellipse(0, -50, 18, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Face mood
    ctx.fillStyle = dj.eyeColor;
    ctx.shadowColor = dj.eyeColor;
    ctx.shadowBlur = 8;
    if (dj.mood === 'demon') {
      ctx.beginPath();
      ctx.moveTo(-10, -55);
      ctx.lineTo(-5, -50);
      ctx.lineTo(-10, -45);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(10, -55);
      ctx.lineTo(5, -50);
      ctx.lineTo(10, -45);
      ctx.fill();
    } else if (dj.mood === 'led') {
      ctx.fillRect(-10, -55, 6, 3);
      ctx.fillRect(4, -55, 6, 3);
    } else if (dj.mood === 'void') {
      ctx.beginPath();
      ctx.arc(0, -50, 10, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(-7, -52, 4, 0, Math.PI * 2);
      ctx.arc(7, -52, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Headphones
    ctx.fillStyle = '#374151';
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, -55, 20, Math.PI, 0);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(-20, -50, 6, 10, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(20, -50, 6, 10, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // DJ Name
    ctx.shadowBlur = 0;
    ctx.font = 'bold 14px sans-serif';
    ctx.fillStyle = '#FFF';
    ctx.textAlign = 'center';
    ctx.fillText(dj.name, 0, 60);
    ctx.font = '10px sans-serif';
    ctx.fillStyle = dj.eyeColor;
    ctx.fillText(dj.style, 0, 75);
    
    ctx.restore();
  };
  
  // ==================== VISUALIZER ROOM DRAWING ====================
  const drawVisualizerRoom = (ctx, W, H, floorY, phase, audio, settings) => {
    const colors = VISUALIZER_PRESETS.colorSchemes.find(c => c.id === settings.colorScheme)?.colors || ['#FF00FF', '#00FFFF', '#FF0080'];
    const intensity = settings.intensity || 0.7;
    const syncAudio = settings.syncToAudio !== false;
    const audioMod = syncAudio ? audio : 0.5;
    
    // === WALL VISUALIZER ===
    const wallY = floorY - 10;
    const wallH = wallY * 0.8;
    
    switch (settings.wallVisual) {
      case 'bars':
        // Audio spectrum bars on walls
        const numBars = 32;
        const barWidth = W / (numBars * 2.5);
        for (let i = 0; i < numBars; i++) {
          const leftX = 30 + i * barWidth * 1.2;
          const rightX = W - 30 - (numBars - i) * barWidth * 1.2;
          const barHeight = (Math.sin(phase * 0.1 + i * 0.3) + 1) * 0.5 * wallH * 0.6 * (0.3 + audioMod * 0.7) * intensity;
          const colorIndex = Math.floor((i / numBars) * colors.length);
          
          const gradient = ctx.createLinearGradient(leftX, wallY - barHeight, leftX, wallY);
          gradient.addColorStop(0, colors[colorIndex % colors.length]);
          gradient.addColorStop(1, colors[(colorIndex + 1) % colors.length] + '40');
          ctx.fillStyle = gradient;
          ctx.fillRect(leftX, wallY - barHeight, barWidth * 0.8, barHeight);
          ctx.fillRect(rightX, wallY - barHeight, barWidth * 0.8, barHeight);
        }
        break;
        
      case 'wave':
        // Waveform visualization
        ctx.strokeStyle = colors[0];
        ctx.lineWidth = 3;
        ctx.shadowColor = colors[0];
        ctx.shadowBlur = 15;
        ctx.beginPath();
        for (let x = 0; x < W; x += 5) {
          const y = wallY - wallH * 0.3 + Math.sin(x * 0.02 + phase * 0.1) * wallH * 0.25 * (0.3 + audioMod * 0.7) * intensity;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
        break;
        
      case 'circular':
        // Circular spectrum
        const cx = W / 2;
        const cy = wallY - wallH * 0.4;
        const baseRadius = 80;
        const segments = 64;
        for (let i = 0; i < segments; i++) {
          const angle = (i / segments) * Math.PI * 2 - Math.PI / 2;
          const segHeight = (Math.sin(phase * 0.1 + i * 0.2) + 1) * 0.5 * 60 * (0.3 + audioMod * 0.7) * intensity;
          const r1 = baseRadius;
          const r2 = baseRadius + segHeight;
          const colorIdx = Math.floor((i / segments) * colors.length);
          
          ctx.strokeStyle = colors[colorIdx % colors.length];
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(angle) * r1, cy + Math.sin(angle) * r1);
          ctx.lineTo(cx + Math.cos(angle) * r2, cy + Math.sin(angle) * r2);
          ctx.stroke();
        }
        break;
        
      case 'kaleidoscope':
        // Kaleidoscope effect
        const kcx = W / 2;
        const kcy = wallY - wallH * 0.4;
        const sides = 8;
        for (let s = 0; s < sides; s++) {
          ctx.save();
          ctx.translate(kcx, kcy);
          ctx.rotate((s / sides) * Math.PI * 2 + phase * 0.02);
          
          const gradient = ctx.createLinearGradient(0, -100, 50, 100);
          gradient.addColorStop(0, colors[s % colors.length] + '80');
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
          
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(100 * audioMod * intensity, -50);
          ctx.lineTo(120 * audioMod * intensity, 0);
          ctx.lineTo(100 * audioMod * intensity, 50);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        break;
        
      case 'tunnel':
        // Tunnel effect
        const tcx = W / 2;
        const tcy = wallY - wallH * 0.4;
        for (let ring = 0; ring < 8; ring++) {
          const radius = 30 + ring * 25 * intensity + Math.sin(phase * 0.1 - ring * 0.5) * 10 * audioMod;
          ctx.strokeStyle = colors[ring % colors.length] + Math.floor((1 - ring / 8) * 255).toString(16).padStart(2, '0');
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(tcx, tcy, radius, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
        
      case 'particles':
        // Floating particles
        for (let i = 0; i < 50; i++) {
          const px = (Math.sin(phase * 0.01 + i * 1.3) + 1) * 0.5 * W;
          const py = (Math.cos(phase * 0.015 + i * 0.7) + 1) * 0.5 * wallH + 20;
          const size = 3 + Math.sin(phase * 0.1 + i) * 2 * audioMod;
          ctx.fillStyle = colors[i % colors.length];
          ctx.shadowColor = colors[i % colors.length];
          ctx.shadowBlur = 10;
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.shadowBlur = 0;
        break;
        
      case 'matrix':
        // Matrix-style rain
        ctx.font = '14px monospace';
        for (let col = 0; col < W / 20; col++) {
          const x = col * 20 + 10;
          const offset = (phase * 2 + col * 137) % (wallY - 20);
          for (let row = 0; row < 8; row++) {
            const y = offset - row * 20;
            if (y > 20 && y < wallY - 20) {
              const alpha = Math.max(0, 1 - row * 0.15);
              ctx.fillStyle = `rgba(57, 255, 20, ${alpha * intensity})`;
              ctx.fillText(String.fromCharCode(0x30A0 + Math.random() * 96), x, y);
            }
          }
        }
        break;
    }
    
    // === FLOOR VISUALIZER ===
    switch (settings.floorVisual) {
      case 'grid':
        // Reactive grid
        const gridSize = 40;
        for (let gx = 0; gx < W; gx += gridSize) {
          for (let gy = floorY; gy < H; gy += gridSize) {
            const dist = Math.sqrt(Math.pow(gx - W/2, 2) + Math.pow(gy - floorY - (H - floorY)/2, 2));
            const pulseOffset = dist * 0.02;
            const brightness = (Math.sin(phase * 0.1 - pulseOffset) + 1) * 0.5 * audioMod * intensity;
            const colorIdx = Math.floor((gx / W) * colors.length);
            ctx.fillStyle = colors[colorIdx % colors.length] + Math.floor(brightness * 100).toString(16).padStart(2, '0');
            ctx.fillRect(gx + 2, gy + 2, gridSize - 4, gridSize - 4);
          }
        }
        break;
        
      case 'ripple':
        // Ripple effect from center
        const rippleCx = W / 2;
        const rippleCy = (floorY + H) / 2;
        for (let ring = 0; ring < 10; ring++) {
          const r = ((phase * 2 + ring * 40) % 300) * intensity;
          const alpha = Math.max(0, 1 - r / 300);
          ctx.strokeStyle = colors[ring % colors.length] + Math.floor(alpha * 200 * audioMod).toString(16).padStart(2, '0');
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.ellipse(rippleCx, rippleCy, r, r * 0.4, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
        
      case 'pulse':
        // Pulse rings from multiple points
        const pulsePoints = [{x: W*0.25, y: (floorY+H)*0.5}, {x: W*0.75, y: (floorY+H)*0.5}, {x: W*0.5, y: floorY + 30}];
        pulsePoints.forEach((p, pi) => {
          for (let ring = 0; ring < 5; ring++) {
            const r = ((phase * 3 + ring * 20 + pi * 50) % 150) * intensity;
            const alpha = Math.max(0, 1 - r / 150) * audioMod;
            ctx.strokeStyle = colors[pi % colors.length] + Math.floor(alpha * 255).toString(16).padStart(2, '0');
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
            ctx.stroke();
          }
        });
        break;
        
      case 'tiles':
        // Dance tiles that light up
        const tileSize = 60;
        for (let tx = 0; tx < W; tx += tileSize) {
          for (let ty = floorY; ty < H; ty += tileSize) {
            const tilePhase = Math.sin(phase * 0.1 + tx * 0.05 + ty * 0.03);
            if (tilePhase > 0.3 * (1 - audioMod)) {
              const colorIdx = Math.floor((tx / tileSize + ty / tileSize) % colors.length);
              ctx.fillStyle = colors[colorIdx] + Math.floor(tilePhase * 150 * intensity).toString(16).padStart(2, '0');
              ctx.fillRect(tx + 2, ty + 2, tileSize - 4, tileSize - 4);
            }
          }
        }
        break;
        
      case 'fire':
        // Fire effect on floor
        for (let fx = 0; fx < W; fx += 20) {
          const flameHeight = 20 + Math.random() * 40 * audioMod * intensity;
          const gradient = ctx.createLinearGradient(fx, floorY + 30, fx, floorY + 30 - flameHeight);
          gradient.addColorStop(0, '#FF4500');
          gradient.addColorStop(0.5, '#FF8C00');
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.moveTo(fx, floorY + 30);
          ctx.quadraticCurveTo(fx + 10 + Math.sin(phase * 0.2 + fx) * 5, floorY + 30 - flameHeight, fx + 20, floorY + 30);
          ctx.fill();
        }
        break;
    }
    
    // === CEILING VISUALIZER ===
    switch (settings.ceilingVisual) {
      case 'stars':
        // Twinkling starfield (already handled by drawStarrySky for rooftop)
        for (let i = 0; i < 80; i++) {
          const sx = (i * 137.5) % W;
          const sy = 20 + (i * 73.7) % (floorY - 40);
          const twinkle = (Math.sin(phase * 0.1 + i) + 1) * 0.5;
          ctx.fillStyle = colors[i % colors.length] + Math.floor(twinkle * 255 * intensity).toString(16).padStart(2, '0');
          ctx.beginPath();
          ctx.arc(sx, sy, 1 + twinkle * 2, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
        
      case 'lasers':
        // Laser beams
        for (let i = 0; i < 12; i++) {
          const startX = W / 2;
          const startY = 20;
          const angle = (i / 12) * Math.PI - Math.PI / 2 + Math.sin(phase * 0.05 + i * 0.5) * 0.3;
          const length = 200 + audioMod * 200;
          const endX = startX + Math.cos(angle) * length;
          const endY = startY + Math.sin(angle) * length;
          
          ctx.strokeStyle = colors[i % colors.length];
          ctx.shadowColor = colors[i % colors.length];
          ctx.shadowBlur = 15;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, Math.min(endY, floorY - 20));
          ctx.stroke();
        }
        ctx.shadowBlur = 0;
        break;
        
      case 'plasma':
        // Plasma effect
        for (let px = 0; px < W; px += 40) {
          for (let py = 20; py < floorY - 20; py += 40) {
            const val = Math.sin(px * 0.02 + phase * 0.1) * Math.cos(py * 0.02 - phase * 0.1) + 
                       Math.sin((px + py) * 0.015 + phase * 0.05);
            const normalized = (val + 2) / 4;
            const colorIdx = Math.floor(normalized * colors.length);
            ctx.fillStyle = colors[colorIdx % colors.length] + Math.floor(normalized * 100 * intensity).toString(16).padStart(2, '0');
            ctx.fillRect(px, py, 38, 38);
          }
        }
        break;
        
      case 'nebula':
        // Nebula clouds
        for (let i = 0; i < 15; i++) {
          const nx = (Math.sin(phase * 0.01 + i * 2) + 1) * 0.5 * W;
          const ny = 30 + (Math.cos(phase * 0.008 + i * 1.5) + 1) * 0.5 * (floorY - 60);
          const size = 50 + Math.sin(phase * 0.05 + i) * 20;
          
          const gradient = ctx.createRadialGradient(nx, ny, 0, nx, ny, size);
          gradient.addColorStop(0, colors[i % colors.length] + Math.floor(80 * intensity).toString(16).padStart(2, '0'));
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(nx, ny, size, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
    }
    
    // Room label
    ctx.font = 'bold 16px sans-serif';
    ctx.fillStyle = '#A855F7';
    ctx.textAlign = 'left';
    ctx.shadowColor = '#A855F7';
    ctx.shadowBlur = 10;
    ctx.fillText('🎨 ' + (settings.wallVisual || 'BARS').toUpperCase() + ' MODE', 20, 80);
    ctx.shadowBlur = 0;
  };
  
  const drawSpeakerStack = (ctx, x, y, w, h, audio, phase, accentColor = '#DC2626') => {
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);
    
    const coneRadius = w * 0.35;
    const cones = [
      { cx: x + w/2, cy: y + h * 0.25 },
      { cx: x + w/2, cy: y + h * 0.6 },
      { cx: x + w/2, cy: y + h * 0.85, r: coneRadius * 0.6 },
    ];
    
    cones.forEach((cone, i) => {
      const r = cone.r || coneRadius;
      ctx.beginPath();
      ctx.arc(cone.cx, cone.cy, r, 0, Math.PI * 2);
      ctx.fillStyle = '#0a0a0a';
      ctx.fill();
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Bass pulse
      const pulseScale = 1 + audio * 0.3 + Math.sin(phase * 0.1 + i) * 0.1;
      ctx.beginPath();
      ctx.arc(cone.cx, cone.cy, r * 0.5 * pulseScale, 0, Math.PI * 2);
      ctx.fillStyle = accentColor;
      ctx.globalAlpha = 0.7 + audio * 0.3;
      ctx.fill();
      ctx.globalAlpha = 1;
    });
    
    ctx.fillStyle = accentColor;
    ctx.globalAlpha = 0.5 + audio * 0.5;
    ctx.fillRect(x, y + h, w, 3);
    ctx.globalAlpha = 1;
  };
  
  const drawLEDScreen = (ctx, x, y, w, h, streaming, phase, audio, accentColor = '#9333EA') => {
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(x - 5, y - 5, w + 10, h + 10);
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 3;
    ctx.strokeRect(x - 5, y - 5, w + 10, h + 10);
    
    if (streaming) {
      // Active stream - show "LIVE" indicator with visual effects
      // The actual video will be displayed via CSS overlay
      const gradient = ctx.createRadialGradient(x + w/2, y + h/2, 0, x + w/2, y + h/2, w/2);
      gradient.addColorStop(0, `rgba(220, 38, 38, ${0.3 + audio * 0.2})`);
      gradient.addColorStop(0.5, 'rgba(124, 58, 237, 0.2)');
      gradient.addColorStop(1, '#0a0a0a');
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, w, h);
      
      // "VIDEO PLAYING" text overlay for canvas (the real video is CSS overlaid)
      ctx.font = `bold ${Math.max(14, w * 0.08)}px sans-serif`;
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.globalAlpha = 0.3;
      ctx.fillText('🎬 STREAMING', x + w/2, y + h/2);
      ctx.globalAlpha = 1;
      
      // Pulsing border
      ctx.shadowColor = '#DC2626';
      ctx.shadowBlur = 15 + audio * 25;
      ctx.strokeStyle = '#DC2626';
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, w, h);
      ctx.shadowBlur = 0;
      
    } else {
      // OFFLINE screen
      const gradient = ctx.createLinearGradient(x, y, x + w, y + h);
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(0.5, '#0f0f1a');
      gradient.addColorStop(1, '#1a1a2e');
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, w, h);
      
      // Scanlines
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      for (let i = 0; i < h; i += 4) {
        ctx.fillRect(x, y + i, w, 2);
      }
      
      // OFFLINE text - larger for readability
      const fontSize = Math.max(18, w * 0.12);
      ctx.font = `bold ${fontSize}px monospace`;
      ctx.fillStyle = accentColor;
      ctx.textAlign = 'center';
      ctx.fillText('OFFLINE', x + w/2, y + h/2 - 5);
      
      ctx.font = `${fontSize * 0.5}px monospace`;
      ctx.fillStyle = `${accentColor}80`;
      ctx.fillText('NO SIGNAL', x + w/2, y + h/2 + 15);
      
      // Static noise (deterministic)
      for (let i = 0; i < 30; i++) {
        const seed = i * 17.3 + phase * 0.1;
        ctx.fillStyle = `rgba(255, 255, 255, ${(Math.sin(seed) * 0.5 + 0.5) * 0.1})`;
        ctx.fillRect(x + (Math.sin(i * 7.1 + phase * 0.05) * 0.5 + 0.5) * w, y + (Math.cos(i * 11.3 + phase * 0.05) * 0.5 + 0.5) * h, 2, 2);
      }
      
      // Screen glow
      ctx.shadowColor = accentColor;
      ctx.shadowBlur = 15 + audio * 10;
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);
      ctx.shadowBlur = 0;
    }
  };
  
  // Advanced dynamic lighting effects system with audio reactivity
  const drawLightingEffects = (ctx, W, H, phase, intensity, audio, accentColor = '#9333EA') => {
    ctx.save(); // Isolate all lighting state changes
    // Helper to convert color and alpha to rgba format
    // Handles both hex colors (#9333EA) and rgb() format
    const colorWithAlpha = (color, alpha) => {
      const a = Math.min(1, Math.max(0, alpha));
      if (color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', `, ${a})`);
      } else if (color.startsWith('#')) {
        const hex = color.slice(1);
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      return `rgba(147, 51, 234, ${a})`;
    };
    
    // Get lighting FX settings
    const fxMode = lightingFx.fx_mode || 'chill';
    const primaryColor = lightingFx.primary_color || accentColor;
    const secondaryColor = lightingFx.secondary_color || '#3B82F6';
    const speed = lightingFx.speed || 0.5;
    const fxIntensity = lightingFx.intensity || 0.4;
    const bassReact = lightingFx.bass_reactivity || 0.5;
    const trebleReact = lightingFx.treble_reactivity || 0.3;
    
    // Audio data for reactive effects (smoothed)
    const bassLevel = Math.min(audioData.bass * bassReact, 0.8);
    const trebleLevel = Math.min(audioData.treble * trebleReact, 0.6);
    const midsLevel = audioData.mids || 0.3;
    const overallLevel = Math.min(audioData.overall, 0.7);
    
    // Adjusted phase for speed control (slower by default)
    const adjustedPhase = phase * speed;
    const time = adjustedPhase * 0.01; // Slower time progression
    
    // Calculate effect-specific modifiers based on mode
    let effectModifier = 0;
    
    switch (fxMode) {
      case 'chill':
        // Very subtle, slow breathing effect
        effectModifier = Math.sin(time * 0.5) * 0.15 + 0.1;
        break;
      case 'auto':
        // Audio reactive but smoother, no color shifting
        effectModifier = bassLevel * 0.3 + 0.1;
        break;
      case 'sweep':
        effectModifier = Math.sin(adjustedPhase * 0.02) * 0.2 + 0.1;
        break;
      case 'wave':
        effectModifier = Math.sin(adjustedPhase * 0.03) * 0.15 + 0.1;
        break;
      case 'pulse':
        // Softer pulse
        effectModifier = Math.abs(Math.sin(adjustedPhase * 0.05)) * 0.25 + 0.1;
        break;
      case 'spotlight':
        effectModifier = 0.2;
        break;
      default:
        effectModifier = 0.15;
    }
    
    // Scale effect by intensity setting
    effectModifier *= fxIntensity;
    
    // HSL to RGB helper
    const hslToRgb = (h, s, l) => {
      h = h / 360;
      const a = s * Math.min(l, 1 - l);
      const f = (n) => {
        const k = (n + h * 12) % 12;
        return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      };
      return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
    };
    
    // Use fixed colors (no rainbow shifting)
    const dynPrimary = primaryColor;
    const dynSecondary = secondaryColor;
    
    const baseIntensity = fxIntensity * intensity * 0.6; // Reduced overall intensity
    
    // === 1. AMBIENT FOG/HAZE EFFECT ===
    const fogIntensity = (0.08 + bassLevel * 0.05) * fxIntensity;
    const fogGradient = ctx.createLinearGradient(0, H * 0.3, 0, H);
    fogGradient.addColorStop(0, 'transparent');
    fogGradient.addColorStop(0.5, colorWithAlpha(dynPrimary, fogIntensity * 0.2));
    fogGradient.addColorStop(1, colorWithAlpha(dynSecondary, fogIntensity * 0.25));
    ctx.fillStyle = fogGradient;
    ctx.fillRect(0, 0, W, H);
    
    // === 2. DISCO BALL EFFECT (subtle) ===
    const discoBallX = W * 0.5;
    const discoBallY = 40;
    const discoBallSize = 20;
    
    // Disco ball body
    ctx.fillStyle = 'rgba(180, 180, 180, 0.8)';
    ctx.beginPath();
    ctx.arc(discoBallX, discoBallY, discoBallSize, 0, Math.PI * 2);
    ctx.fill();
    
    // Disco ball facets (fewer, subtler) - deterministic pattern
    ctx.fillStyle = 'rgba(220, 220, 220, 0.6)';
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 6; col++) {
        const angle = (col / 6) * Math.PI * 2 + time * 0.3;
        const rowOffset = (row - 1.5) * 7;
        const fx = discoBallX + Math.cos(angle) * (discoBallSize - 5) * Math.cos(rowOffset * 0.1);
        const fy = discoBallY + rowOffset + Math.sin(angle) * 2;
        // Use deterministic sparkle based on position and time instead of Math.random()
        const sparkle = Math.sin(col * 3.7 + row * 5.3 + time * 2) > -0.2;
        if (sparkle) {
          ctx.fillRect(fx - 1.5, fy - 1.5, 3, 3);
        }
      }
    }
    
    // Disco ball reflection beams (fewer, more subtle)
    const numReflections = fxMode === 'chill' ? 4 : 8;
    for (let i = 0; i < numReflections; i++) {
      const reflectAngle = (i / numReflections) * Math.PI * 2 + time * 0.5;
      const reflectLength = H * 0.6;
      const reflectX = discoBallX + Math.cos(reflectAngle) * 15;
      const reflectEndX = reflectX + Math.cos(reflectAngle + 0.2) * reflectLength;
      const reflectEndY = discoBallY + Math.sin(reflectAngle * 0.5 + 1) * reflectLength * 0.5 + reflectLength * 0.5;
      
      const reflectIntensity = (0.05 + bassLevel * 0.08) * fxIntensity;
      ctx.strokeStyle = colorWithAlpha(i % 2 === 0 ? dynPrimary : dynSecondary, reflectIntensity);
      ctx.lineWidth = 1 + bassLevel * 1.5;
      ctx.beginPath();
      ctx.moveTo(discoBallX, discoBallY);
      ctx.lineTo(reflectEndX, reflectEndY);
      ctx.stroke();
    }
    
    // === 3. MOVING HEAD SPOTLIGHTS (subtle) ===
    const numSpots = fxMode === 'chill' ? 2 : 4;
    for (let i = 0; i < numSpots; i++) {
      const spotPhase = time * 0.7 + i * (Math.PI / 2);
      const spotX = W * (0.2 + i * 0.3) + Math.sin(spotPhase) * W * 0.05;
      const spotY = 20;
      const targetX = W * 0.5 + Math.cos(spotPhase * 0.8) * W * 0.25;
      const targetY = H * 0.65 + Math.sin(spotPhase * 0.5) * H * 0.1;
      
      const spotColor = i % 2 === 0 ? dynPrimary : dynSecondary;
      const spotIntensity = baseIntensity * 0.25 + (fxMode === 'auto' ? bassLevel * 0.15 : effectModifier * 0.15);
      
      // Beam cone
      ctx.save();
      ctx.globalAlpha = spotIntensity;
      ctx.fillStyle = spotColor;
      ctx.beginPath();
      ctx.moveTo(spotX, spotY);
      ctx.lineTo(targetX - 30, targetY);
      ctx.lineTo(targetX + 30, targetY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      
      // Spot hit on floor
      const floorGlow = ctx.createRadialGradient(targetX, targetY, 0, targetX, targetY, 45);
      floorGlow.addColorStop(0, colorWithAlpha(spotColor, spotIntensity * 0.5));
      floorGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = floorGlow;
      ctx.beginPath();
      ctx.ellipse(targetX, targetY, 45, 20, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // === 4. LASER GRID (subtle, skip in chill mode) ===
    if (fxMode !== 'chill') {
      ctx.save();
      const laserIntensity = (0.08 + bassLevel * 0.1) * fxIntensity;
      ctx.strokeStyle = colorWithAlpha(dynPrimary, laserIntensity);
      ctx.lineWidth = 1;
      
      // Horizontal laser sweeps (fewer)
      for (let i = 0; i < 3; i++) {
        const laserY = H * 0.25 + i * H * 0.15 + Math.sin(time + i) * 10;
        ctx.beginPath();
        ctx.moveTo(0, laserY);
        ctx.lineTo(W, laserY + Math.sin(time * 0.5 + i * 0.5) * 15);
        ctx.stroke();
      }
      
      // Crossing laser beams (fewer, slower)
      ctx.strokeStyle = colorWithAlpha(dynSecondary, laserIntensity * 0.6);
      for (let i = 0; i < 4; i++) {
        const startX = (i / 4) * W;
        const angle = Math.sin(time * 0.5 + i * 0.7) * 0.3;
        ctx.beginPath();
        ctx.moveTo(startX, 0);
        ctx.lineTo(startX + Math.tan(angle) * H, H);
        ctx.stroke();
      }
      ctx.restore();
    }
    
    // === 5. NEON TUBE LIGHTS (walls - subtle) ===
    const drawNeonTube = (x1, y1, x2, y2, color, glowSize) => {
      const tubeGlow = (0.15 + midsLevel * 0.2) * fxIntensity;
      ctx.strokeStyle = colorWithAlpha(color, tubeGlow);
      ctx.lineWidth = glowSize * 0.7;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      
      // Inner bright core
      ctx.strokeStyle = colorWithAlpha('#ffffff', tubeGlow * 0.4);
      ctx.lineWidth = glowSize * 0.2;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    };
    
    // Left wall neon
    drawNeonTube(10, H * 0.15, 10, H * 0.85, dynPrimary, 6);
    drawNeonTube(22, H * 0.2, 22, H * 0.8, dynSecondary, 4);
    
    // Right wall neon
    drawNeonTube(W - 10, H * 0.15, W - 10, H * 0.85, dynPrimary, 6);
    drawNeonTube(W - 22, H * 0.2, W - 22, H * 0.8, dynSecondary, 4);
    
    // Top frame neon
    drawNeonTube(30, 10, W - 30, 10, dynPrimary, 4);
    
    // === 6. LED PANELS / PIXEL WALL (behind DJ - using fixed colors, no rainbow) ===
    const panelX = W * 0.35;
    const panelY = H * 0.1;
    const panelW = W * 0.3;
    const panelH = H * 0.2;
    const pixelSize = 10;
    const cols = Math.floor(panelW / pixelSize);
    const rows = Math.floor(panelH / pixelSize);
    
    ctx.save();
    ctx.globalAlpha = (0.3 + bassLevel * 0.15) * fxIntensity;
    
    // Parse primary color for LED panel
    let pr = 147, pg = 51, pb = 234;
    if (dynPrimary.startsWith('#')) {
      const hex = dynPrimary.slice(1);
      pr = parseInt(hex.substring(0, 2), 16);
      pg = parseInt(hex.substring(2, 4), 16);
      pb = parseInt(hex.substring(4, 6), 16);
    }
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // Create wave pattern using fixed color
        const waveVal = Math.sin(time + col * 0.3 + row * 0.2) * 0.5 + 0.5;
        const brightness = 0.3 + waveVal * 0.4;
        
        ctx.fillStyle = `rgba(${Math.floor(pr * brightness)}, ${Math.floor(pg * brightness)}, ${Math.floor(pb * brightness)}, ${0.4 + waveVal * 0.3})`;
        ctx.fillRect(
          panelX + col * pixelSize,
          panelY + row * pixelSize,
          pixelSize - 2,
          pixelSize - 2
        );
      }
    }
    ctx.restore();
    
    // === 7. FLOOR TILES (reactive dance floor - subtle, fixed colors) ===
    const floorY = H * 0.78;
    const tileSize = 45;
    const tilesX = Math.ceil(W / tileSize);
    const tilesY = 3;
    
    ctx.save();
    for (let ty = 0; ty < tilesY; ty++) {
      for (let tx = 0; tx < tilesX; tx++) {
        const tilePhase = Math.sin(time + tx * 0.4 + ty * 0.3);
        const tileBrightness = (0.05 + (tilePhase * 0.5 + 0.5) * 0.15 + bassLevel * 0.1) * fxIntensity;
        
        // Use primary/secondary colors alternating
        const useSecondary = (tx + ty) % 2 === 0;
        const tileColor = useSecondary ? dynSecondary : dynPrimary;
        
        ctx.fillStyle = colorWithAlpha(tileColor, tileBrightness);
        ctx.fillRect(
          tx * tileSize + 3,
          floorY + ty * tileSize + 3,
          tileSize - 6,
          tileSize - 6
        );
      }
    }
    ctx.restore();
    
    // === 8. PARTICLE SPARKLES (fewer, subtler) ===
    if (fxMode !== 'chill') {
      ctx.save();
      const numParticles = 15;
      for (let i = 0; i < numParticles; i++) {
        const px = (Math.sin(time * 0.3 + i * 2.1) * 0.5 + 0.5) * W;
        const py = (Math.cos(time * 0.2 + i * 1.7) * 0.5 + 0.5) * H * 0.6;
        const pSize = 1.5 + Math.sin(time * 2 + i) * 0.8;
        const pAlpha = (0.15 + Math.sin(time * 2 + i * 0.5) * 0.1 + bassLevel * 0.1) * fxIntensity;
        
        ctx.fillStyle = colorWithAlpha(i % 2 === 0 ? '#ffffff' : dynPrimary, pAlpha);
        ctx.beginPath();
        ctx.arc(px, py, pSize, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    
    // === 9. SUBTLE AMBIENT GLOW (replaces bass drop flash) ===
    if (fxMode === 'auto' && bassLevel > 0.5) {
      // Very subtle glow instead of harsh flash
      const glowIntensity = (bassLevel - 0.5) * 0.1 * fxIntensity;
      const glowGrad = ctx.createRadialGradient(W/2, H * 0.6, 0, W/2, H * 0.6, W * 0.5);
      glowGrad.addColorStop(0, colorWithAlpha(dynPrimary, glowIntensity));
      glowGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = glowGrad;
      ctx.fillRect(0, 0, W, H);
    }
    
    // === 10. VIGNETTE EFFECT ===
    const vignette = ctx.createRadialGradient(W/2, H/2, H * 0.35, W/2, H/2, H);
    vignette.addColorStop(0, 'transparent');
    vignette.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, W, H);
    
    // === 11. AUDIO VISUALIZER BARS (bottom - only in auto mode, more subtle) ===
    if (fxMode === 'auto' && overallLevel > 0.1) {
      const barCount = 16;
      const barWidth = W / (barCount * 2);
      const maxBarHeight = H * 0.08;
      
      ctx.save();
      for (let i = 0; i < barCount; i++) {
        const freqIndex = i / barCount;
        let barLevel;
        if (freqIndex < 0.33) {
          barLevel = bassLevel * (1 - freqIndex * 2);
        } else if (freqIndex < 0.66) {
          barLevel = midsLevel;
        } else {
          barLevel = trebleLevel * (freqIndex - 0.33) * 2;
        }
        
        const barHeight = barLevel * maxBarHeight * fxIntensity;
        const x = W * 0.1 + i * (W * 0.8 / barCount);
        const y = H - barHeight - 3;
        
        ctx.fillStyle = colorWithAlpha(dynPrimary, 0.3 + barLevel * 0.2);
        ctx.fillRect(x, y, barWidth * 0.6, barHeight);
      }
      ctx.restore();
    }
    ctx.restore(); // Restore state saved at start of drawLightingEffects
  };
  
  // Draw weather effects (rain, sunset, etc.) on rooftop
  const drawWeatherEffects = (ctx, W, H, phase) => {
    if (currentRoom !== 'rooftop') return;
    
    ctx.save();
    const time = phase * 0.01;
    
    // Time of day overlay
    if (timeOfDay === 'sunset') {
      const sunsetGrad = ctx.createLinearGradient(0, 0, 0, H * 0.5);
      sunsetGrad.addColorStop(0, 'rgba(255, 100, 50, 0.15)');
      sunsetGrad.addColorStop(0.5, 'rgba(255, 150, 100, 0.1)');
      sunsetGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = sunsetGrad;
      ctx.fillRect(0, 0, W, H);
    }
    
    // Rain effect
    if (weatherEffect === 'rain') {
      ctx.strokeStyle = 'rgba(150, 180, 255, 0.4)';
      ctx.lineWidth = 1;
      
      for (let i = 0; i < 100; i++) {
        const x = ((i * 37 + time * 200) % W);
        const y = ((i * 23 + time * 500) % H);
        const length = 15 + Math.sin(i * 7.3) * 5 + 5;
        
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 3, y + length);
        ctx.stroke();
      }
      
      // Rain puddle reflections on floor
      ctx.fillStyle = 'rgba(100, 150, 200, 0.1)';
      for (let i = 0; i < 5; i++) {
        const px = (i * W / 5) + Math.sin(time + i) * 20;
        const py = H * 0.9;
        ctx.beginPath();
        ctx.ellipse(px, py, 40 + Math.sin(time * 2 + i) * 5, 8, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    ctx.restore();
  };
  
  // Draw drink effect aura around player - SPECTACULAR EFFECTS
  const drawDrinkEffect = (ctx, x, y, effect, effectColor, phase) => {
    if (!effect) return;
    
    ctx.save();
    const time = phase * 0.03;
    
    // Parse effect color
    let r = 0, g = 255, b = 136;
    if (effectColor?.startsWith('#')) {
      const hex = effectColor.slice(1);
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    
    if (effect === 'glow') {
      // Multi-layered pulsing glow with outer ring
      for (let layer = 0; layer < 4; layer++) {
        const layerTime = time + layer * 0.5;
        const radius = 35 + layer * 15 + Math.sin(layerTime * 2) * 8;
        const alpha = (0.4 - layer * 0.08) * (0.8 + Math.sin(layerTime * 3) * 0.2);
        
        const glowGrad = ctx.createRadialGradient(x, y - 20, 0, x, y - 20, radius);
        glowGrad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha})`);
        glowGrad.addColorStop(0.6, `rgba(${r}, ${g}, ${b}, ${alpha * 0.4})`);
        glowGrad.addColorStop(1, 'transparent');
        
        ctx.fillStyle = glowGrad;
        ctx.beginPath();
        ctx.arc(x, y - 20, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Floating energy particles
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2 + time * 0.8;
        const dist = 25 + Math.sin(time * 2 + i * 0.7) * 15;
        const px = x + Math.cos(angle) * dist;
        const py = y - 30 + Math.sin(angle) * dist * 0.4 + Math.sin(time * 3 + i) * 8;
        const size = 2 + Math.sin(time * 4 + i) * 1.5;
        
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.6 + Math.sin(time * 3 + i) * 0.3})`;
        ctx.beginPath();
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fill();
        
        // Particle trail
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.2)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px - Math.cos(angle) * 8, py - Math.sin(angle) * 4);
        ctx.stroke();
      }
      
    } else if (effect === 'sparkle') {
      // Ice crystal sparkles with trails
      for (let i = 0; i < 20; i++) {
        const angle = (i / 20) * Math.PI * 2 + time * 1.2;
        const dist = 20 + Math.sin(time * 1.5 + i * 0.5) * 25;
        const sx = x + Math.cos(angle) * dist;
        const sy = y - 25 + Math.sin(angle) * dist * 0.5 + Math.cos(time * 2 + i) * 10;
        const size = 1.5 + Math.sin(time * 5 + i * 0.8) * 1.5;
        const alpha = 0.5 + Math.sin(time * 4 + i) * 0.4;
        
        // Star shape for sparkle
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate(time * 2 + i);
        
        // 4-point star
        ctx.beginPath();
        for (let p = 0; p < 4; p++) {
          const starAngle = (p / 4) * Math.PI * 2;
          const starX = Math.cos(starAngle) * size * 2;
          const starY = Math.sin(starAngle) * size * 2;
          if (p === 0) ctx.moveTo(starX, starY);
          else ctx.lineTo(starX, starY);
          
          const innerAngle = starAngle + Math.PI / 4;
          ctx.lineTo(Math.cos(innerAngle) * size * 0.5, Math.sin(innerAngle) * size * 0.5);
        }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        
        // Sparkle glow
        const sparkleGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, size * 3);
        sparkleGrad.addColorStop(0, `rgba(255, 255, 255, ${alpha * 0.5})`);
        sparkleGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = sparkleGrad;
        ctx.beginPath();
        ctx.arc(sx, sy, size * 3, 0, Math.PI * 2);
        ctx.fill();
      }
      
    } else if (effect === 'flames') {
      // Realistic fire with multiple flame layers
      for (let layer = 0; layer < 3; layer++) {
        for (let i = 0; i < 10; i++) {
          const baseX = x - 25 + (i * 5.5);
          const flameSpeed = 4 + layer;
          const flameHeight = 30 + Math.sin(time * flameSpeed + i * 0.6 + layer) * 15;
          const flameWidth = 6 + Math.sin(time * 3 + i) * 2;
          const baseY = y - 10 + layer * 3;
          
          // Flame gradient (orange core, yellow tips, red base)
          const flameGrad = ctx.createLinearGradient(baseX, baseY, baseX, baseY - flameHeight);
          if (layer === 0) {
            flameGrad.addColorStop(0, 'rgba(255, 50, 0, 0)');
            flameGrad.addColorStop(0.3, 'rgba(255, 100, 0, 0.8)');
            flameGrad.addColorStop(0.7, 'rgba(255, 200, 0, 0.9)');
            flameGrad.addColorStop(1, 'rgba(255, 255, 100, 0.6)');
          } else {
            flameGrad.addColorStop(0, `rgba(${r}, ${Math.floor(g * 0.5)}, 0, 0)`);
            flameGrad.addColorStop(0.5, `rgba(${r}, ${g}, 0, ${0.4 - layer * 0.1})`);
            flameGrad.addColorStop(1, 'rgba(255, 255, 200, 0.3)');
          }
          
          ctx.fillStyle = flameGrad;
          ctx.beginPath();
          ctx.moveTo(baseX, baseY);
          
          // Wavy flame shape
          const cp1x = baseX - flameWidth * (0.8 + Math.sin(time * 5 + i) * 0.3);
          const cp1y = baseY - flameHeight * 0.4;
          const cp2x = baseX - flameWidth * 0.3;
          const cp2y = baseY - flameHeight * 0.8;
          const tipX = baseX + Math.sin(time * 6 + i * 0.7) * 3;
          const tipY = baseY - flameHeight;
          
          ctx.quadraticCurveTo(cp1x, cp1y, tipX, tipY);
          ctx.quadraticCurveTo(baseX + flameWidth * 0.3, cp2y, baseX, baseY);
          ctx.fill();
        }
      }
      
      // Ember particles rising
      for (let i = 0; i < 8; i++) {
        const emberX = x - 20 + Math.sin(time + i * 2) * 25 + (i * 5);
        const emberY = y - 50 - ((time * 30 + i * 20) % 60);
        const emberSize = 1 + Math.random() * 1.5;
        const emberAlpha = 0.8 - ((time * 30 + i * 20) % 60) / 80;
        
        if (emberAlpha > 0) {
          ctx.fillStyle = `rgba(255, ${150 + Math.random() * 100}, 0, ${emberAlpha})`;
          ctx.beginPath();
          ctx.arc(emberX, emberY, emberSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      
    } else if (effect === 'rainbow') {
      // Spectacular rainbow aurora effect
      const colors = [
        [255, 0, 0], [255, 127, 0], [255, 255, 0], 
        [0, 255, 0], [0, 0, 255], [139, 0, 255]
      ];
      
      // Outer rotating rings
      for (let ring = 0; ring < 3; ring++) {
        const ringRadius = 40 + ring * 12;
        const ringAlpha = 0.4 - ring * 0.1;
        
        ctx.lineWidth = 4 - ring;
        for (let i = 0; i < colors.length; i++) {
          const startAngle = (i / colors.length) * Math.PI * 2 + time * (1 + ring * 0.3);
          const endAngle = startAngle + Math.PI / 2.5;
          const [cr, cg, cb] = colors[i];
          
          ctx.strokeStyle = `rgba(${cr}, ${cg}, ${cb}, ${ringAlpha + Math.sin(time * 2 + i) * 0.15})`;
          ctx.beginPath();
          ctx.arc(x, y - 20, ringRadius + Math.sin(time * 2 + i * 0.5) * 5, startAngle, endAngle);
          ctx.stroke();
        }
      }
      
      // Central rainbow glow
      const centerGrad = ctx.createRadialGradient(x, y - 20, 0, x, y - 20, 35);
      const hue = (time * 50) % 360;
      centerGrad.addColorStop(0, `hsla(${hue}, 100%, 70%, 0.5)`);
      centerGrad.addColorStop(0.5, `hsla(${(hue + 60) % 360}, 100%, 60%, 0.3)`);
      centerGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = centerGrad;
      ctx.beginPath();
      ctx.arc(x, y - 20, 35, 0, Math.PI * 2);
      ctx.fill();
      
      // Rainbow particles
      for (let i = 0; i < 15; i++) {
        const angle = (i / 15) * Math.PI * 2 + time * 1.5;
        const dist = 30 + Math.sin(time * 2 + i) * 15;
        const px = x + Math.cos(angle) * dist;
        const py = y - 25 + Math.sin(angle) * dist * 0.4;
        const [pr, pg, pb] = colors[i % colors.length];
        const pAlpha = 0.7 + Math.sin(time * 3 + i) * 0.3;
        
        ctx.fillStyle = `rgba(${pr}, ${pg}, ${pb}, ${pAlpha})`;
        ctx.beginPath();
        ctx.arc(px, py, 2 + Math.sin(time * 4 + i) * 1, 0, Math.PI * 2);
        ctx.fill();
      }
      
    } else if (effect === 'acid_trip') {
      // Psychedelic swirling aura around player avatar
      // Rotating kaleidoscope rings
      for (let ring = 0; ring < 4; ring++) {
        const ringR = 30 + ring * 18 + Math.sin(time * 2 + ring) * 8;
        const segCount = 6 + ring * 2;
        for (let seg = 0; seg < segCount; seg++) {
          const angle = (seg / segCount) * Math.PI * 2 + time * (1 + ring * 0.3) * (ring % 2 === 0 ? 1 : -1);
          const px = x + Math.cos(angle) * ringR;
          const py = y - 20 + Math.sin(angle) * ringR * 0.5;
          const hue = (time * 60 + seg * 40 + ring * 90) % 360;
          const size = 4 + Math.sin(time * 3 + seg + ring) * 3;
          const alpha = 0.6 + Math.sin(time * 4 + seg * 0.5) * 0.3;
          
          ctx.fillStyle = `hsla(${hue}, 100%, 60%, ${alpha})`;
          ctx.beginPath();
          
          // Morphing shape
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(time * 2 + seg);
          const shapeType = Math.floor((time + seg + ring) % 3);
          if (shapeType === 0) {
            ctx.arc(0, 0, size, 0, Math.PI * 2);
          } else if (shapeType === 1) {
            ctx.moveTo(0, -size);
            ctx.lineTo(size * 0.87, size * 0.5);
            ctx.lineTo(-size * 0.87, size * 0.5);
            ctx.closePath();
          } else {
            ctx.rect(-size / 2, -size / 2, size, size);
          }
          ctx.fill();
          ctx.restore();
        }
      }
      
      // Central pulsing rainbow aura
      const auraGrad = ctx.createRadialGradient(x, y - 20, 0, x, y - 20, 50 + Math.sin(time * 2) * 10);
      const h = (time * 40) % 360;
      auraGrad.addColorStop(0, `hsla(${h}, 100%, 60%, 0.3)`);
      auraGrad.addColorStop(0.5, `hsla(${(h + 120) % 360}, 100%, 60%, 0.15)`);
      auraGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = auraGrad;
      ctx.beginPath();
      ctx.arc(x, y - 20, 50 + Math.sin(time * 2) * 10, 0, Math.PI * 2);
      ctx.fill();
      
    } else if (effect === 'ice') {
      // Orbiting ice shards + frosty ground mist
      for (let i = 0; i < 10; i++) {
        const angle = (i / 10) * Math.PI * 2 + time * 0.9;
        const dist = 24 + Math.sin(time * 2 + i) * 10;
        const ix = x + Math.cos(angle) * dist;
        const iy = y - 28 + Math.sin(angle) * dist * 0.5;
        ctx.save();
        ctx.translate(ix, iy);
        ctx.rotate(angle + time);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.7 + Math.sin(time * 3 + i) * 0.25})`;
        ctx.beginPath();
        ctx.moveTo(0, -6); ctx.lineTo(3, 0); ctx.lineTo(0, 6); ctx.lineTo(-3, 0);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
      const mist = ctx.createRadialGradient(x, y, 5, x, y, 45);
      mist.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.25)`);
      mist.addColorStop(1, 'transparent');
      ctx.fillStyle = mist;
      ctx.beginPath();
      ctx.ellipse(x, y, 45, 14, 0, 0, Math.PI * 2);
      ctx.fill();

    } else if (effect === 'lightning') {
      // Crackling lightning bolts around the player
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.9)`;
      ctx.shadowColor = effectColor;
      ctx.shadowBlur = 12;
      ctx.lineWidth = 2;
      for (let i = 0; i < 4; i++) {
        if ((time * 7 + i * 2.3) % 3 > 1.1) continue;
        const angle = (i / 4) * Math.PI * 2 + Math.sin(time * 9 + i) * 1.2;
        let bx = x + Math.cos(angle) * 14;
        let by = y - 40 + Math.sin(angle) * 10;
        ctx.beginPath();
        ctx.moveTo(bx, by);
        for (let seg = 0; seg < 4; seg++) {
          bx += (Math.sin(time * 13 + i * 3 + seg * 7) * 10);
          by += 12;
          ctx.lineTo(bx, by);
        }
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
      const halo = ctx.createRadialGradient(x, y - 25, 0, x, y - 25, 40);
      halo.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.15 + Math.abs(Math.sin(time * 8)) * 0.15})`);
      halo.addColorStop(1, 'transparent');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(x, y - 25, 40, 0, Math.PI * 2);
      ctx.fill();

    } else if (effect === 'cosmic') {
      // Orbiting stars, tiny galaxy swirls
      for (let i = 0; i < 14; i++) {
        const angle = (i / 14) * Math.PI * 2 + time * (0.5 + (i % 3) * 0.3);
        const dist = 20 + (i % 4) * 9;
        const sx = x + Math.cos(angle) * dist;
        const sy = y - 30 + Math.sin(angle) * dist * 0.5;
        const tw = 0.5 + Math.abs(Math.sin(time * 4 + i)) * 0.5;
        const hueShift = (i * 40 + time * 60) % 360;
        ctx.fillStyle = `hsla(${hueShift}, 90%, 70%, ${tw})`;
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate(time + i);
        ctx.beginPath();
        for (let p = 0; p < 5; p++) {
          const a1 = (p / 5) * Math.PI * 2 - Math.PI / 2;
          const a2 = a1 + Math.PI / 5;
          const R = 3.4 * tw, ir = 1.4 * tw;
          if (p === 0) ctx.moveTo(Math.cos(a1) * R, Math.sin(a1) * R);
          else ctx.lineTo(Math.cos(a1) * R, Math.sin(a1) * R);
          ctx.lineTo(Math.cos(a2) * ir, Math.sin(a2) * ir);
        }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      const neb = ctx.createRadialGradient(x, y - 28, 0, x, y - 28, 48);
      neb.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.18)`);
      neb.addColorStop(0.6, 'rgba(80, 40, 160, 0.1)');
      neb.addColorStop(1, 'transparent');
      ctx.fillStyle = neb;
      ctx.beginPath();
      ctx.arc(x, y - 28, 48, 0, Math.PI * 2);
      ctx.fill();

    } else if (effect === 'gold') {
      // Golden coin shower with sparkle glints
      for (let i = 0; i < 12; i++) {
        const fall = ((time * 40 + i * 31) % 70);
        const cx = x - 28 + (i * 61) % 56;
        const cy = y - 65 + fall;
        const alpha = 1 - fall / 70;
        const spin = Math.abs(Math.cos(time * 5 + i));
        ctx.fillStyle = `rgba(255, 215, 0, ${alpha})`;
        ctx.beginPath();
        ctx.ellipse(cx, cy, 4 * spin + 0.5, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = `rgba(180, 140, 0, ${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      for (let i = 0; i < 5; i++) {
        const gx = x - 25 + ((i * 43 + time * 25) % 50);
        const gy = y - 20 - ((i * 17) % 35);
        const ga = Math.abs(Math.sin(time * 6 + i * 2));
        ctx.fillStyle = `rgba(255, 255, 200, ${ga})`;
        ctx.fillRect(gx - 1, gy - 4, 2, 8);
        ctx.fillRect(gx - 4, gy - 1, 8, 2);
      }

    } else if (effect === 'shadow') {
      // Billowing dark smoke wisps
      for (let i = 0; i < 8; i++) {
        const riseT = (time * 22 + i * 27) % 55;
        const sx = x - 18 + (i * 37) % 36 + Math.sin(time * 2 + i) * 8;
        const sy = y - 5 - riseT;
        const size = 7 + riseT * 0.28;
        const alpha = 0.4 * (1 - riseT / 55);
        const smoke = ctx.createRadialGradient(sx, sy, 0, sx, sy, size);
        smoke.addColorStop(0, `rgba(30, 25, 55, ${alpha})`);
        smoke.addColorStop(0.7, `rgba(15, 12, 32, ${alpha * 0.6})`);
        smoke.addColorStop(1, 'transparent');
        ctx.fillStyle = smoke;
        ctx.beginPath();
        ctx.arc(sx, sy, size, 0, Math.PI * 2);
        ctx.fill();
      }
      // Occasional purple glint eyes in the smoke
      if (Math.sin(time * 3) > 0.4) {
        ctx.fillStyle = 'rgba(147, 51, 234, 0.8)';
        ctx.beginPath();
        ctx.arc(x - 5, y - 48, 1.6, 0, Math.PI * 2);
        ctx.arc(x + 5, y - 48, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }

    } else if (effect === 'speed') {
      // Speed lines emanating from player
      for (let i = 0; i < 12; i++) {
        const lineAngle = (i / 12) * Math.PI * 2;
        const lineLength = 30 + Math.sin(time * 4 + i) * 15;
        const startDist = 20;
        
        const sx = x + Math.cos(lineAngle) * startDist;
        const sy = y - 20 + Math.sin(lineAngle) * startDist * 0.5;
        const ex = x + Math.cos(lineAngle) * (startDist + lineLength);
        const ey = y - 20 + Math.sin(lineAngle) * (startDist + lineLength) * 0.5;
        
        const lineGrad = ctx.createLinearGradient(sx, sy, ex, ey);
        lineGrad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.8)`);
        lineGrad.addColorStop(1, 'transparent');
        
        ctx.strokeStyle = lineGrad;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.stroke();
      }
      
      // Electric sparks
      for (let i = 0; i < 6; i++) {
        const sparkAngle = time * 5 + i * 1.2;
        const sparkDist = 25 + Math.sin(time * 3 + i) * 10;
        ctx.fillStyle = `rgba(255, 255, 0, ${0.8 + Math.sin(time * 8 + i) * 0.2})`;
        ctx.beginPath();
        ctx.arc(
          x + Math.cos(sparkAngle) * sparkDist,
          y - 25 + Math.sin(sparkAngle) * sparkDist * 0.4,
          1.5 + Math.random(),
          0, Math.PI * 2
        );
        ctx.fill();
      }
    } else if (effect === 'turtle_transform') {
      // Green nature particles floating around the turtle
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2 + time * 0.5;
        const dist = 30 + Math.sin(time * 1.5 + i) * 10;
        const px = x + Math.cos(angle) * dist;
        const py = y - 10 + Math.sin(angle) * dist * 0.4 - Math.sin(time + i) * 8;
        const leafSize = 4 + Math.sin(time * 2 + i) * 2;
        const alpha = 0.5 + Math.sin(time * 3 + i * 0.7) * 0.3;
        ctx.fillStyle = `rgba(82, 183, 136, ${alpha})`;
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(time + i * 0.8);
        ctx.beginPath();
        ctx.ellipse(0, 0, leafSize, leafSize * 0.4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      for (let i = 0; i < 5; i++) {
        const sparkAngle = time * 2 + i * 1.3;
        const sparkDist = 25 + Math.sin(time * 2.5 + i) * 8;
        ctx.fillStyle = `rgba(45, 106, 79, ${0.6 + Math.sin(time * 4 + i) * 0.3})`;
        ctx.beginPath();
        ctx.arc(
          x + Math.cos(sparkAngle) * sparkDist,
          y - 15 + Math.sin(sparkAngle) * sparkDist * 0.3,
          1.5, 0, Math.PI * 2
        );
        ctx.fill();
      }
    }
    
    ctx.restore();
  };
  
  // Draw floor fog effect
  const drawFog = (ctx, W, H, phase, audioLevel, accentColor = '#9333EA') => {
    ctx.save();
    
    const time = phase * 0.015;
    const fogY = H * 0.85; // Fog starts from near the bottom
    const fogHeight = H * 0.2;
    
    // Parse accent color for fog tinting
    let r = 147, g = 51, b = 234;
    if (accentColor.startsWith('#')) {
      const hex = accentColor.slice(1);
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    
    // Create multiple fog layers for depth
    for (let layer = 0; layer < 3; layer++) {
      const layerOffset = layer * 0.2;
      const layerSpeed = 1 + layer * 0.3;
      const layerAlpha = (0.15 - layer * 0.04) * (0.7 + audioLevel * 0.3);
      
      // Create fog gradient
      const fogGradient = ctx.createLinearGradient(0, fogY, 0, H);
      fogGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
      fogGradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${layerAlpha * 0.5})`);
      fogGradient.addColorStop(0.7, `rgba(255, 255, 255, ${layerAlpha})`);
      fogGradient.addColorStop(1, `rgba(200, 200, 220, ${layerAlpha * 0.8})`);
      
      ctx.fillStyle = fogGradient;
      
      // Draw wavy fog using path
      ctx.beginPath();
      ctx.moveTo(0, H);
      
      // Create wave pattern
      const waveSegments = 20;
      for (let i = 0; i <= waveSegments; i++) {
        const x = (i / waveSegments) * W;
        const waveOffset = Math.sin(time * layerSpeed + i * 0.5 + layerOffset) * 15;
        const audioWave = Math.sin(time * 2 + i * 0.3) * audioLevel * 10;
        const y = fogY + waveOffset + audioWave;
        
        if (i === 0) {
          ctx.lineTo(x, y);
        } else {
          const prevX = ((i - 1) / waveSegments) * W;
          const cpX = (prevX + x) / 2;
          ctx.quadraticCurveTo(cpX, y - 5, x, y);
        }
      }
      
      ctx.lineTo(W, H);
      ctx.closePath();
      ctx.fill();
    }
    
    // Add some fog wisps/particles
    const numWisps = 8;
    for (let i = 0; i < numWisps; i++) {
      const wispX = (Math.sin(time * 0.3 + i * 1.5) * 0.5 + 0.5) * W;
      const wispY = fogY + Math.sin(time * 0.5 + i) * 20 + 30;
      const wispSize = 30 + Math.sin(time + i * 2) * 15 + audioLevel * 20;
      const wispAlpha = 0.1 + Math.sin(time * 0.8 + i) * 0.05;
      
      const wispGradient = ctx.createRadialGradient(wispX, wispY, 0, wispX, wispY, wispSize);
      wispGradient.addColorStop(0, `rgba(255, 255, 255, ${wispAlpha})`);
      wispGradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${wispAlpha * 0.5})`);
      wispGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = wispGradient;
      ctx.beginPath();
      ctx.arc(wispX, wispY, wispSize, 0, Math.PI * 2);
      ctx.fill();
    }
    
    ctx.restore();
  };
  
  // Helper function to adjust color brightness for gradients
  const adjustColorBrightness = (hex, percent) => {
    if (!hex || hex === 'transparent') return hex;
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + percent));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  };
  
  // Draw avatar with walking animation and emotes
  const drawAvatar = useCallback((ctx, x, y, avatarData, isCurrentPlayer = false, playerRole = 'user', playerName = '', isPlayerWalking = false, chatMsg = null, playerEmote = null, isSpeaking = false, voiceVolume = 1, playerTransform = null, playerDirection = 'down') => {
    drawPlayerAvatar(ctx, x, y, {
      avatarData,
      isCurrentPlayer,
      playerRole,
      playerName,
      isPlayerWalking,
      chatMsg,
      playerEmote,
      isSpeaking,
      voiceVolume,
      playerTransform,
      playerDirection,
    }, {
      isMobile,
      isWalking,
      isTurtle,
      currentEmote,
      emoteEndTime,
      walkFrameRef,
      drawTurtle,
      drawChatBubble,
    });
    // eslint-disable-next-line
  }, [isMobile, isWalking, isTurtle, currentEmote, emoteEndTime]);
  
  const drawFace = (ctx, mood, eyeColor, eyeGlow, phase) => {
    const glowIntensity = eyeGlow / 100;
    
    switch (mood) {
      case 'demon':
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 10 * glowIntensity;
        ctx.beginPath();
        ctx.moveTo(-12, -40);
        ctx.lineTo(-6, -35);
        ctx.lineTo(-12, -30);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(12, -40);
        ctx.lineTo(6, -35);
        ctx.lineTo(12, -30);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-15, -25);
        ctx.quadraticCurveTo(0, -15, 15, -25);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.stroke();
        break;
        
      case 'skull':
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.ellipse(-8, -38, 6, 8, 0, 0, Math.PI * 2);
        ctx.ellipse(8, -38, 6, 8, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(0, -32);
        ctx.lineTo(-3, -25);
        ctx.lineTo(3, -25);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#FFF';
        ctx.fillRect(-10, -22, 20, 8);
        ctx.strokeStyle = '#000';
        for (let i = -8; i < 10; i += 4) {
          ctx.beginPath();
          ctx.moveTo(i, -22);
          ctx.lineTo(i, -14);
          ctx.stroke();
        }
        break;
        
      case 'led':
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 15 * glowIntensity;
        ctx.fillRect(-12, -42, 8, 4);
        ctx.fillRect(4, -42, 8, 4);
        ctx.fillRect(-10, -25, 20, 4);
        if (Math.sin(phase * 0.2) > 0) {
          ctx.fillRect(-6, -25, 4, 4);
          ctx.fillRect(2, -25, 4, 4);
        }
        break;
        
      case 'tripping':
        ctx.fillStyle = '#FFF';
        ctx.beginPath();
        ctx.ellipse(-8, -38, 10, 12, 0, 0, Math.PI * 2);
        ctx.ellipse(8, -38, 10, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 10 * glowIntensity;
        ctx.beginPath();
        ctx.arc(-8, -38, 5, 0, Math.PI * 2);
        ctx.arc(8, -38, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(0, -22, 3, 0, Math.PI);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.stroke();
        break;
        
      case 'void':
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.arc(0, -35, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 20 * glowIntensity;
        ctx.beginPath();
        ctx.arc(0, -35, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.arc(0, -35, 4, 0, Math.PI * 2);
        ctx.fill();
        break;
        
      case 'sad':
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 5 * glowIntensity;
        ctx.beginPath();
        ctx.arc(-8, -38, 4, 0, Math.PI * 2);
        ctx.arc(8, -38, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-14, -48);
        ctx.lineTo(-4, -45);
        ctx.moveTo(14, -48);
        ctx.lineTo(4, -45);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, -18, 8, Math.PI * 0.2, Math.PI * 0.8);
        ctx.stroke();
        break;
        
      case 'drowsy':
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 5 * glowIntensity;
        ctx.beginPath();
        ctx.ellipse(-8, -38, 5, 2, 0, 0, Math.PI * 2);
        ctx.ellipse(8, -38, 5, 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(0, -25, 5, 0.1, Math.PI - 0.1);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.stroke();
        break;
        
      default:
        ctx.fillStyle = eyeColor;
        ctx.shadowColor = eyeColor;
        ctx.shadowBlur = 8 * glowIntensity;
        ctx.beginPath();
        ctx.arc(-8, -38, 4, 0, Math.PI * 2);
        ctx.arc(8, -38, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-6, -22);
        ctx.lineTo(6, -22);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.stroke();
    }
    ctx.shadowBlur = 0;
  };
  
  const drawFaceAccessory = (ctx, accessoryData, phase) => {
    if (accessoryData.glow) {
      ctx.shadowColor = accessoryData.glow;
      ctx.shadowBlur = 10;
    }
    
    switch (accessoryData.type) {
      case 'glasses':
        ctx.strokeStyle = accessoryData.color;
        ctx.lineWidth = 2;
        switch (accessoryData.id) {
          case 'cyber_glasses':
            // Cyber visor style
            ctx.fillStyle = accessoryData.color;
            ctx.fillRect(-18, -42, 36, 8);
            ctx.fillStyle = 'rgba(0,0,0,0.5)';
            ctx.fillRect(-16, -41, 32, 6);
            break;
          case 'round_glasses':
            ctx.beginPath();
            ctx.arc(-10, -38, 7, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(10, -38, 7, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(-3, -38);
            ctx.lineTo(3, -38);
            ctx.stroke();
            break;
          case 'shutter_shades':
            ctx.fillStyle = accessoryData.color;
            for (let i = 0; i < 5; i++) {
              ctx.fillRect(-18, -44 + i * 3, 36, 1.5);
            }
            break;
          case 'led_glasses':
            ctx.fillStyle = accessoryData.color;
            ctx.fillRect(-16, -42, 12, 6);
            ctx.fillRect(4, -42, 12, 6);
            break;
          case 'heart_glasses':
            ctx.fillStyle = accessoryData.color;
            ctx.beginPath();
            ctx.moveTo(-10, -35);
            ctx.bezierCurveTo(-10, -42, -18, -42, -18, -35);
            ctx.bezierCurveTo(-18, -30, -10, -28, -10, -28);
            ctx.bezierCurveTo(-10, -28, -2, -30, -2, -35);
            ctx.bezierCurveTo(-2, -42, -10, -42, -10, -35);
            ctx.fill();
            ctx.beginPath();
            ctx.moveTo(10, -35);
            ctx.bezierCurveTo(10, -42, 2, -42, 2, -35);
            ctx.bezierCurveTo(2, -30, 10, -28, 10, -28);
            ctx.bezierCurveTo(10, -28, 18, -30, 18, -35);
            ctx.bezierCurveTo(18, -42, 10, -42, 10, -35);
            ctx.fill();
            break;
          default:
            break;
        }
        break;
        
      case 'mask':
        ctx.fillStyle = accessoryData.color;
        switch (accessoryData.id) {
          case 'gas_mask':
            ctx.beginPath();
            ctx.ellipse(0, -28, 15, 12, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#1a1a1a';
            ctx.beginPath();
            ctx.arc(0, -28, 6, 0, Math.PI * 2);
            ctx.fill();
            break;
          case 'half_mask':
            ctx.beginPath();
            ctx.moveTo(-18, -30);
            ctx.lineTo(-18, -15);
            ctx.quadraticCurveTo(0, -5, 18, -15);
            ctx.lineTo(18, -30);
            ctx.closePath();
            ctx.fill();
            break;
          case 'neon_mask':
          case 'demon_mask':
            ctx.beginPath();
            ctx.moveTo(-20, -45);
            ctx.lineTo(-20, -20);
            ctx.quadraticCurveTo(0, -10, 20, -20);
            ctx.lineTo(20, -45);
            ctx.quadraticCurveTo(0, -50, -20, -45);
            ctx.closePath();
            ctx.fill();
            // Eye holes
            ctx.fillStyle = '#000';
            ctx.beginPath();
            ctx.ellipse(-8, -35, 5, 4, 0, 0, Math.PI * 2);
            ctx.ellipse(8, -35, 5, 4, 0, 0, Math.PI * 2);
            ctx.fill();
            break;
          case 'skull_mask':
            ctx.beginPath();
            ctx.ellipse(0, -30, 18, 22, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#000';
            ctx.beginPath();
            ctx.ellipse(-7, -35, 4, 6, 0, 0, Math.PI * 2);
            ctx.ellipse(7, -35, 4, 6, 0, 0, Math.PI * 2);
            ctx.fill();
            // Nose hole
            ctx.beginPath();
            ctx.moveTo(0, -28);
            ctx.lineTo(-3, -22);
            ctx.lineTo(3, -22);
            ctx.closePath();
            ctx.fill();
            break;
          default:
            break;
        }
        break;
        
      case 'jewelry':
        ctx.fillStyle = accessoryData.color;
        ctx.strokeStyle = accessoryData.color;
        ctx.lineWidth = 2;
        switch (accessoryData.id) {
          case 'nose_ring':
            ctx.beginPath();
            ctx.arc(-3, -25, 3, 0, Math.PI);
            ctx.stroke();
            break;
          case 'septum':
            ctx.beginPath();
            ctx.arc(0, -24, 4, 0.3, Math.PI - 0.3);
            ctx.stroke();
            break;
          case 'lip_ring':
            ctx.beginPath();
            ctx.arc(-4, -18, 2, 0, Math.PI * 2);
            ctx.fill();
            break;
          case 'face_chain':
            ctx.beginPath();
            ctx.moveTo(-20, -35);
            ctx.quadraticCurveTo(0, -25, 20, -35);
            ctx.stroke();
            break;
          case 'led_earrings':
          case 'hoop_earrings':
          case 'spike_earrings':
            ctx.beginPath();
            ctx.arc(-22, -35, 4, 0, Math.PI * 2);
            ctx.arc(22, -35, 4, 0, Math.PI * 2);
            ctx.fill();
            break;
          case 'cyber_implant':
            ctx.fillRect(-22, -40, 6, 3);
            ctx.fillRect(16, -40, 6, 3);
            break;
          case 'third_eye':
            ctx.beginPath();
            ctx.ellipse(0, -55, 5, 3, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#000';
            ctx.beginPath();
            ctx.arc(0, -55, 2, 0, Math.PI * 2);
            ctx.fill();
            break;
          default:
            break;
        }
        break;
        
      default:
        break;
    }
    ctx.shadowBlur = 0;
  };
  
  const drawHat = (ctx, hatData, phase) => {
    if (hatData.glow) {
      ctx.shadowColor = hatData.glow;
      ctx.shadowBlur = 10;
    }
    
    switch (hatData.id) {
      case 'beanie_black':
      case 'beanie_purple':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.ellipse(0, -60, 24, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(-24, -65, 48, 10);
        break;
        
      case 'demon_horns':
      case 'demon_horns_black':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.moveTo(-15, -55);
        ctx.quadraticCurveTo(-25, -85, -10, -70);
        ctx.lineTo(-15, -55);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(15, -55);
        ctx.quadraticCurveTo(25, -85, 10, -70);
        ctx.lineTo(15, -55);
        ctx.fill();
        break;
        
      case 'led_visor':
      case 'led_visor_pink':
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(-22, -45, 44, 12);
        ctx.fillStyle = hatData.color;
        ctx.fillRect(-20, -43, 40, 8);
        break;
        
      case 'spiked_helmet':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.ellipse(0, -58, 25, 15, 0, 0, Math.PI);
        ctx.fill();
        ctx.fillStyle = '#6B7280';
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(i * 8 - 4, -58);
          ctx.lineTo(i * 8, -75);
          ctx.lineTo(i * 8 + 4, -58);
          ctx.fill();
        }
        break;
        
      case 'cat_ears':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.moveTo(-18, -55);
        ctx.lineTo(-12, -75);
        ctx.lineTo(-6, -55);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(6, -55);
        ctx.lineTo(12, -75);
        ctx.lineTo(18, -55);
        ctx.fill();
        break;
        
      case 'bunny_ears':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.ellipse(-10, -80, 6, 25, -0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(10, -80, 6, 25, 0.2, 0, Math.PI * 2);
        ctx.fill();
        break;
        
      case 'halo':
      case 'devil_halo':
        ctx.strokeStyle = hatData.color;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.ellipse(0, -70, 20, 8, 0, 0, Math.PI * 2);
        ctx.stroke();
        break;
        
      case 'crown':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.moveTo(-20, -55);
        ctx.lineTo(-15, -70);
        ctx.lineTo(-8, -58);
        ctx.lineTo(0, -75);
        ctx.lineTo(8, -58);
        ctx.lineTo(15, -70);
        ctx.lineTo(20, -55);
        ctx.closePath();
        ctx.fill();
        break;
        
      case 'headphones':
        ctx.fillStyle = hatData.color;
        ctx.beginPath();
        ctx.arc(0, -55, 25, Math.PI, 0);
        ctx.lineWidth = 4;
        ctx.strokeStyle = hatData.color;
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(-25, -40, 8, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(25, -40, 8, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
        
      case 'mohawk':
        ctx.fillStyle = hatData.color;
        for (let i = -15; i <= 15; i += 6) {
          ctx.beginPath();
          ctx.moveTo(i - 3, -55);
          ctx.lineTo(i, -75 - Math.abs(i) * 0.3);
          ctx.lineTo(i + 3, -55);
          ctx.fill();
        }
        break;
      
      case 'turtle_shell': {
        // Turtle Man's Shell Hat - a dome shell sitting on top of the head
        const shellCx = 0;
        const shellCy = -62;
        const shellW = 28;
        const shellH = 18;
        
        // Main dome (dark green)
        ctx.fillStyle = '#2D6A4F';
        ctx.beginPath();
        ctx.ellipse(shellCx, shellCy, shellW, shellH, 0, Math.PI, 0);
        ctx.closePath();
        ctx.fill();
        
        // Shell rim (lighter green band at base)
        ctx.fillStyle = '#40916C';
        ctx.beginPath();
        ctx.ellipse(shellCx, shellCy, shellW, 5, 0, 0, Math.PI * 2);
        ctx.fill();
        
        // Shell pattern - hexagonal segments
        ctx.strokeStyle = '#1B4332';
        ctx.lineWidth = 1.5;
        
        // Center vertical line
        ctx.beginPath();
        ctx.moveTo(shellCx, shellCy);
        ctx.lineTo(shellCx, shellCy - shellH + 2);
        ctx.stroke();
        
        // Left diagonal lines
        ctx.beginPath();
        ctx.moveTo(shellCx, shellCy - shellH * 0.5);
        ctx.lineTo(shellCx - shellW * 0.6, shellCy);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(shellCx, shellCy - shellH * 0.8);
        ctx.lineTo(shellCx - shellW * 0.35, shellCy);
        ctx.stroke();
        
        // Right diagonal lines
        ctx.beginPath();
        ctx.moveTo(shellCx, shellCy - shellH * 0.5);
        ctx.lineTo(shellCx + shellW * 0.6, shellCy);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(shellCx, shellCy - shellH * 0.8);
        ctx.lineTo(shellCx + shellW * 0.35, shellCy);
        ctx.stroke();
        
        // Horizontal arc segments
        ctx.beginPath();
        ctx.ellipse(shellCx, shellCy, shellW * 0.7, shellH * 0.45, 0, Math.PI, 0);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.ellipse(shellCx, shellCy, shellW * 0.4, shellH * 0.7, 0, Math.PI, 0);
        ctx.stroke();
        
        // Highlight shine on top
        ctx.fillStyle = 'rgba(82, 183, 136, 0.3)';
        ctx.beginPath();
        ctx.ellipse(shellCx - 6, shellCy - shellH * 0.5, 6, 4, -0.4, 0, Math.PI * 2);
        ctx.fill();
        
        // Glow effect
        if (hatData.glow) {
          ctx.shadowColor = hatData.glow;
          ctx.shadowBlur = 8;
          ctx.strokeStyle = hatData.glow;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.ellipse(shellCx, shellCy, shellW + 2, shellH + 2, 0, Math.PI, 0);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
        break;
      }
        
      default:
        if (hatData.color !== 'transparent') {
          ctx.fillStyle = hatData.color;
          ctx.beginPath();
          ctx.ellipse(0, -60, 24, 12, 0, 0, Math.PI * 2);
          ctx.fill();
        }
    }
    ctx.shadowBlur = 0;
  };
  
  // drawChatBubble is now imported from components/nightclub/chatOverlay
  
  // Game loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let lastTimestamp = performance.now();
    let frameCount = 0;
    
    const gameLoop = () => {
      const currentTime = performance.now();
      const deltaMs = currentTime - lastTimestamp;
      lastTimestamp = currentTime;
      
      // Time-based animation phases (60fps = ~16.67ms per frame)
      // Increment by deltaMs/16.67 to normalize speed regardless of frame rate
      const deltaFrames = deltaMs / 16.67;
      bouncePhaseRef.current += deltaFrames;
      lightingPhaseRef.current += deltaFrames;
      djBounceRef.current += deltaFrames;
      frameCount++;
      
      // Use refs to avoid stale closures
      const currentPosition = myPositionRef.current;
      const currentTarget = targetPositionRef.current;
      const currentIsWalking = isWalkingRef.current;
      
      if (currentIsWalking) {
        walkFrameRef.current += 1;
        
        // Footstep sound every few frames
        footstepCounter.current++;
        if (footstepCounter.current % 15 === 0) {
          soundFX.footstep();
        }
      } else {
        footstepCounter.current = 0;
      }
      
      // Keyboard movement (WASD/Arrows) — desktop. Takes precedence over click-to-move.
      const keys = keysPressedRef.current;
      const kbActive = keys.up || keys.down || keys.left || keys.right;
      
      if (kbActive) {
        let kdx = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
        let kdy = (keys.down ? 1 : 0) - (keys.up ? 1 : 0);
        const mag = Math.sqrt(kdx * kdx + kdy * kdy);
        if (mag > 0) {
          if (rooftopSoakRef.current && Date.now() - rooftopSoakEnterRef.current > 400) rooftopSoakRef.current = false;
          kdx /= mag;
          kdy /= mag;
          const kbSpeed = 3.4 * deltaFrames * (drinkEffect === 'speed' ? 1.6 : 1); // tuned for ~60fps feel
          let newX = currentPosition.x + kdx * kbSpeed;
          let newY = currentPosition.y + kdy * kbSpeed;
          // Soft canvas bounds clamp
          newX = Math.max(20, Math.min(CANVAS_WIDTH - 20, newX));
          newY = Math.max(60, Math.min(CANVAS_HEIGHT - 20, newY));
          
          myPositionRef.current = { x: newX, y: newY };
          // Update direction
          const dir = directionFromDelta(kdx, kdy);
          if (dir) myDirectionRef.current = dir;
          
          if (!isWalkingRef.current) {
            isWalkingRef.current = true;
            setIsWalking(true);
          }
          if (frameCount % 3 === 0) {
            setMyPosition({ x: newX, y: newY });
          }
          const now = Date.now();
          if (now - lastPositionSendRef.current >= POSITION_SEND_INTERVAL && socketRef.current?.connected) {
            lastPositionSendRef.current = now;
            moveSequenceRef.current++;
            socketRef.current.emit('player_move', {
              seq: moveSequenceRef.current,
              x: Math.round(newX),
              y: Math.round(newY),
              is_walking: true,
              direction: myDirectionRef.current
            });
          }
        }
      } else if (currentTarget) {
        // Move towards target - smooth walking with easing (click-to-move)
        const dx = currentTarget.x - currentPosition.x;
        const dy = currentTarget.y - currentPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 3) {
          if (rooftopSoakRef.current && Date.now() - rooftopSoakEnterRef.current > 400) rooftopSoakRef.current = false;
          // Snappy walking speed with easing near target (was 0.35/0.45 — felt sluggish)
          const baseSpeed = (isMobile ? 1.7 : 2.1) * (drinkEffect === 'speed' ? 1.6 : 1);
          const easingFactor = Math.min(1, distance / 80);
          const speed = baseSpeed * (0.4 + easingFactor * 0.6);
          
          const newX = currentPosition.x + (dx / distance) * speed;
          const newY = currentPosition.y + (dy / distance) * speed;
          
          // CLIENT-SIDE PREDICTION: Update local position immediately (no waiting for server)
          myPositionRef.current = { x: newX, y: newY };
          // Update direction
          const dir = directionFromDelta(dx, dy);
          if (dir) myDirectionRef.current = dir;
          
          if (!isWalkingRef.current) {
            isWalkingRef.current = true;
            setIsWalking(true);
          }
          
          // Update React state every 3 frames (for UI elements that use state)
          if (frameCount % 3 === 0) {
            setMyPosition({ x: newX, y: newY });
          }
          
          // Send position to server at configured interval (~30fps)
          const now = Date.now();
          if (now - lastPositionSendRef.current >= POSITION_SEND_INTERVAL && socketRef.current?.connected) {
            lastPositionSendRef.current = now;
            moveSequenceRef.current++;
            socketRef.current.emit('player_move', {
              seq: moveSequenceRef.current,
              x: Math.round(newX),
              y: Math.round(newY),
              is_walking: true,
              direction: myDirectionRef.current
            });
          }
        } else {
          targetPositionRef.current = null;
          setTargetPosition(null);
          myPositionRef.current = { x: currentTarget.x, y: currentTarget.y };
          setMyPosition({ x: currentTarget.x, y: currentTarget.y });
          if (isWalkingRef.current) {
            isWalkingRef.current = false;
            setIsWalking(false);
            // Always send final stopped position
            if (socketRef.current?.connected) {
              moveSequenceRef.current++;
              socketRef.current.emit('player_move', {
                seq: moveSequenceRef.current,
                x: Math.round(currentTarget.x),
                y: Math.round(currentTarget.y),
                is_walking: false,
                direction: myDirectionRef.current
              });
            }
          }
        }
      } else {
        if (isWalkingRef.current) {
          isWalkingRef.current = false;
          setIsWalking(false);
          if (socketRef.current?.connected) {
            moveSequenceRef.current++;
            socketRef.current.emit('player_move', {
              seq: moveSequenceRef.current,
              x: Math.round(currentPosition.x),
              y: Math.round(currentPosition.y),
              is_walking: false,
              direction: myDirectionRef.current
            });
          }
        }
      }
      
      // Draw everything
      drawEnvironment(ctx);
      
      // Hard reset canvas state before drawing players
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';
      
      // Get players directly from ref (real-time, no React delay)
      const currentPlayers = playersRef.current;
      const currentEntranceEffects = entranceEffectsRef.current;
      const now = Date.now();
      
      // Smooth interpolation using targetX/targetY from server updates
      Object.entries(currentPlayers).forEach(([id, player]) => {
        if (player.x === undefined || player.y === undefined) return;
        
        let interp = interpolatedPositionsRef.current[id];
        if (!interp) {
          // New player - snap to position instantly AND render this frame
          interpolatedPositionsRef.current[id] = { 
            x: player.x, y: player.y, 
            targetX: player.x, targetY: player.y 
          };
          interp = interpolatedPositionsRef.current[id];
        }
        
        const targetX = interp.targetX !== undefined ? interp.targetX : player.x;
        const targetY = interp.targetY !== undefined ? interp.targetY : player.y;
        const dx = targetX - interp.x;
        const dy = targetY - interp.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist > SNAP_DISTANCE) {
          // Teleport if too far (e.g. room change)
          interp.x = targetX;
          interp.y = targetY;
        } else if (dist > 0.5) {
          // Adaptive lerp: faster when far, slower when close
          const lerpFactor = Math.min(0.35, 0.15 + (dist / 200) * 0.2);
          interp.x += dx * lerpFactor;
          interp.y += dy * lerpFactor;
          // Track direction from movement vector (or use player.direction if provided)
          const dirFromMove = directionFromDelta(dx, dy);
          if (player.direction) {
            playerDirectionsRef.current[id] = player.direction;
          } else if (dirFromMove) {
            playerDirectionsRef.current[id] = dirFromMove;
          }
        } else {
          interp.x = targetX;
          interp.y = targetY;
          if (player.direction) playerDirectionsRef.current[id] = player.direction;
        }
      });
      
      // Clean up interpolated positions for players who left
      Object.keys(interpolatedPositionsRef.current).forEach(id => {
        if (!currentPlayers[id]) delete interpolatedPositionsRef.current[id];
      });
      
      // Draw other players in the same room
      Object.entries(currentPlayers)
        .filter(([id, player]) => (player.room || 'main') === currentRoom && player.x && player.y)
        .forEach(([id, player]) => {
          // Handle leaving players - gradual fade out effect
          if (player.status === 'leaving') {
            const elapsed = Date.now() - (player.leaveTime || Date.now());
            const fadeProgress = Math.min(elapsed / 1000, 1); // 1 second fade
            ctx.globalAlpha = Math.max(0, 1 - fadeProgress);
            if (ctx.globalAlpha <= 0) return; // Fully faded, skip rendering
          }
          
          // Get interpolated position
          const interpPos = interpolatedPositionsRef.current[id] || { x: player.x, y: player.y, targetX: player.x, targetY: player.y };
          
          // Detect if player is actually moving (interpolation still in progress towards target)
          const interpDx = interpPos.targetX !== undefined ? Math.abs(interpPos.x - interpPos.targetX) : 0;
          const interpDy = interpPos.targetY !== undefined ? Math.abs(interpPos.y - interpPos.targetY) : 0;
          const isActuallyMoving = player.is_walking || interpDx > 2 || interpDy > 2;
          
          // Check for entrance animation effect
          const entranceEffect = currentEntranceEffects[id];
          if (entranceEffect) {
            const elapsed = Date.now() - entranceEffect.startTime;
            const progress = Math.min(elapsed / 2000, 1); // 2 second animation
            
            // Draw sparkle/particle entrance effect
            ctx.save();
            const px = interpPos.x * (CANVAS_WIDTH / 800);
            const py = interpPos.y * (CANVAS_HEIGHT / 600);
            
            // Radial sparkles
            const sparkleCount = 8;
            const sparkleRadius = 60 * (1 - progress);
            const sparkleOpacity = 1 - progress;
            
            for (let i = 0; i < sparkleCount; i++) {
              const angle = (i / sparkleCount) * Math.PI * 2 + elapsed * 0.003;
              const sx = px + Math.cos(angle) * sparkleRadius;
              const sy = py - 40 + Math.sin(angle) * sparkleRadius * 0.6;
              
              ctx.fillStyle = `rgba(255, 215, 0, ${sparkleOpacity * 0.8})`;
              ctx.shadowColor = 'gold';
              ctx.shadowBlur = 10;
              ctx.beginPath();
              ctx.arc(sx, sy, 3 + Math.sin(elapsed * 0.01 + i) * 2, 0, Math.PI * 2);
              ctx.fill();
            }
            
            // Rising particles
            for (let i = 0; i < 6; i++) {
              const particleProgress = (elapsed * 0.002 + i * 0.15) % 1;
              const particleX = px + Math.sin(i * 1.5 + elapsed * 0.002) * 25;
              const particleY = py - 20 - particleProgress * 80;
              const particleOpacity = (1 - particleProgress) * sparkleOpacity;
              
              ctx.fillStyle = `rgba(147, 51, 234, ${particleOpacity})`; // Purple particles
              ctx.beginPath();
              ctx.arc(particleX, particleY, 2 + particleProgress * 2, 0, Math.PI * 2);
              ctx.fill();
            }
            
            ctx.shadowBlur = 0;
            ctx.restore();
          }
          
          const currentChatMessages = chatMessagesRef.current;
          const playerChat = currentChatMessages.find(m => m.playerId === id && Date.now() - m.timestamp < Math.min(9000, 3000 + (m.text?.length || 0) * 60));
          // Check if player has an active emote (prefer exact startedAt for phase sync)
          const playerEmoteDef = player.currentEmote && player.emoteEndTime > Date.now()
            ? DANCE_EMOTES.find(e => e.id === player.currentEmote)
            : null;
          const playerEmote = playerEmoteDef
            ? { ...playerEmoteDef, startedAt: player.emoteStartedAt || (player.emoteEndTime - (playerEmoteDef.duration || 3000)) }
            : null;
          
          // Check if player is speaking AND within proximity range
          const speakerData = speakingPlayers[id];
          const isPlayerSpeaking = speakerData?.speaking && isInVoiceProximity(speakerData.x, speakerData.y);
          const voiceVolume = speakerData?.speaking ? getVoiceVolume(speakerData.x, speakerData.y) : 0;
          
          drawAvatar(
            ctx, 
            interpPos.x * (CANVAS_WIDTH / 800), 
            interpPos.y * (CANVAS_HEIGHT / 600), 
            player.avatar || avatarRef.current, 
            false, 
            player.role, 
            player.name,
            isActuallyMoving,  // Use detected movement, not just server flag
            playerChat,
            playerEmote,
            isPlayerSpeaking,
            voiceVolume,
            player.transform || null,
            playerDirectionsRef.current[id] || 'down'
          );
          
          // Remote soaker — clip below the waterline + steam overlay
          {
            const rpX = interpPos.x * (CANVAS_WIDTH / 800);
            const rpY = interpPos.y * (CANVAS_HEIGHT / 600);
            const tubY0 = (isMobile ? CANVAS_HEIGHT * 0.35 : 200) + 175;
            if (currentRoom === 'rooftop' && !isActuallyMoving &&
                Math.abs(rpX - CANVAS_WIDTH * 0.78) < 50 && Math.abs(rpY - (tubY0 - 4)) < 26) {
              drawTubSoakOverlay(ctx, rpX, rpY, lightingPhaseRef.current, null);
            }
          }
          
          // Remote drink visual effect (synced via player_effect_update)
          if (player.drink_effect?.effect) {
            drawDrinkEffect(
              ctx,
              interpPos.x * (CANVAS_WIDTH / 800),
              interpPos.y * (CANVAS_HEIGHT / 600),
              player.drink_effect.effect,
              player.drink_effect.color || '#9333EA',
              lightingPhaseRef.current
            );
          }
          
          // Reset alpha after drawing leaving players
          if (player.status === 'leaving') {
            ctx.globalAlpha = 1;
          }
        });
      
      // Draw voice proximity range indicator when mic is active
      const currentMyPosition = myPositionRef.current;
      // Scale from normalized (800x600) to canvas coordinates
      const scaledMyX = currentMyPosition.x * (CANVAS_WIDTH / 800);
      const scaledMyY = currentMyPosition.y * (CANVAS_HEIGHT / 600);
      
      if (voiceChatEnabled && !isMicMuted) {
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 255, 0, 0.3)';
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(scaledMyX, scaledMyY, VOICE_PROXIMITY_RANGE, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
      
      // Draw current player
      const currentChatMsgs = chatMessagesRef.current;
      const myChat = currentChatMsgs.find(m => m.playerId === (user?.user_id || 'me') && Date.now() - m.timestamp < Math.min(9000, 3000 + (m.text?.length || 0) * 60));
      const myEmote = currentEmote && emoteEndTime > Date.now() 
        ? DANCE_EMOTES.find(e => e.id === currentEmote)
        : null;
      
      // Draw drink effect behind player
      if (drinkEffect && activeDrink) {
        drawDrinkEffect(ctx, scaledMyX, scaledMyY, drinkEffect, activeDrink.effectColor, lightingPhaseRef.current);
      }
      
      const mySoaking = currentRoom === 'rooftop' && rooftopSoakRef.current;
      if (mySoaking) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(scaledMyX - 46, scaledMyY - 96, 92, 70);
        ctx.clip();
      }
      drawAvatar(
        ctx, 
        scaledMyX, 
        scaledMyY, 
        avatar, 
        true, 
        user?.role || 'user', 
        user?.username || user?.name || 'Guest',
        isWalkingRef.current,
        myChat,
        myEmote,
        false,
        1,
        null,
        myDirectionRef.current
      );
      if (mySoaking) {
        ctx.restore();
        drawTubSoakOverlay(ctx, scaledMyX, scaledMyY, lightingPhaseRef.current, user?.username || user?.name || 'Guest');
      }
      
      // Draw target indicator (scale from normalized to canvas)
      const currentTargetPos = targetPositionRef.current;
      if (currentTargetPos) {
        const scaledTargetX = currentTargetPos.x * (CANVAS_WIDTH / 800);
        const scaledTargetY = currentTargetPos.y * (CANVAS_HEIGHT / 600);
        ctx.strokeStyle = 'rgba(147, 51, 234, 0.5)';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.arc(scaledTargetX, scaledTargetY, isMobile ? 8 : 10, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      
      // Draw spotlight zone on dance floor
      const spotlightX = SPOTLIGHT_ZONE.x * (CANVAS_WIDTH / 800);
      const spotlightY = SPOTLIGHT_ZONE.y * (CANVAS_HEIGHT / 600);
      const spotlightR = SPOTLIGHT_ZONE.radius * (CANVAS_WIDTH / 800);
      
      const currentAudioLevel = audioLevelRef.current;
      const spotGradient = ctx.createRadialGradient(spotlightX, spotlightY, 0, spotlightX, spotlightY, spotlightR);
      spotGradient.addColorStop(0, `rgba(147, 51, 234, ${0.15 + currentAudioLevel * 0.1})`);
      spotGradient.addColorStop(0.5, `rgba(147, 51, 234, ${0.08 + currentAudioLevel * 0.05})`);
      spotGradient.addColorStop(1, 'transparent');
      ctx.fillStyle = spotGradient;
      ctx.beginPath();
      ctx.arc(spotlightX, spotlightY, spotlightR, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw particles
      const currentParticles = particlesRef.current;
      currentParticles.forEach(p => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        if (p.type === 'star') {
          // Draw star
          ctx.font = `${p.size * 3}px sans-serif`;
          ctx.fillText('✦', p.x, p.y);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      });
      
      // Draw floating reactions
      const currentReactions = reactionsRef.current;
      currentReactions.forEach(r => {
        ctx.globalAlpha = r.life;
        ctx.font = `${20 + (1 - r.life) * 10}px sans-serif`;
        ctx.fillText(r.emoji, r.x, r.y);
        ctx.globalAlpha = 1;
      });
      
      // Draw music visualizer bars at bottom
      if (currentAudioLevel > 0.1) {
        const barCount = isMobile ? 10 : 20;
        const barWidth = CANVAS_WIDTH / barCount;
        for (let i = 0; i < barCount; i++) {
          const barHeight = (Math.sin(lightingPhaseRef.current * 0.1 + i * 0.5) * 0.5 + 0.5) * currentAudioLevel * 40;
          ctx.fillStyle = `rgba(147, 51, 234, ${0.3 + currentAudioLevel * 0.3})`;
          ctx.fillRect(i * barWidth, CANVAS_HEIGHT - barHeight, barWidth - 2, barHeight);
        }
      }
      
      animationRef.current = requestAnimationFrame(gameLoop);
    };
    
    gameLoop();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // Minimal dependencies - most values accessed via refs to avoid stale closures
    // eslint-disable-next-line
  }, [user, drawEnvironment, drawAvatar, CANVAS_WIDTH, CANVAS_HEIGHT, isMobile, speakingPlayers, voiceChatEnabled, isMicMuted, isInVoiceProximity, getVoiceVolume, VOICE_PROXIMITY_RANGE, currentEmote, emoteEndTime, drinkEffect, activeDrink, currentRoom]);
  
  // Shared interactive zones (elevators + bars) — used by BOTH click and touch
  const tryInteractiveTap = (x, y, floorY) => {
    if (currentRoom === 'vip_lounge') {
      // VIP bar shelf — tap to order a drink
      const shelfX = CANVAS_WIDTH - (isMobile ? 150 : 190);
      const shelfY = isMobile ? 55 : 75;
      if (x >= shelfX - 12 && x <= shelfX + 142 && y >= shelfY - 30 && y <= shelfY + 92) {
        setShowBartender(true);
        soundFX.click();
        return true;
      }
      if (canAccessRooftop) {
        // Elevator position matches drawElevator — generous hitbox incl. the ROOFTOP sign
        const elevatorW = isMobile ? 58 : 80;
        const elevatorH = isMobile ? 66 : 94;
        const elevatorX = Math.round(CANVAS_WIDTH * 0.22) - elevatorW / 2;
        const elevatorY = floorY - elevatorH - 5;
        if (x >= elevatorX - 18 && x <= elevatorX + elevatorW + 18 &&
            y >= elevatorY - 48 && y <= elevatorY + elevatorH + 14) {
          setShowElevatorPrompt(true);
          soundFX.click();
          return true;
        }
      }
    }
    if (currentRoom === 'rooftop') {
      // Return elevator — matches drawReturnElevator
      const elevatorW = isMobile ? 52 : 72;
      const elevatorH = isMobile ? 60 : 86;
      const elevatorX = isMobile ? 30 : 50;
      const elevatorY = floorY + 30;
      if (x >= elevatorX - 16 && x <= elevatorX + elevatorW + 16 &&
          y >= elevatorY - 44 && y <= elevatorY + elevatorH + 14) {
        setShowReturnElevatorPrompt(true);
        soundFX.click();
        return true;
      }
      // Sky Bar / Rio
      const bartenderHit = getBartenderHitbox(CANVAS_WIDTH, CANVAS_HEIGHT, floorY);
      if (x >= bartenderHit.x && x <= bartenderHit.x + bartenderHit.width &&
          y >= bartenderHit.y && y <= bartenderHit.y + bartenderHit.height) {
        setShowBartender(true);
        soundFX.click();
        return true;
      }
      // Hot tub — tap to soak, tap again to hop out
      const tubX = CANVAS_WIDTH * 0.78;
      const tubY = floorY + 175;
      if (Math.abs(x - tubX) < 66 && Math.abs(y - (tubY - 5)) < 36) {
        const sx = CANVAS_WIDTH / 800;
        const sy = CANVAS_HEIGHT / 600;
        if (rooftopSoakRef.current) {
          if (Date.now() - rooftopSoakEnterRef.current > 800) {
            rooftopSoakRef.current = false;
            setTargetPosition({ x: tubX / sx, y: Math.min(575, (tubY + 46) / sy) });
          }
          soundFX.click();
          return true;
        }
        const seats = [-26, 0, 26].map((o) => ({ x: (tubX + o) / sx, y: (tubY - 4) / sy }));
        const others = Object.values(playersRef.current || {}).filter((p) => (p.room || 'main') === 'rooftop');
        const free = seats.filter((s) => !others.some((o) => Math.abs((o.x ?? 0) - s.x) < 18 && Math.abs((o.y ?? 0) - s.y) < 18));
        const pool = free.length ? free : seats;
        let seat = pool[0];
        let best = Infinity;
        pool.forEach((s) => {
          const d = (s.x * sx - x) ** 2 + (s.y * sy - y) ** 2;
          if (d < best) { best = d; seat = s; }
        });
        rooftopSoakRef.current = true;
        rooftopSoakEnterRef.current = Date.now();
        myPositionRef.current = { x: seat.x, y: seat.y };
        setMyPosition({ x: seat.x, y: seat.y });
        setTargetPosition(null);
        myDirectionRef.current = 'down';
        if (socketRef.current?.connected) {
          moveSequenceRef.current++;
          socketRef.current.emit('player_move', {
            seq: moveSequenceRef.current,
            x: Math.round(seat.x), y: Math.round(seat.y),
            is_walking: false, direction: 'down',
          });
        }
        toast.success('Ahhh... a rooftop soak under the stars 🛁✨');
        soundFX.click();
        return true;
      }
    }
    return false;
  };

  // Handle canvas click for movement or player click
  const handleCanvasClick = (e) => {
    // ignore the synthetic click browsers fire right after a touchstart
    if (Date.now() - canvasTouchTimeRef.current < 700) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const floorY = isMobile ? CANVAS_HEIGHT * 0.35 : 200;
    
    if (tryInteractiveTap(x, y, floorY)) return;
    
    // Check if clicked on the Apartments door (Chill Zone)
    if (currentRoom === 'chill_zone') {
      const door = getApartmentsDoorRect(CANVAS_WIDTH, CANVAS_HEIGHT, floorY, isMobile);
      if (x >= door.x - 5 && x <= door.x + door.w + 5 && y >= door.y - 24 && y <= door.y + door.h + 9) {
        const aptRoom = CLUB_ROOMS.find(r => r.id === 'apartments');
        if (aptRoom) changeRoom(aptRoom);
        return;
      }
    }
    
    // Check if clicked on another player (only in same room) — live positions
    for (const [playerId, player] of Object.entries(playersRef.current)) {
      // Skip players in different rooms
      if ((player.room || 'main') !== currentRoom) continue;
      
      const live = interpolatedPositionsRef.current[playerId];
      const lx = live?.x ?? player.x;
      const ly = live?.y ?? player.y;
      const playerX = lx * (CANVAS_WIDTH / 800);
      const playerY = ly * (CANVAS_HEIGHT / 600);
      const distance = Math.sqrt(Math.pow(x - playerX, 2) + Math.pow(y - playerY, 2));
      
      if (distance < 40) {
        // turn to face the player you're looking at
        const me = myPositionRef.current;
        const fdir = directionFromDelta(lx - me.x, ly - me.y);
        if (fdir) {
          myDirectionRef.current = fdir;
          if (socketRef.current?.connected) {
            socketRef.current.emit('player_move', {
              x: Math.round(me.x), y: Math.round(me.y), is_walking: false, direction: fdir,
            });
          }
        }
        handlePlayerClick(playerId, player);
        return;
      }
    }
    
    if (y > floorY) {
      // IMPORTANT: Convert canvas coordinates to normalized 800x600 space
      // This ensures positions are consistent across all screen sizes
      const normalizedX = x * (800 / CANVAS_WIDTH);
      const normalizedY = Math.min(y, CANVAS_HEIGHT - 50) * (600 / CANVAS_HEIGHT);
      setTargetPosition({ x: normalizedX, y: normalizedY });
    }
  };
  
  // Handle touch for mobile
  const handleCanvasTouch = (e) => {
    canvasTouchTimeRef.current = Date.now();
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (touch.clientX - rect.left) * scaleX;
    const y = (touch.clientY - rect.top) * scaleY;
    
    const floorY = isMobile ? CANVAS_HEIGHT * 0.35 : 200;
    
    // Elevators + bars work on touch too
    if (tryInteractiveTap(x, y, floorY)) return;
    
    // Apartments door tap (Chill Zone)
    if (currentRoom === 'chill_zone') {
      const door = getApartmentsDoorRect(CANVAS_WIDTH, CANVAS_HEIGHT, floorY, isMobile);
      if (x >= door.x - 5 && x <= door.x + door.w + 5 && y >= door.y - 24 && y <= door.y + door.h + 9) {
        const aptRoom = CLUB_ROOMS.find(r => r.id === 'apartments');
        if (aptRoom) changeRoom(aptRoom);
        return;
      }
    }
    
    // Tap on another player (same room) — open their profile card
    for (const [playerId, player] of Object.entries(playersRef.current)) {
      if ((player.room || 'main') !== currentRoom) continue;
      const live = interpolatedPositionsRef.current[playerId];
      const lx = live?.x ?? player.x;
      const ly = live?.y ?? player.y;
      const playerX = lx * (CANVAS_WIDTH / 800);
      const playerY = ly * (CANVAS_HEIGHT / 600);
      if (Math.sqrt((x - playerX) ** 2 + (y - playerY) ** 2) < 42) {
        handlePlayerClick(playerId, player);
        return;
      }
    }
    
    if (y > floorY) {
      // IMPORTANT: Convert canvas coordinates to normalized 800x600 space
      const normalizedX = x * (800 / CANVAS_WIDTH);
      const normalizedY = Math.min(y, CANVAS_HEIGHT - 50) * (600 / CANVAS_HEIGHT);
      setTargetPosition({ x: normalizedX, y: normalizedY });
    }
  };
  
  // Send chat message (called by ChatPanel component)
  const sendChat = (text) => {
    if (!text) return;
    
    soundFX.chatSend();
    
    const message = {
      playerId: user?.user_id || 'me',
      playerName: user?.username || user?.name || 'Guest',
      text: text,
      role: user?.role || 'user',
      timestamp: Date.now()
    };
    
    setChatMessages(prev => [...prev.slice(-20), message]);
    
    // Send via WebSocket with user_id for quest tracking
    if (socketRef.current?.connected) {
      socketRef.current.emit('player_chat', { 
        message: text,
        user_id: user?.user_id 
      });
    }
  };
  
  // Update avatar
  const updateAvatar = (key, value) => {
    setAvatar(prev => {
      const newAvatar = { ...prev, [key]: value };
      
      // Send update via Socket.IO
      if (socketRef.current?.connected) {
        socketRef.current.emit('avatar_update', { avatar: newAvatar });
      }
      
      return newAvatar;
    });
  };
  
  // Admin controls
  const handleStreamControl = async (active) => {
    // Use both Socket.IO and REST API for reliability
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin_stream', {
        url: streamUrl,
        is_active: active
      });
    }
    
    // Also send via REST API to persist
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/nightclub/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: streamUrl, is_active: active })
      });
    } catch (e) {
      console.log('REST API fallback failed:', e);
    }
    
    setIsStreaming(active);
    toast.success(active ? 'Stream started!' : 'Stream stopped');
  };
  
  const handleLightingChange = (mode) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('admin_lighting', { mode });
    }
    setLightingMode(mode);
  };

  // ==================== BATCH 2 & 3 FEATURE HANDLERS ====================
  
  // Claim daily bonus (with VIP room multiplier)
  const claimDailyBonus = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/vibecoins/daily?current_room=${currentRoom}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        setVibecoins(data.balance);
        setDailyBonusClaimed(true);
        toast.success(data.message);
        soundFX.success();
      } else {
        toast.error(data.detail || 'Already claimed!');
      }
    } catch (e) {
      toast.error('Failed to claim bonus');
    }
  };
  
  // Send gift to player (with VIP room discount)
  const sendGift = async (giftType) => {
    if (!giftTarget) return;
    
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/gifts/send?to_user_id=${giftTarget.id}&gift_type=${giftType}&current_room=${currentRoom}`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        setVibecoins(prev => prev - NIGHTCLUB_GIFT_COSTS[giftType]);
        setShowGiftModal(false);
        soundFX.success();
        
        // Broadcast gift via Socket.IO with user_id for quest tracking
        if (socketRef.current?.connected) {
          socketRef.current.emit('send_gift', {
            to_id: giftTarget.id,
            to_name: giftTarget.name,
            gift_type: giftType,
            gift_emoji: NIGHTCLUB_GIFT_EMOJIS[giftType],
            animation: data.animation,
            user_id: user?.user_id
          });
        }
        
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to send gift');
      }
    } catch (e) {
      toast.error('Failed to send gift');
    }
  };
  
  // Claim quest reward
  const claimQuestReward = async (questId) => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/quests/${questId}/claim`, {
        method: 'POST'
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        setVibecoins(prev => prev + data.reward);
        setQuests(prev => prev.map(q => 
          q.quest_id === questId ? { ...q, claimed: true } : q
        ));
        soundFX.success();
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to claim reward');
      }
    } catch (e) {
      toast.error('Failed to claim reward');
    }
  };
  
  // Open gift modal for a player
  const openGiftModal = (playerId, playerName) => {
    setGiftTarget({ id: playerId, name: playerName });
    setShowGiftModal(true);
    setShowProfileCard(false);
  };

  // Guest mode - show option to enter as guest or sign in
  if (!isAuthenticated && !isGuest) {
    // This shouldn't happen since isGuest = !isAuthenticated, but keeping as safety
  }

  // Debug: Simple render test
  try {
    return (
      <div 
        ref={containerRef}
        className={`${isFullscreen ? 'fixed inset-0 z-50 bg-black' : `min-h-screen ${isMobile ? 'pt-16 pb-2 px-2' : 'pt-20 pb-4 px-4'}`} relative z-10 ${tripActive ? 'nightclub-trip-active' : ''}`}
      >
        <div className={`mx-auto ${isFullscreen ? 'w-full h-full' : isMobile ? 'max-w-full' : 'max-w-6xl'}`}>
        {/* Header - Clean minimal design */}
        {!isFullscreen && (
          <div className="flex items-center justify-between mb-3 px-2">
            <div>
              <h1 className="font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent text-xl sm:text-2xl">
                Virtual Nightclub
              </h1>
              <p className="text-white/50 text-xs sm:text-sm">Tap anywhere to move</p>
            </div>
            
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 rounded-full border border-yellow-500/30">
              <span className="text-yellow-400 text-xl">💰</span>
              <span className="text-yellow-300 font-bold text-lg">{vibecoins}</span>
              <span className="text-yellow-500/60 text-sm">VibeCoins</span>
              {!dailyBonusClaimed && (
                <Button onClick={claimDailyBonus} size="sm" className="ml-2 h-7 px-3 text-xs bg-gradient-to-r from-green-600 to-emerald-600">+25 Daily</Button>
              )}
              {hourlyRewardReady ? (
                <Button onClick={claimHourlyReward} size="sm" className="ml-1 h-7 px-3 text-xs bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse">+100 Hourly</Button>
              ) : (
                <span className="ml-2 text-xs text-white/40">⏰ {Math.floor(hourlyCountdown / 60)}:{(hourlyCountdown % 60).toString().padStart(2, '0')}</span>
              )}
            </div>
            
            <div className="flex items-center gap-2 mr-16">
              <div className="sm:hidden flex items-center gap-1 px-2 py-1 bg-yellow-600/20 rounded-full border border-yellow-500/30">
                <span className="text-yellow-400 text-sm">💰</span>
                <span className="text-yellow-300 font-bold text-sm">{vibecoins}</span>
                {hourlyRewardReady && (
                  <button onClick={claimHourlyReward} className="ml-1 text-xs bg-purple-600 px-1.5 py-0.5 rounded animate-pulse">+100</button>
                )}
              </div>
              {isStreaming && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-600/30 rounded-full border border-red-500/50 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-red-400 text-xs font-bold">LIVE</span>
                </div>
              )}
              {!isGuest && <NotificationBell ref={notificationBellRef} isGuest={isGuest} />}
            </div>
          </div>
        )}
        
        <HamburgerMenu
          setShowEmoteMenu={setShowEmoteMenu}
          setShowQuestPanel={setShowQuestPanel}
          setShowLeaderboard={setShowLeaderboard}
          setShowRoomMenu={setShowRoomMenu}
          setShowAvatarEditor={setShowAvatarEditor}
          setShowAdminPanel={setShowAdminPanel}
          setShowVirtualBar={setShowVirtualBar}
          setShowDailyRewards={setShowDailyRewards}
          setShowPrivateRooms={setShowPrivateRooms}
          setShowAnnouncement={setShowAnnouncement}
          voiceChatEnabled={voiceChatEnabled}
          isMicMuted={isMicMuted}
          toggleMic={toggleMic}
          isAdmin={isAdmin}
          isDJ={isDJ}
          vibecoins={vibecoins}
          dailyBonusClaimed={dailyBonusClaimed}
          claimDailyBonus={claimDailyBonus}
          soundFX={soundFX}
          currentRoom={currentRoom}
          lightingFx={lightingFx}
          onLightingFxChange={setLightingFx}
          lightingPreset={lightingPreset}
          onLightingPresetChange={setLightingPreset}
          autoCycleLights={autoCycleLights}
          onAutoCycleChange={setAutoCycleLights}
          onDropTrigger={triggerDrop}
          slowMode={slowMode}
          onSlowModeToggle={setSlowMode}
          roomLocked={roomLocked}
          onRoomLockToggle={setRoomLocked}
          onOpenRoomCreator={() => setShowRoomCreator(true)}
          onOpenPhotoMode={() => setShowPhotoMode(true)}
          onOpenDJQueue={() => setShowDJQueue(true)}
          onOpenDanceBattle={() => setShowDanceBattle(true)}
          onOpenFriendsPanel={() => setShowFriendsPanel(true)}
          weatherEffect={weatherEffect}
          setWeatherEffect={setWeatherEffect}
          isFullscreen={isFullscreen}
          visualizerSettings={visualizerSettings}
          onVisualizerSettingsChange={(newSettings) => {
            setVisualizerSettings(newSettings);
            localStorage.setItem('nightclub_visualizer', JSON.stringify(newSettings));
          }}
          VISUALIZER_PRESETS={VISUALIZER_PRESETS}
          socketRef={socketRef}
          user={user}
          voiceChatVolume={voiceChatVolume}
          onVoiceChatVolumeChange={setVoiceChatVolume}
          musicVolume={musicVolume}
          onMusicVolumeChange={(vol) => {
            setMusicVolume(vol);
            // Send volume command to YouTube iframe
            if (youtubeRef.current?.contentWindow) {
              youtubeRef.current.contentWindow.postMessage(
                JSON.stringify({ event: 'command', func: 'setVolume', args: [Math.round(vol * 100)] }),
                '*'
              );
            }
          }}
          onUsernameChange={async (newUsername) => {
            try {
              const res = await authFetch(`${API_URL}/api/nightclub/set-username`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: newUsername, is_change: true })
              });
              if (!res) return false;
              const data = await res.json();
              if (res.ok) {
                setDisplayName(newUsername);
                updateUser({ 
                  username: newUsername, 
                  username_changed_at: new Date().toISOString() 
                });
                toast.success('Username changed successfully!');
                return true;
              } else {
                toast.error(data.detail || 'Failed to change username');
                return false;
              }
            } catch (err) {
              toast.error('Failed to change username');
              return false;
            }
          }}
          isStreaming={isStreaming}
          onVideoPlay={async () => {
            try {
              await authFetch(`${API_URL}/api/nightclub/video/play`, { method: 'POST' });
            } catch (e) {
              console.error('Video play error:', e);
            }
          }}
          onVideoPause={async () => {
            try {
              await authFetch(`${API_URL}/api/nightclub/video/pause`, { method: 'POST' });
            } catch (e) {
              console.error('Video pause error:', e);
            }
          }}
          onVideoSkip={async (seconds) => {
            try {
              await authFetch(`${API_URL}/api/nightclub/video/skip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ seconds })
              });
            } catch (e) {
              console.error('Video skip error:', e);
            }
          }}
        />
        
        {/* Game Canvas */}
        <div className={`relative ${isFullscreen ? 'w-full h-full' : 'rounded-xl overflow-hidden border-2 border-purple-500/30 shadow-[0_0_30px_rgba(147,51,234,0.3)]'}`}>
          <canvas
            ref={canvasRef}
            data-testid="nightclub-canvas"
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            onClick={handleCanvasClick}
            onTouchStart={handleCanvasTouch}
            className={`${isFullscreen ? 'w-full h-full' : 'w-full'} cursor-pointer touch-none`}
            style={{ maxHeight: isFullscreen ? '100vh' : isMobile ? '70vh' : '65vh' }}
          />
          
          {/* Apartment Complex takes over fullscreen when in the apartments room */}
          {currentRoom === 'apartments' && (
            <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center pt-14 pb-1 px-1" data-testid="apartments-overlay">
              <ApartmentComplex
                user={user}
                avatar={avatar}
                vibecoins={vibecoins}
                setVibecoins={setVibecoins}
                authFetch={authFetch}
                API_URL={API_URL}
                socketRef={socketRef}
                isMobile={isMobile}
                players={players}
                playersLiveRef={playersRef}
                currentEmote={currentEmote}
                emoteEndTime={emoteEndTime}
                onExit={() => changeRoom(CLUB_ROOMS.find(r => r.id === 'chill_zone'))}
                onGoCity={() => changeRoom(CLUB_ROOMS.find(r => r.id === 'city'))}
                serverRoomRef={serverRoomRef}
                chatMessagesRef={chatMessagesRef}
              />
            </div>
          )}
          
          {/* City Block — walkable street outside the club */}
          {currentRoom === 'city' && (
            <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center pt-14 pb-1 px-1" data-testid="city-overlay">
              <CityBlock
                user={user}
                avatar={avatar}
                vibecoins={vibecoins}
                setVibecoins={setVibecoins}
                authFetch={authFetch}
                API_URL={API_URL}
                socketRef={socketRef}
                players={players}
                playersLiveRef={playersRef}
                currentEmote={currentEmote}
                emoteEndTime={emoteEndTime}
                onExitClub={() => changeRoom(CLUB_ROOMS.find(r => r.id === 'main'))}
                onGoApartments={() => changeRoom(CLUB_ROOMS.find(r => r.id === 'apartments'))}
                onPlayerClick={handlePlayerClick}
                spawnSignal={citySpawn}
                serverRoomRef={serverRoomRef}
                chatMessagesRef={chatMessagesRef}
              />
            </div>
          )}
          
          {/* Fullscreen Exit Button (top-left X) */}
          {isFullscreen && (
            <Button
              onClick={toggleFullscreen}
              variant="ghost"
              size="icon"
              className="absolute top-4 left-4 bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 z-50"
            >
              <X className="w-6 h-6" />
            </Button>
          )}
          
          {/* Fullscreen Button (when not fullscreen) */}
          {!isFullscreen && (
            <Button
              onClick={toggleFullscreen}
              variant="ghost"
              size="icon"
              className={`absolute ${isMobile ? 'top-2 left-12' : 'top-4 left-20'} bg-black/60 hover:bg-black/80 text-white rounded-full`}
            >
              <Maximize className="w-4 h-4" />
            </Button>
          )}
          
          {/* Online players count with connection status - shows players in same room */}
          {(!isFullscreen || !hideFullscreenControls) && (
            <div className={`absolute ${isMobile ? 'top-2 left-2' : 'top-4 left-4'} ${isFullscreen ? 'left-16' : ''} bg-black/60 backdrop-blur px-2 py-1 rounded-full flex items-center gap-2`}>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-red-500'} ${wsConnected ? 'animate-pulse' : ''}`} />
                <Users className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-purple-400`} />
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-white`}>
                  {Object.values(players).filter(p => (p.room || 'main') === currentRoom).length + 1}
                </span>
              </div>
              {!wsConnected && (
                <span className="text-xs text-red-400">Offline</span>
              )}
            </div>
          )}
          
          {/* Stream status */}
          {(!isFullscreen || !hideFullscreenControls) && (
            <div className={`absolute ${isMobile ? 'top-2 right-2' : 'top-4 right-4'} bg-black/60 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 z-30`}>
              <Tv className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} ${isStreaming ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} />
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-white`}>{isStreaming ? 'LIVE' : 'OFF'}</span>
              {/* Mute toggle in stream status bar */}
              {isStreaming && (
                <button
                  onClick={() => {
                    const newMuted = !muted;
                    setMuted(newMuted);
                    if (youtubeRef.current) {
                      youtubeRef.current.contentWindow.postMessage(
                        JSON.stringify({ event: 'command', func: newMuted ? 'mute' : 'unMute' }), 
                        '*'
                      );
                    }
                    soundFX.click();
                  }}
                  className="ml-1"
                >
                  {muted ? (
                    <VolumeX className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-red-400`} />
                  ) : (
                    <Volume2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-green-400`} />
                  )}
                </button>
              )}
            </div>
          )}
          
          {/* Emote Quick Bar (in fullscreen) - moved to bottom left */}
          {isFullscreen && !hideFullscreenControls && (
            <div className="absolute bottom-16 left-4 flex gap-1 z-40">
              {DANCE_EMOTES.slice(0, 5).map(emote => (
                <Button
                  key={emote.id}
                  onClick={() => playEmote(emote)}
                  variant="ghost"
                  size="icon"
                  className="bg-black/60 hover:bg-purple-500/50 text-white rounded-full w-10 h-10"
                  title={emote.name}
                >
                  <span className="text-lg">{emote.icon}</span>
                </Button>
              ))}
            </div>
          )}
          
          {/* YouTube Video Player */}
          {isStreaming && streamUrl && (
            <YouTubePlayer
              streamUrl={streamUrl}
              streamStartedAt={streamStartedAt}
              muted={muted}
              setMuted={setMuted}
              isMobile={isMobile}
              shouldShowVideo={shouldShowVideo}
              youtubeRef={youtubeRef}
              socketRef={socketRef}
              onAudioData={setAudioData}
              volume={musicVolume}
              userRole={user?.role}
            />
          )}
          
          {/* Guest Mode Banner */}
          {isGuest && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-black/80 backdrop-blur-sm border border-purple-500/30 rounded-full px-6 py-2 flex items-center gap-3" data-testid="guest-mode-banner">
              <span className="text-white/80 text-sm">You're in Guest Mode</span>
              <a href="/signup" className="text-purple-400 hover:text-purple-300 text-sm font-medium underline">Sign up to chat</a>
            </div>
          )}
          
          {/* Chat Panel */}
          <ChatPanel
            chatMessages={chatMessages}
            onSendMessage={sendChat}
            isMobile={isMobile}
            showChatLog={showChatLog}
            setShowChatLog={setShowChatLog}
            isFullscreen={isFullscreen}
            hideFullscreenControls={hideFullscreenControls}
            overlayMode={currentRoom === 'city' || currentRoom === 'apartments'}
            typingPlayers={typingPlayers}
            onTypingStart={() => socketRef.current?.emit('typing_start', { room: currentRoom })}
            onTypingStop={() => socketRef.current?.emit('typing_stop', { room: currentRoom })}
            isGuest={isGuest}
          />
          
          {/* Now Playing Mini Bar */}
          <NowPlayingBar
            isOpen={!showDJQueue}
            currentSong={currentPlayingSong}
            isStreaming={isStreaming}
            streamUrl={streamUrl}
          />
        </div>
        
        {/* Role Change Banner */}
        <RoleChangeBanner
          isVisible={roleChangeBanner.visible}
          newRole={roleChangeBanner.newRole}
          changedBy={roleChangeBanner.changedBy}
          onDismiss={() => setRoleChangeBanner(prev => ({ ...prev, visible: false }))}
        />
        
        {/* RTG Soda Trip Effect - Full Screen */}
        <TripOverlay
          active={tripActive}
          duration={300}
          onEnd={() => { setTripActive(false); setDrinkEffect(null); setActiveDrink(null); }}
        />
        
        {/* Avatar Editor Modal */}
        <AvatarEditorModal
          isOpen={showAvatarEditor}
          onClose={() => setShowAvatarEditor(false)}
          avatar={avatar}
          updateAvatar={updateAvatar}
          isMobile={isMobile}
          vibecoins={vibecoins}
          savedOutfits={savedOutfits}
          saveCurrentOutfit={saveCurrentOutfit}
          loadOutfit={loadOutfit}
          deleteOutfit={deleteOutfit}
          SKIN_TONES={SKIN_TONES}
          isTurtle={isTurtle}
          ownedItems={ownedOutfitItems}
          onPurchaseItem={handlePurchaseOutfitItem}
        />
        
        {/* Admin Panel Modal */}
        <AdminPanel
          isOpen={showAdminPanel && isAdmin}
          onClose={() => setShowAdminPanel(false)}
          streamUrl={streamUrl}
          setStreamUrl={setStreamUrl}
          currentVideoId={currentVideoId}
          isStreaming={isStreaming}
          onStreamControl={handleStreamControl}
          currentDJ={currentDJ}
          setCurrentDJ={setCurrentDJ}
          djPlaying={djPlaying}
          setDjPlaying={setDjPlaying}
          lightingMode={lightingMode}
          onLightingChange={handleLightingChange}
          lightingFx={lightingFx}
          onLightingFxChange={setLightingFx}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          npcDJs={NPC_DJS}
          socketRef={socketRef}
          players={players}
          onSummon={(sid) => socketRef.current?.emit('admin_teleport', { target_sid: sid })}
          onSummonAll={() => socketRef.current?.emit('admin_teleport', { target_sid: 'all' })}
          onGoTo={goToPlayer}
        />
        
        {/* Emote Menu Modal */}
        <EmotesPanel
          isOpen={showEmoteMenu}
          onClose={() => setShowEmoteMenu(false)}
          emotes={DANCE_EMOTES}
          onPlayEmote={playEmote}
        />
        
        {/* Room Selection Modal */}
        <RoomsModal
          isOpen={showRoomMenu}
          onClose={() => setShowRoomMenu(false)}
          rooms={allRooms}
          currentRoom={currentRoom}
          onChangeRoom={changeRoom}
          isAdmin={isAdmin}
          canAccessRooftop={canAccessRooftop}
          user={user}
          hasVipAccess={hasVipAccess}
          roomCounts={roomCounts}
        />
        
        <ElevatorPrompts
          showElevatorPrompt={showElevatorPrompt}
          setShowElevatorPrompt={setShowElevatorPrompt}
          showReturnElevatorPrompt={showReturnElevatorPrompt}
          setShowReturnElevatorPrompt={setShowReturnElevatorPrompt}
          CLUB_ROOMS={CLUB_ROOMS}
          changeRoom={changeRoom}
        />
        
        <ReactionPicker
          isOpen={showReactionPicker}
          onClose={() => setShowReactionPicker(false)}
          reactions={QUICK_REACTIONS}
          onSendReaction={sendReaction}
        />
        
        <TipDJModal
          isOpen={showTipDJ}
          onClose={() => setShowTipDJ(false)}
          vibecoins={vibecoins}
          onTipSent={(newBalance) => setVibecoins(newBalance)}
        />
        
        {/* Custom widgets */}
        <CustomWidgets socketRef={socketRef} />
        
        {/* Custom CSS */}
        <CustomCSSLoader socketRef={socketRef} />
        
        <RoomCreator
          isOpen={showRoomCreator}
          onClose={() => setShowRoomCreator(false)}
          onRoomCreated={(room) => {
            toast.success(`Room "${room.name}" is now available!`);
          }}
        />
        
        {/* Profile Card Modal */}
        <PlayerProfileCard
          isOpen={showProfileCard}
          onClose={() => setShowProfileCard(false)}
          player={selectedPlayer}
          setPlayer={setSelectedPlayer}
          friends={friends}
          isAdmin={isAdmin}
          isOwner={user?.role === 'owner'}
          onStartPrivateChat={startPrivateChat}
          onOpenGiftModal={openGiftModal}
          onToggleFriend={toggleFriend}
          user={user}
          socket={socketRef.current}
          currentUserName={displayName || user?.name || 'Admin'}
        />
        
        {/* Private Chat Modal */}
        <PrivateChatModal
          isOpen={showPrivateChat}
          onClose={() => setShowPrivateChat(false)}
          targetPlayer={privateChatTarget}
          privateMessages={privateMessages}
          setPrivateMessages={setPrivateMessages}
          socketRef={socketRef}
        />
        
        {/* Gift Modal */}
        <GiftModal
          isOpen={showGiftModal && !!giftTarget}
          onClose={() => setShowGiftModal(false)}
          targetPlayer={giftTarget}
          vibecoins={vibecoins}
          onSendGift={sendGift}
        />
        
        {/* Photo Mode */}
        <PhotoMode
          canvasRef={canvasRef}
          isOpen={showPhotoMode}
          onClose={() => setShowPhotoMode(false)}
          user={user}
          isGuest={isGuest}
        />
        
        {/* DJ Queue */}
        <DJQueue
          isOpen={showDJQueue}
          onClose={() => setShowDJQueue(false)}
          user={user}
          isGuest={isGuest}
          isAdmin={isAdmin}
          socketRef={socketRef}
        />
        
        {/* Dance Battle */}
        <DanceBattle
          isOpen={showDanceBattle}
          onClose={() => setShowDanceBattle(false)}
          user={user}
          isGuest={isGuest}
          players={players}
          socketRef={socketRef}
        />
        
        {/* Friends Panel */}
        <FriendsPanel
          isOpen={showFriendsPanel}
          onClose={() => setShowFriendsPanel(false)}
          user={user}
          isGuest={isGuest}
          onStartChat={startPrivateChat}
          players={players}
        />
        
        {/* Quest Panel */}
        <QuestPanel
          isOpen={showQuestPanel}
          onClose={() => setShowQuestPanel(false)}
          quests={quests}
          onClaimReward={claimQuestReward}
        />
        
        {/* Leaderboard Modal */}
        <LeaderboardModal
          isOpen={showLeaderboard}
          onClose={() => setShowLeaderboard(false)}
        />
        
        {/* Username Prompt Modal - Shows on first join */}
        <UsernamePromptModal
          isOpen={showUsernamePrompt}
          onComplete={handleUsernameComplete}
          onClose={handleUsernamePromptClose}
          user={user}
          sessionToken={getToken()}
        />
        
        {/* Virtual Bar Modal */}
        <VirtualBar
          isOpen={showVirtualBar}
          onClose={() => setShowVirtualBar(false)}
          vibecoins={vibecoins}
          onPurchase={handlePurchaseDrink}
          activeDrink={activeDrink}
        />
        
        {/* Bartender Modal (Rooftop only) */}
        <BartenderModal
          isOpen={showBartender}
          onClose={() => setShowBartender(false)}
          vibecoins={vibecoins}
          onPurchase={handleBartenderPurchase}
          activeDrink={activeDrink}
        />
        
        {/* Daily Rewards Modal */}
        <DailyRewardsModal
          isOpen={showDailyRewards}
          onClose={() => setShowDailyRewards(false)}
          currentStreak={loginStreak.streak}
          lastClaimDate={loginStreak.lastClaim}
          canClaim={canClaimDailyReward()}
          onClaim={handleClaimDailyReward}
        />
        
        {/* Private Rooms Modal */}
        <PrivateRoomsModal
          isOpen={showPrivateRooms}
          onClose={() => setShowPrivateRooms(false)}
          privateRooms={privateRooms}
          onCreateRoom={handleCreatePrivateRoom}
          onJoinRoom={handleJoinPrivateRoom}
          onLeaveRoom={handleLeavePrivateRoom}
          currentPrivateRoom={currentPrivateRoom}
          userName={user?.name}
        />
        
        {/* Announcement Modal (for Admin/DJ) */}
        <AnnouncementModal
          isOpen={showAnnouncement}
          onClose={() => setShowAnnouncement(false)}
          onSend={handleSendAnnouncement}
          role={isAdmin ? 'admin' : 'dj'}
        />
        
        {/* Announcement Banner (shows to all) */}
        <AnnouncementBanner
          announcement={currentAnnouncement}
          onDismiss={() => setCurrentAnnouncement(null)}
        />
        
        {/* Apartment knock popup (owner side) */}
        {knockRequest && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[95] bg-gray-900 border border-yellow-500/50 rounded-xl p-4 shadow-2xl flex flex-col items-center gap-3 max-w-xs w-[90%]" data-testid="knock-popup">
            <p className="text-white text-sm font-bold text-center">
              🚪 {knockRequest.from_name} is knocking on your door ({knockRequest.property === 'house'
                ? ({ 17: 'Grand Mansion', 18: 'Elite Mansion I', 19: 'Elite Mansion II', 20: 'Admin Mansion I', 21: 'Admin Mansion II', 0: 'Your Mansion' }[knockRequest.room_number] || `House ${String(knockRequest.room_number).padStart(2, '0')}`)
                : `Apt ${String(knockRequest.room_number).padStart(2, '0')}`})
            </p>
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => respondToKnock(true)} data-testid="knock-accept-btn">
                Let them in
              </Button>
              <Button size="sm" variant="outline" onClick={() => respondToKnock(false)} data-testid="knock-decline-btn">
                Not now
              </Button>
            </div>
          </div>
        )}
        
        {/* Fullscreen Controls Toggle Button */}
        {isFullscreen && (
          <Button
            onClick={() => setHideFullscreenControls(!hideFullscreenControls)}
            variant="ghost"
            size="icon"
            className="fixed bottom-4 right-4 z-50 bg-black/60 hover:bg-black/80 text-white rounded-full"
            title={hideFullscreenControls ? "Show Controls" : "Hide Controls"}
          >
            {hideFullscreenControls ? (
              <span className="text-lg">👁️</span>
            ) : (
              <span className="text-lg">🙈</span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
  } catch (err) {
    console.error('Nightclub render error:', err);
    return <div className="min-h-screen flex items-center justify-center text-white">Error loading nightclub: {err.message}</div>;
  }
}
